import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Chip,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemButton,
  Switch,
  FormControlLabel,
  Divider,
  Fab,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  ListItemText,
  TextField
} from '@mui/material';
import { IconButton, Tooltip } from '@mui/material';
import { apiRequest, fetchSchedulingLogs } from '../api/api';
import { impostazioniService } from '../api/impostazioniService';
import { useNegozi, useDipendenti, useTurniNegozio, useStatsNegozio, useMaternitaNegozio, useFestivita } from '../api/queries';
import InlineLoader from './common/InlineLoader';
import ErrorBanner from './common/ErrorBanner';
import {
  CalendarMonth as CalendarIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  PersonAdd as PersonAddIcon,
  AccessTime as AccessTimeIcon,
  GroupAdd as GroupAddIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  PersonOff as PersonOffIcon
} from '@mui/icons-material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CloseIcon from '@mui/icons-material/Close';
import { Tabs, Tab, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
// PDF helpers estratti
import { esportaPdfMatrice, esportaScreenshotMatrice } from './common/GenerazioneOrarioPDF';
import { Dipendente, SchedulingLogsResponse } from '../types/types';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../api/authService';
import { visibilitaService, orariGeneratiService, preferenzeService, coordinatoreOreLockService } from '../api/services';
import { CoordinatoreOreLockStatus } from '../types/types';
import { isCompetenzaLocked, LOCK_MESSAGE } from '../utils/competenzaLock';
import { formatDecimalHours } from '../utils/timeUtils';
import { useSettings } from '../context/SettingsContext';
import GenerazioneOrarioStats from './GenerazioneOrarioStats';
import AggiuntaDipendenteMomentaneoDialog from './AggiuntaDipendenteMomentaneoDialog';
import ConfermaDipendenteDialog from './ConfermaDipendenteDialog';

dayjs.locale('it');

// Colore tema per Generazione Orari
const PAGE_COLOR = '#607D8B';
const NEOASSUNTA_COL_WIDTH = 110; // larghezza colonna "Neoassunta"
// Altezza standard delle celle turno (ridotta rispetto ai 40px originali)
const TURN_CELL_HEIGHT = 32; // px

interface Negozio {
  neg: string;
  negozio: string;
  coordinatore: string;
}

interface DipendenteTemporaneo extends Dipendente {
  // Campi aggiuntivi opzionali per i temporanei
  negozioOriginale?: string; // Negozio di provenienza originale
  negozioDestinazione?: string; // Negozio dove viene temporaneamente assegnato
}

interface TurnoCreato {
  id: number;
  neg: string;
  descrizione: string;
  data: string;
  oraConsInizio: string;
  oraConsFine: string;
  oraPrevInizio: string;
  oraPrevFine: string;
  giornoSettimana: string;
  turno: string;
  utenteModifica: string;
  dataModifica: string;
  blocco?: number;
  preferenza?: boolean | number;
  codiceTipoOrario?: string;
  annotazioni?: string;
}

interface OrarioMatrice {
  dipendente: string;
  orari: { [giorno: number]: TurnoCreato[] }; // Array di turni per supportare turni multipli (mese selezionato)
  orariPrev?: { [giorno: number]: TurnoCreato[] }; // Turni del mese precedente (per i giorni visibili prima del mese corrente)
  orariNext?: { [giorno: number]: TurnoCreato[] }; // Turni del mese successivo (per i giorni visibili dopo il mese corrente)
}

interface SchedulingRule {
  id: string; // ID logico della regola
  name: string;
  description: string;
  priority: number;
  enabled: boolean;
}

interface DipendenteStatistiche {
  nomeDipendente: string;
  cognomeDipendente: string;
  oreMensiliContratto: number;
  oreMensiliAssegnate: number;
  attuale?: number;
  // opzionale calcolata lato UI
  differenza?: number;
  // nuovi campi dal backend per colonne aggiuntive
  tipoContratto?: string; // "I" o "d"
  lunedi?: number;
  martedi?: number;
  mercoledi?: number;
  giovedi?: number;
  venerdi?: number;
  sabato?: number;
  domenica?: number;
  oreSettimanali?: number;
}

interface DipendenteStatisticheTurni {
  nomeDipendente: string;
  cognomeDipendente: string;
  mattine: number;
  pomeriggi: number;
  intermezzi: number;
  riposiSettimanali: number;
  riposiSabati: number;
  riposiDomeniche: number;
}

// ========================= BASILARE (Baseline RIPOSO) =========================
// Un "baseline" è il record creato automaticamente per ogni giorno/mese/negozio con:
//   turno = NULL / ''  AND  codiceTipoOrario = 'RIPOSO'  AND  utenteModifica = 'Sistema'
// Requisiti di business:
//  - Visualizzazione: nessun chip '-' o 'Riposo' -> cella appare vuota
//  - Non bloccabile (singolo, verticale, blocca tutto)
//  - Non eliminabile (singola cella, selezione multipla, drag & drop, swap, copia)
//  - Menu contestuale: SOLO azioni di cambio (Cambia in Mattina/Pomeriggio/Intermezzo/Personalizzato/Riposo, Cambia in Assenza)
//  - Permesso di sostituzione: quando scelgo un nuovo turno la baseline sparisce (backend già gestisce rimozione)
// Implementazione FE: riconoscimento tramite helper sotto e filtri nelle funzioni di blocco/eliminazione/menu.
const isBaselineTurno = (t?: TurnoCreato | null): boolean => {
  if (!t) return false;
  const turnoRaw = (t.turno || '').trim().toUpperCase();
  const codice = (t.codiceTipoOrario || '').trim().toUpperCase();
  // Rilassato: qualunque RIPOSO con turno vuoto è baseline (ignoro autore)
  return (!turnoRaw || turnoRaw === 'NULL') && codice === 'RIPOSO';
};

// Helper per verificare se un turno è una preferenza
const isPreferenzaTurno = (t?: TurnoCreato | null): boolean => {
  return !!t && Number(t.preferenza || 0) === 1;
};

const GenerazioneOrario: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings, setSettings } = useSettings();
  // Ref per lo scroll orizzontale della matrice e gestione wheel -> scroll orizzontale
  const matrixScrollRef = useRef<HTMLDivElement | null>(null);
  // Memorizza posizione di scroll orizzontale per preservarla quando si entra/esce da Esteso
  const lastScrollLeftRef = useRef<number>(0);
  const prevEstesoRef = useRef<boolean>(false);
  // Refs per misurazioni header sticky (Attivo, Dipendente)
  const attivoHeaderRef = useRef<HTMLTableCellElement | null>(null);
  const dipHeaderRef = useRef<HTMLTableCellElement | null>(null);
  const neoHeaderRef = useRef<HTMLTableCellElement | null>(null);
  const handleMatrixWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    // Blocca lo scroll verticale della pagina e mappa la rotella su scroll orizzontale della matrice
    // INVERTITO: Rotella giù => scorri a destra (verso i giorni successivi), Rotella su => scorri a sinistra
    if (matrixScrollRef.current) {
      e.preventDefault();
      e.stopPropagation();
      const container = matrixScrollRef.current;
      const delta = e.deltaY || 0;
      container.scrollLeft += delta;
    }
  }, []);
  
  const [negoziAccessibili, setNegoziAccessibili] = useState<Negozio[]>([]);
  const [selectedNegozio, setSelectedNegozio] = useState<string>('');
  const [selectedMese, setSelectedMese] = useState<number>(0);
  const [selectedAnno, setSelectedAnno] = useState<number>(0);
  const [coordinatoreLock, setCoordinatoreLock] = useState<CoordinatoreOreLockStatus | null>(null);

  // Fetch festività per il mese selezionato
  const monthStart = selectedAnno && selectedMese ? dayjs().year(selectedAnno).month(selectedMese - 1).startOf('month').format('YYYY-MM-DD') : undefined;
  const monthEnd = selectedAnno && selectedMese ? dayjs().year(selectedAnno).month(selectedMese - 1).endOf('month').format('YYYY-MM-DD') : undefined;
  const { data: festivitaData } = useFestivita(monthStart, monthEnd);
  const [dipendenti, setDipendenti] = useState<Dipendente[]>([]);
  const [dipendentiAttivi, setDipendentiAttivi] = useState<{[nome: string]: boolean}>({});
  const [openDisattivatiDialog, setOpenDisattivatiDialog] = useState(false);
  const [openAggiuntaMomentaneoDialog, setOpenAggiuntaMomentaneoDialog] = useState(false);
  const [openConfermaDialog, setOpenConfermaDialog] = useState(false);
  const [dipendentiAutorizzati104, setDipendentiAutorizzati104] = useState<string[]>([]); // CODICEPERSONALE autorizzati per LEGGE 104
  const [utilizzo104, setUtilizzo104] = useState<Record<string, { ggMese: number; usedGg: number; remainingGg: number }>>({});
  const [matriceOrari, setMatriceOrari] = useState<OrarioMatrice[]>([]);
  // Annotazioni di negozio per giorno (Inventario/Ricartellinamento/Giro) - staccate dai dipendenti
  const [annotazioniGiorno, setAnnotazioniGiorno] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [generationWarnings, setGenerationWarnings] = useState<{ data: string, eligible: number, required: number, reason: string, turno?: string, dipendente?: string }[]>([]);
  const [weeklyRestDiagnostics, setWeeklyRestDiagnostics] = useState<{ data: string, eligible: number, required: number, candidates: number, capacityForRests: number, generated: number, exclusions: Record<string, number> }[]>([]);
  const [missingConfigOpen, setMissingConfigOpen] = useState(false);
  const [missingConfigDates, setMissingConfigDates] = useState<string[]>([]);

  const resetGenerationFeedback = () => {
    setError(null);
    setSuccess(null);
    setGenerationWarnings([]);
    setWeeklyRestDiagnostics([]);
    setMissingConfigDates([]);
    setMissingConfigOpen(false);
  };

  useEffect(() => {
    setGenerationWarnings([]);
    setWeeklyRestDiagnostics([]);
    setMissingConfigDates([]);
    setMissingConfigOpen(false);
  }, [selectedNegozio, selectedMese, selectedAnno]);

  const formatWeeklyRestExclusions = (exclusions: Record<string, number>) => {
    const labels: Record<string, string> = {
      MANUAL_OR_LOCKED_SHIFT: 'ha un turno manuale o bloccato',
      EXISTING_SHIFT_OR_ABSENCE: 'ha già un turno lavorativo o un’assenza',
      EXTERNAL_STORE_SHIFT: 'ha già un turno previsto in un altro negozio',
      CAPACITY_EXHAUSTED: 'non può essere assegnato perché la copertura richiesta è già completa',
      WEEKLY_TARGET_REACHED: 'ha già raggiunto il numero previsto di riposi per la settimana',
      CONSECUTIVE_REST_OR_FERIE: 'ha già un riposo o ferie il giorno precedente o successivo',
      NO_ELIGIBLE_CANDIDATE: 'non è disponibile per un riposo',
      NO_ASSIGNABLE_CANDIDATE: 'non può ricevere un riposo rispettando le regole attive'
    };
    return Object.entries(exclusions)
      .map(([reason, count]) => `${count} ${count === 1 ? 'dipendente' : 'dipendenti'}: ${labels[reason] ?? 'esclusi da un vincolo di pianificazione'}`)
      .join('; ');
  };

  // Gestione risposta generazione (string plain o JSON con warnings)
  const handleGenerationResponse = (raw: any, fase: 'domeniche' | 'sabati' | 'riposi' | 'resto') => {
    // Ogni risposta sostituisce anche gli avvisi specifici della fase precedente.
    setMissingConfigDates([]);
    setMissingConfigOpen(false);
    try {
      let obj: any | null = null;
      if (raw && typeof raw === 'object') {
        obj = raw; // già parsato da fetch/axios
      } else if (typeof raw === 'string' && raw.trim().startsWith('{')) {
        try { obj = JSON.parse(raw); } catch { obj = null; }
      }

      if (obj) {
        const msg = typeof obj.message === 'string' ? obj.message : JSON.stringify(obj);
        setSuccess(msg);
        if (fase === 'riposi') {
          const dateMatches = (msg.match(/\b\d{4}-\d{2}-\d{2}\b/g) || []);
          if (/mancanti|mancante|assenza configurazioni|quantita_personale/i.test(msg) && dateMatches.length > 0) {
            setMissingConfigDates(Array.from(new Set(dateMatches)));
            setMissingConfigOpen(true);
          } else {
            setMissingConfigDates([]);
            setMissingConfigOpen(false);
          }
        }
        if (Array.isArray(obj.warnings)) {
          setGenerationWarnings(obj.warnings.map((w: any) => ({
            data: String(w.data),
            eligible: Number(w.eligible ?? 0),
            required: Number(w.required ?? 0),
            reason: String(w.reason ?? 'CAPACITY'),
            turno: w.turno ? String(w.turno) : undefined,
            dipendente: w.dipendente ? String(w.dipendente) : undefined
          })));
        } else {
          setGenerationWarnings([]);
        }
        if (fase === 'riposi' && Array.isArray(obj.weeklyRestDays)) {
          setWeeklyRestDiagnostics(obj.weeklyRestDays.map((d: any) => ({
            data: String(d.data),
            eligible: Number(d.eligible ?? 0),
            required: Number(d.required ?? 0),
            candidates: Number(d.candidates ?? 0),
            capacityForRests: Number(d.capacityForRests ?? 0),
            generated: Number(d.generated ?? 0),
            exclusions: typeof d.exclusions === 'object' && d.exclusions ? d.exclusions : {}
          })));
        } else {
          setWeeklyRestDiagnostics([]);
        }
      } else {
        const msgStr = typeof raw === 'string' ? raw : JSON.stringify(raw);
        setSuccess(msgStr);
        if (fase === 'riposi') {
          const dateMatches = (msgStr.match(/\b\d{4}-\d{2}-\d{2}\b/g) || []);
          if (/mancanti|mancante|assenza configurazioni|quantita_personale/i.test(msgStr) && dateMatches.length > 0) {
            setMissingConfigDates(Array.from(new Set(dateMatches)));
            setMissingConfigOpen(true);
          } else {
            setMissingConfigDates([]);
            setMissingConfigOpen(false);
          }
        }
        setGenerationWarnings([]);
        setWeeklyRestDiagnostics([]);
      }
    } catch (e) {
      const msgStr = typeof raw === 'string' ? raw : JSON.stringify(raw);
      setSuccess(msgStr);
      if (fase === 'riposi') {
        const dateMatches = (msgStr.match(/\b\d{4}-\d{2}-\d{2}\b/g) || []);
        if (/mancanti|mancante|assenza configurazioni|quantita_personale/i.test(msgStr) && dateMatches.length > 0) {
          setMissingConfigDates(Array.from(new Set(dateMatches)));
          setMissingConfigOpen(true);
        } else {
          setMissingConfigDates([]);
          setMissingConfigOpen(false);
        }
      }
      setGenerationWarnings([]);
      setWeeklyRestDiagnostics([]);
    }
  };
  // Stato visibilità mese (tabella visibilita)
  const [meseVisibile, setMeseVisibile] = useState<boolean | null>(null);
  const [visibilitaBusy, setVisibilitaBusy] = useState<boolean>(false);
  const [rulesDrawerOpen, setRulesDrawerOpen] = useState(false);
  // ── Log Pianificazione (domeniche/sabati) ──────────────────────────────────
  const [logsDrawerOpen, setLogsDrawerOpen] = useState(false);
  const [logsData, setLogsData] = useState<SchedulingLogsResponse | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [logsTab, setLogsTab] = useState<0 | 1 | 2>(0); // 0=graduatoria, 1=domeniche, 2=sabati
  // Avvisi: abilita/disabilita i popup di conferma per lo scambio turni via drag&drop
  const [avvisiEnabled, setAvvisiEnabled] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem('apporari-avvisi-enabled');
      return v === null ? true : v === '1';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try { localStorage.setItem('apporari-avvisi-enabled', avvisiEnabled ? '1' : '0'); } catch {}
  }, [avvisiEnabled]);
  // Layout: nessuna altezza fissa, il contenitore del calendario si adatta al numero di righe
  const draggedTurnoRef = useRef<{
    dipendenteSource: string;
    dateIso: string;
    orario: any;
  } | null>(null);
  const dragOverCellRef = useRef<HTMLElement | null>(null);
  const setDragOverCell = useCallback((nextCell: HTMLElement | null) => {
    if (dragOverCellRef.current === nextCell) return;
    dragOverCellRef.current?.classList.remove('turn-drag-over');
    dragOverCellRef.current = nextCell;
    nextCell?.classList.add('turn-drag-over');
  }, []);
  // Drag multi-selezione (scambio massivo)
  // Stato drag massivo: quando true, l'utente ha una selezione multipla e trascina una qualsiasi delle celle selezionate
  // bulkDragActive attivo automaticamente durante il drag di una selezione; non più togglato da menu
  const [bulkDragActive, setBulkDragActive] = useState<boolean>(false);
  // Selezione multipla orizzontale per riga (dipendente)
  const [rowSelection, setRowSelection] = useState<{
    dipendente: string;
    startIdx: number; // indice nella griglia calendarGrid.days
    endIdx: number;   // indice nella griglia calendarGrid.days
    selecting: boolean; // true mentre si trascina con il mouse
  } | null>(null);
  const selectionPreviewRef = useRef<{
    dipendente: string;
    startIdx: number;
    endIdx: number;
  } | null>(null);
  const selectionCellsRef = useRef<Map<string, HTMLElement>>(new Map());
  const selectionCellKey = (dipendente: string, gridIdx: number) => `${dipendente}\u0000${gridIdx}`;
  const registerSelectionCell = useCallback((dipendente: string, gridIdx: number, element: HTMLElement | null) => {
    const key = selectionCellKey(dipendente, gridIdx);
    if (element) selectionCellsRef.current.set(key, element);
    else selectionCellsRef.current.delete(key);
  }, []);
  const setSelectionPreview = useCallback((next: { dipendente: string; startIdx: number; endIdx: number } | null) => {
    const previous = selectionPreviewRef.current;
    if (previous && (!next || previous.dipendente !== next.dipendente)) {
      const from = Math.min(previous.startIdx, previous.endIdx);
      const to = Math.max(previous.startIdx, previous.endIdx);
      for (let idx = from; idx <= to; idx++) selectionCellsRef.current.get(selectionCellKey(previous.dipendente, idx))?.classList.remove('row-selection-preview');
    }
    if (next) {
      const previousFrom = previous?.dipendente === next.dipendente ? Math.min(previous.startIdx, previous.endIdx) : 0;
      const previousTo = previous?.dipendente === next.dipendente ? Math.max(previous.startIdx, previous.endIdx) : -1;
      const nextFrom = Math.min(next.startIdx, next.endIdx);
      const nextTo = Math.max(next.startIdx, next.endIdx);
      const from = Math.min(previousFrom, nextFrom);
      const to = Math.max(previousTo, nextTo);
      for (let idx = from; idx <= to; idx++) {
        const wasSelected = idx >= previousFrom && idx <= previousTo;
        const isSelected = idx >= nextFrom && idx <= nextTo;
        if (wasSelected === isSelected) continue;
        selectionCellsRef.current.get(selectionCellKey(next.dipendente, idx))?.classList.toggle('row-selection-preview', isSelected);
      }
    }
    selectionPreviewRef.current = next;
  }, []);
  const commitSelectionPreview = useCallback(() => {
    const preview = selectionPreviewRef.current;
    if (!preview) return;
    setSelectionPreview(null);
    setRowSelection({ ...preview, selecting: false });
  }, [setSelectionPreview]);
  
  // Long-press per abilitare la selezione multipla con tasto sinistro
  const LONG_PRESS_MS = 200; // 1 secondo
  const longPressTimerRef = useRef<number | null>(null);
  const longPressActiveRef = useRef<boolean>(false);
  const startLongPress = useCallback((dipendente: string, startIdx: number) => {
    // reset eventuali timer
    if (longPressTimerRef.current != null) {
      try { clearTimeout(longPressTimerRef.current); } catch {}
      longPressTimerRef.current = null;
    }
    longPressActiveRef.current = false;
    selectionModeActiveRef.current = false;
    longPressTimerRef.current = window.setTimeout(() => {
      longPressActiveRef.current = true;
      selectionModeActiveRef.current = true; // blocca drag dei turni
      setRowSelection(null); // rimuove eventuale selezione precedente una sola volta
      setSelectionPreview({ dipendente, startIdx, endIdx: startIdx });
      setBulkDragActive(false);
    }, LONG_PRESS_MS);
  }, [setSelectionPreview]);
  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current != null) {
      try { clearTimeout(longPressTimerRef.current); } catch {}
      longPressTimerRef.current = null;
    }
  }, []);

  // Gesture direction: orizzontale => selezione; verticale => cambio turno (drag)
  const gestureRef = useRef<{
    active: boolean;
    decided: boolean;
    originX: number;
    originY: number;
    mode: null | 'horizontal' | 'vertical';
    dipendente?: string;
    day?: number;
    orario?: any;
    startIdx?: number;
  } | null>(null);
  const selectionModeActiveRef = useRef<boolean>(false);

  const resetGesture = useCallback(() => {
    gestureRef.current = null;
    selectionModeActiveRef.current = false;
    longPressActiveRef.current = false;
    cancelLongPress();
  }, [cancelLongPress]);

  const handleTurnoPointerDown = useCallback((e: React.PointerEvent, dipendente: string, day: number, orario: any, gridIdx: number) => {
    if (e.button !== 0) return; // solo sinistro
    if (orario && orario.blocco) return; // non attivare su turno bloccato
    // NON avviare long press qui: sul chip deve rimanere il drag standard.
    gestureRef.current = {
      active: true,
      decided: false,
      originX: e.clientX,
      originY: e.clientY,
      mode: null,
      dipendente,
      day,
      orario,
      startIdx: gridIdx,
    };
  }, []);

  const handleTurnoPointerMove = useCallback((e: React.PointerEvent, dipendente: string, gridIdx: number) => {
    const g = gestureRef.current;
    if (!g || !g.active) return;
    // Con long-press non facciamo nulla qui: l'aggiornamento endIdx avviene con onMouseEnter delle celle
    // Se l'utente si muove prima del long-press, consideriamo che non sia selezione multipla e NON forziamo modalità drag qui.
  }, []);

  const handleTurnoPointerUp = useCallback((dipendente: string, gridIdx: number) => {
    const g = gestureRef.current;
    cancelLongPress();
    if (!g || !g.active) { resetGesture(); return; }
    if (longPressActiveRef.current) {
      // Se la selezione multipla è attiva (long press su cella vuota, non chip), termina la selezione.
      commitSelectionPreview();
    }
    // NESSUNA selezione singola: click breve sul chip serve solo per eventuale drag.
    setBulkDragActive(false);
    resetGesture();
  }, [cancelLongPress, commitSelectionPreview, resetGesture]);

  const handleTurnoPointerLeave = useCallback(() => {
    const g = gestureRef.current;
    if (!g || !g.active) return;
    cancelLongPress();
    // Se stavamo selezionando, chiudiamo la selezione; altrimenti reset semplice
    if (longPressActiveRef.current) {
      commitSelectionPreview();
    }
    resetGesture();
  }, [cancelLongPress, commitSelectionPreview, resetGesture]);

  const clearRowSelection = useCallback(() => {
    setSelectionPreview(null);
    setRowSelection(null);
  }, [setSelectionPreview]);
  const isCellSelected = useCallback((dip: string, gridIdx: number) => {
    if (!rowSelection) return false;
    if (rowSelection.dipendente !== dip) return false;
    const a = Math.min(rowSelection.startIdx, rowSelection.endIdx);
    const b = Math.max(rowSelection.startIdx, rowSelection.endIdx);
    return gridIdx >= a && gridIdx <= b;
  }, [rowSelection]);

  // Gestione mouseup globale e ESC per chiudere selezione
  useEffect(() => {
    const onUp = () => {
      cancelLongPress();
      commitSelectionPreview();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') clearRowSelection();
    };
    window.addEventListener('mouseup', onUp);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('keydown', onKey);
    };
  }, [cancelLongPress, clearRowSelection, commitSelectionPreview]);
  const [statistiche, setStatistiche] = useState<DipendenteStatistiche[]>([]);
  const [statisticheTurni, setStatisticheTurni] = useState<DipendenteStatisticheTurni[]>([]);
  const [exporting, setExporting] = useState(false);
  // Vista estesa: mostra anche gli ultimi 3 giorni del mese precedente (solo frontend)
  const [esteso, setEsteso] = useState<boolean>(false);
  // Toggle: mostra tutto il mese corrente in una schermata (compressione colonne giorni)
  const [fitMonth, setFitMonth] = useState<boolean>(() => {
    try { return localStorage.getItem('apporari-fit-month') === '1'; } catch { return false; }
  });
  const [dayColWidth, setDayColWidth] = useState<number | null>(null);
  const [dipColWidth, setDipColWidth] = useState<number | null>(null);
  // Titolo dinamico: mese/anno predominanti nella vista della matrice
  const [dominantView, setDominantView] = useState<{ month: number; year: number } | null>(null);
  const lastDominantRef = useRef<{ month: number; year: number } | null>(null);
  const scrollRafRef = useRef<number | null>(null);
  const monthLayoutRef = useRef<{ prevStart: number; prevEnd: number; currStart: number; currEnd: number; nextStart: number; nextEnd: number; colW: number; leftFixed: number } | null>(null);
  // Override in-memory per stato "Neoassunta" (non persistito, globale per sessione)
  const [neoassuntaOverride, setNeoassuntaOverride] = useState<Record<string, boolean | undefined>>({});
  const canEditNeoassunta = useMemo(() => {
    try {
      const u = authService.getCurrentUser();
      const ruolo = String(u?.ruolo || '').toLowerCase();
      if (['responsabileorari', 'admin', 'coordinatore', 'hr'].includes(ruolo)) return true;
      // Anche gli utenti con abilitaInsOrari=1 possono flaggare "Neoassunta"
      return u?.abilitaInsOrari === true;
    } catch {
      return false;
    }
  }, []);

  // Intervallo visibile della matrice: da lunedì precedente/primo del mese a domenica successiva/ultima del mese
  const calendarGrid = useMemo(() => {
    if (!selectedMese || !selectedAnno) return { days: [] as dayjs.Dayjs[], monthIndex: -1, prevDays: 0, currDays: 0, nextDays: 0 };
    const monthIndex = selectedMese - 1; // 0-based
    const firstOfMonth = dayjs().year(selectedAnno).month(monthIndex).date(1);
    const lastOfMonth = firstOfMonth.endOf('month');
    const daysToMondayFirst = (firstOfMonth.day() + 6) % 7; // 0..6
    const startDay = firstOfMonth.subtract(daysToMondayFirst, 'day');
    const daysToSundayLast = (7 - lastOfMonth.day()) % 7; // 0..6
    const endDay = lastOfMonth.add(daysToSundayLast, 'day');
    const days: dayjs.Dayjs[] = [];
    for (let d = startDay; d.isBefore(endDay) || d.isSame(endDay, 'day'); d = d.add(1, 'day')) {
      days.push(d);
    }
    const prevDays = days.filter(d => d.month() < monthIndex || (monthIndex === 0 && d.year() < selectedAnno)).length;
    const currDays = days.filter(d => d.month() === monthIndex && d.year() === selectedAnno).length;
    const nextDays = days.length - prevDays - currDays;
    return { days, monthIndex, prevDays, currDays, nextDays };
  }, [selectedMese, selectedAnno]);

  // Griglia estesa: mese precedente (intero, con padding lun->dom) + mese corrente (intero) + mese successivo (intero, senza padding finale).
  const extendedCalendarGrid = useMemo(() => {
    if (!selectedMese || !selectedAnno) return { days: [] as dayjs.Dayjs[], prevSegmentDays: 0, currSegmentDays: 0, nextSegmentDays: 0 };
    const currMonthIndex = selectedMese - 1;
    const currFirst = dayjs().year(selectedAnno).month(currMonthIndex).startOf('month');
    // Mese precedente (gestione cambio anno)
    const prevFirst = currFirst.subtract(1, 'month');
    // Mese successivo (gestione cambio anno)
    const nextFirst = currFirst.add(1, 'month');
    // Mese dopo quello successivo (per usare come limite esclusivo)
    const afterNextFirst = nextFirst.add(1, 'month');
    
    // Padding per mese precedente: inizia dal lunedì prima del primo del mese precedente
    const prevDaysToMondayFirst = (prevFirst.day() + 6) % 7;
    const prevStartDay = prevFirst.subtract(prevDaysToMondayFirst, 'day');
    
    const days: dayjs.Dayjs[] = [];
    
    // Aggiungi dal padding iniziale del mese precedente fino alla fine del mese precedente
    for (let d = prevStartDay; d.isBefore(currFirst); d = d.add(1, 'day')) {
      days.push(d);
    }
    const prevSegmentDays = days.length;
    
    // Aggiungi tutto il mese corrente (dal primo all'ultimo giorno)
    for (let d = currFirst; d.isBefore(nextFirst); d = d.add(1, 'day')) {
      days.push(d);
    }
    const currSegmentDays = days.length - prevSegmentDays;
    
    // Aggiungi tutto il mese successivo (dal primo all'ultimo giorno, usando limite esclusivo)
    for (let d = nextFirst; d.isBefore(afterNextFirst); d = d.add(1, 'day')) {
      days.push(d);
    }
    const nextSegmentDays = days.length - prevSegmentDays - currSegmentDays;
    
    return { days, prevSegmentDays, currSegmentDays, nextSegmentDays };
  }, [selectedMese, selectedAnno]);

  // Giorni effettivamente da mostrare (normale o esteso)
  const allGridDays = useMemo(() => esteso ? extendedCalendarGrid.days : calendarGrid.days, [esteso, extendedCalendarGrid.days, calendarGrid.days]);
  // Misura testo (per calcolare larghezze naturali intestazioni in modalità Mese intero)
  const measureText = useCallback((text: string, font: string = '12px Roboto, Helvetica, Arial, sans-serif') => {
    try {
      const canvas = (measureText as any)._canvas || ((measureText as any)._canvas = document.createElement('canvas'));
      const ctx = canvas.getContext('2d');
      if (!ctx) return text.length * 7; // fallback approssimato
      ctx.font = font;
      const metrics = ctx.measureText(text);
      return metrics.width;
    } catch {
      return text.length * 7; // fallback in ambienti senza canvas
    }
  }, []);
  // Ricalcola larghezza colonne giorni quando necessario
  useEffect(() => {
    if (!fitMonth) { setDayColWidth(null); return; }
    if (!matrixScrollRef.current) return;

    const recalc = () => {
      if (!matrixScrollRef.current) return;
      const containerWidth = matrixScrollRef.current.clientWidth;
      const attW = attivoHeaderRef.current?.clientWidth ?? 80;
      const neoW = neoHeaderRef.current?.clientWidth ?? NEOASSUNTA_COL_WIDTH;
      const dipW = dipHeaderRef.current?.clientWidth ?? 150;
      const padding = 24; // margine/bordi
      const avail = Math.max(0, containerWidth - attW - neoW - dipW - padding);
      // In modalità estesa, calcola la larghezza per far entrare UN mese nella vista (giorni del mese corrente)
      // invece di tutti i giorni della griglia estesa
      const daysForCalc = esteso 
        ? (extendedCalendarGrid.currSegmentDays || 31) 
        : (calendarGrid.days.length || 30);
      // Larghezza naturale: basata sulla colonna più larga in intestazione
      // In modalità Mese intero l'header è su due righe: numero (grassetto) sopra e giorno (ddd) sotto.
      // Quindi calcoliamo la larghezza massima tra il numero e l'abbreviazione del giorno.
      let headerMax = 0;
      if ((esteso ? extendedCalendarGrid.days.length : calendarGrid.days.length) > 0) {
        for (const d of (esteso ? extendedCalendarGrid.days : calendarGrid.days)) {
          const numW = measureText(String(d.date()));
          const dowW = measureText(d.format('ddd').toLowerCase());
          const w = Math.max(numW, dowW);
          headerMax = Math.max(headerMax, w);
        }
      } else {
        // Fallback: 31 e 'mer' (3 lettere) sono casi tra i più larghi
        headerMax = Math.max(measureText('31'), measureText('mer'));
      }
      const headerWithPadding = Math.ceil(headerMax + 10); // padding orizzontale ~10px
      const natural = Math.max(16, headerWithPadding);
      // Togli 1px per colonna per compensare i bordi e prevenire overflow
      const availAdjusted = Math.max(0, avail - daysForCalc);
      const maxThatFits = Math.floor(availAdjusted / Math.max(1, daysForCalc));
      const finalW = Math.max(12, Math.min(natural, maxThatFits));
      setDayColWidth(finalW);
    };

    // Prima calc immediata
    recalc();
    // Resize observer sul container
    let ro: ResizeObserver | null = null;
    try {
      ro = new ResizeObserver(() => recalc());
      if (matrixScrollRef.current) ro.observe(matrixScrollRef.current);
    } catch {
      // fallback: evento window resize
      const onResize = () => recalc();
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }
    return () => { try { ro && ro.disconnect(); } catch {} };
  }, [fitMonth, selectedMese, selectedAnno, dipColWidth, esteso, calendarGrid.days, extendedCalendarGrid.days, extendedCalendarGrid.currSegmentDays, measureText]);

  // Calcola mese/anno "più visibili" nella matrice (in base alla porzione visibile delle colonne giorno)
  const updateDominantFromViewport = useCallback(() => {
    if (!selectedMese || !selectedAnno || !matrixScrollRef.current) { setDominantView(null); return; }
    const container = matrixScrollRef.current;
    // Precalcola layout una volta (o quando cambiano dipendenze chiave)
    if (!monthLayoutRef.current) {
      const attW = attivoHeaderRef.current?.clientWidth ?? 80;
      const neoW = neoHeaderRef.current?.clientWidth ?? NEOASSUNTA_COL_WIDTH;
      const dipW = fitMonth ? (dipColWidth || 80) : 150;
      const leftFixed = attW + neoW + dipW;
      // Conteggi per layout: se esteso usa segmenti completi, altrimenti i giorni prima (padding) + mese corrente
      const prevCount = esteso ? extendedCalendarGrid.prevSegmentDays : calendarGrid.prevDays;
      const currCount = esteso ? extendedCalendarGrid.currSegmentDays : (calendarGrid.currDays || (selectedMese && selectedAnno ? dayjs().year(selectedAnno).month(selectedMese - 1).daysInMonth() : 30));
      const nextCount = esteso ? extendedCalendarGrid.nextSegmentDays : 0;
      const colW = fitMonth && dayColWidth ? dayColWidth : 120;
      const sep = 0;
      const prevStart = 0;
      const prevEnd = prevStart + prevCount * (colW + sep);
      const currStart = prevEnd;
      const currEnd = currStart + currCount * (colW + sep);
      const nextStart = currEnd;
      const nextEnd = nextStart + nextCount * (colW + sep);
      monthLayoutRef.current = { prevStart, prevEnd, currStart, currEnd, nextStart, nextEnd, colW, leftFixed };
    }
    const layout = monthLayoutRef.current!;
    const viewportStart = Math.max(0, container.scrollLeft - layout.leftFixed);
    const viewportWidth = Math.max(0, container.clientWidth - layout.leftFixed);
    const viewEnd = viewportStart + viewportWidth;
    const interPrev = Math.max(0, Math.min(layout.prevEnd, viewEnd) - Math.max(layout.prevStart, viewportStart));
    const interCurr = Math.max(0, Math.min(layout.currEnd, viewEnd) - Math.max(layout.currStart, viewportStart));
    const interNext = Math.max(0, Math.min(layout.nextEnd, viewEnd) - Math.max(layout.nextStart, viewportStart));
    if (!esteso) {
      const currMonth = dayjs().year(selectedAnno).month(selectedMese - 1);
      const next = { month: currMonth.month() + 1, year: currMonth.year() };
      if (!lastDominantRef.current || lastDominantRef.current.month !== next.month || lastDominantRef.current.year !== next.year) {
        lastDominantRef.current = next;
        setDominantView(next);
      }
      return;
    }
    const currMonth = dayjs().year(selectedAnno).month(selectedMese - 1);
    const prevMonth = currMonth.subtract(1, 'month');
    const nextMonth = currMonth.add(1, 'month');
    // Trova il mese con maggiore visibilità
    let next: { month: number; year: number };
    if (interPrev >= interCurr && interPrev >= interNext) {
      next = { month: prevMonth.month() + 1, year: prevMonth.year() };
    } else if (interNext >= interCurr && interNext >= interPrev) {
      next = { month: nextMonth.month() + 1, year: nextMonth.year() };
    } else {
      next = { month: currMonth.month() + 1, year: currMonth.year() };
    }
    if (!lastDominantRef.current || lastDominantRef.current.month !== next.month || lastDominantRef.current.year !== next.year) {
      lastDominantRef.current = next;
      setDominantView(next);
    }
  }, [selectedMese, selectedAnno, esteso, fitMonth, dayColWidth, dipColWidth, calendarGrid.prevDays, calendarGrid.currDays, extendedCalendarGrid.prevSegmentDays, extendedCalendarGrid.currSegmentDays, extendedCalendarGrid.nextSegmentDays]);

  useEffect(() => {
    updateDominantFromViewport();
  }, [updateDominantFromViewport]);

  // Aggiorna layout quando cambiano misure/dipendenze chiave
  useEffect(() => {
    monthLayoutRef.current = null;
    if (esteso) {
      // ricalcola al prossimo scroll
      updateDominantFromViewport();
    }
  }, [esteso, selectedMese, selectedAnno, fitMonth, dayColWidth, dipColWidth, updateDominantFromViewport]);

  // Listener scroll passivo + rAF per evitare troppi re-render durante lo scroll orizzontale
  useEffect(() => {
    const container = matrixScrollRef.current;
    if (!container || !esteso) return;
    const onScroll = () => {
      if (scrollRafRef.current != null) return;
      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        updateDominantFromViewport();
      });
    };
    container.addEventListener('scroll', onScroll, { passive: true } as any);
    const ro = new ResizeObserver(() => onScroll());
    try { ro.observe(container); } catch {}
    // prima sync
    onScroll();
    return () => {
      container.removeEventListener('scroll', onScroll as any);
      try { ro.disconnect(); } catch {}
      if (scrollRafRef.current != null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, [esteso, updateDominantFromViewport]);

  // Preserva o aggiusta lo scroll quando si entra/esce da Esteso
  useEffect(() => {
    const container = matrixScrollRef.current;
    if (!container) return;
    const colW = fitMonth && dayColWidth ? dayColWidth : 120;
    if (esteso && !prevEstesoRef.current) {
      // Stiamo entrando in modalità estesa: aggiusta lo scroll aggiungendo la larghezza del segmento precedente
      const addedWidth = extendedCalendarGrid.prevSegmentDays * colW;
      container.scrollLeft = (lastScrollLeftRef.current || container.scrollLeft) + addedWidth;
    } else if (!esteso && prevEstesoRef.current) {
      // Uscendo da esteso: rimuovi offset
      const removedWidth = extendedCalendarGrid.prevSegmentDays * colW;
      container.scrollLeft = Math.max(0, (lastScrollLeftRef.current || container.scrollLeft) - removedWidth);
    } else {
      container.scrollLeft = lastScrollLeftRef.current || container.scrollLeft;
    }
    prevEstesoRef.current = esteso;
  }, [esteso, fitMonth, selectedMese, selectedAnno, extendedCalendarGrid.prevSegmentDays, dayColWidth]);

  // Calcola larghezza colonna Dipendente in modalità Mese intero in base alla riga più lunga
  useEffect(() => {
    if (!fitMonth) { setDipColWidth(null); return; }
    // Font corrispondente al 0.70rem impostato (≈ 11px)
    const font = '11px Roboto, Helvetica, Arial, sans-serif';
    const pad = 8; // padding orizzontale per aria di respiro
    let maxW = 80; // minimo
    try {
      for (const r of matriceOrari) {
        const parts = (r.dipendente || '').trim().split(/\s+/).filter(Boolean);
        let primaRiga = '';
        let secondaRiga = '';
        if (parts.length >= 4) {
          // Es: "anna maria rossi bianchi" -> primaRiga="anna maria", secondaRiga="rossi bianchi"
          const meta = Math.ceil(parts.length / 2);
          primaRiga = parts.slice(0, meta).join(' ');
          secondaRiga = parts.slice(meta).join(' ');
        } else if (parts.length === 3) {
          // Es: "anna maria filippi" -> primaRiga="anna maria", secondaRiga="filippi"
          primaRiga = parts.slice(0, 2).join(' ');
          secondaRiga = parts[2];
        } else if (parts.length === 2) {
          primaRiga = parts[0];
          secondaRiga = parts[1];
        } else {
          primaRiga = parts[0] || r.dipendente || '';
        }
        const wPrima = measureText(String(primaRiga), font);
        const wSeconda = secondaRiga ? measureText(String(secondaRiga), font) : 0;
        const w = Math.max(wPrima, wSeconda) + pad;
        if (w > maxW) maxW = w;
      }
    } catch {}
    setDipColWidth(Math.ceil(maxW));
  }, [fitMonth, matriceOrari, measureText]);

  // Persistenza preferenza
  useEffect(() => {
    try { localStorage.setItem('apporari-fit-month', fitMonth ? '1' : '0'); } catch {}
  }, [fitMonth]);

  // Persistenza stato "Attivo" per negozio+mese+anno (helper chiave backend)
  const getAttiviKey = useCallback(() => {
    if (!selectedNegozio || !selectedMese || !selectedAnno) return '';
    // Chiave univoca in impostazioni_globali: attivi.<NEG>.<ANNO>.<MESE>
    return `attivi.${selectedNegozio}.${selectedAnno}.${selectedMese}`;
  }, [selectedNegozio, selectedAnno, selectedMese]);

  // Ref che diventa true solo dopo che fetchDipendenti ha impostato dipendentiAttivi la prima volta.
  // Evita di sovrascrivere i valori salvati con {} al mount del componente.
  const attiviCaricatiRef = useRef(false);
  // Chiave dell'ultimo caricamento riuscito: usata per evitare salvataggi su chiave sbagliata
  const attiviKeyRef = useRef<string>('');

  // Salva stato Attivo al cambiare (localStorage sincrono + backend asincrono)
  useEffect(() => {
    const key = getAttiviKey();
    if (!key) return;
    // Non salvare se il caricamento iniziale non è ancora avvenuto per questa chiave
    if (!attiviCaricatiRef.current) return;
    // Non salvare se la chiave corrente non corrisponde a quella del caricamento
    // (evita salvataggi "stantii" dopo cambio negozio/mese/anno)
    if (attiviKeyRef.current !== key) return;
    // Non salvare se dipendentiAttivi è vuoto (stato iniziale React)
    if (Object.keys(dipendentiAttivi).length === 0) return;

    // Converti da {nome: bool} a {codicePersonale: bool} per la persistenza
    const perCodice: Record<string, boolean> = {};
    Object.entries(dipendentiAttivi).forEach(([nome, val]) => {
      const dip = dipendenti.find(d => d.nome === nome);
      const chiave = dip?.codicePersonale || nome;
      perCodice[chiave] = val;
    });
    const serialized = JSON.stringify(perCodice);

    // Scrivi subito in localStorage (sincrono, fonte primaria al reload)
    try { localStorage.setItem(`apporari-${key}`, serialized); } catch {}

    // Persisti lato backend (best effort, asincrono)
    (async () => {
      try {
        await impostazioniService.salvaImpostazione(key, serialized, 'Preferenze Attivi per negozio/mese/anno');
      } catch (e) {
        console.warn('Salvataggio preferenze Attivi su backend fallito:', e);
      }
    })();
  }, [dipendentiAttivi, dipendenti, getAttiviKey]);

  // Ordine coerente dei dipendenti (usiamo l'ordine della matrice Calendario Orari)
  const normalizeName = useCallback((name: string) => name.replace(/\s+/g, ' ').trim().toLowerCase(), []);
  

  
  
  // Info utente per permessi visualizzazione CODICE_TIPO_ORARIO
  const userInfo = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('userInfo') || '{}'); } catch { return {}; }
  }, []);
  const isCoordinatore = userInfo?.ruolo === 'Coordinatore';
  const isPrivileged = (userInfo?.ruolo === 'Coordinatore' || userInfo?.ruolo === 'Admin');
  const isAdmin = (userInfo?.ruolo === 'Admin');
  const isCoordinatoreLockActive = isCoordinatore && !!coordinatoreLock?.locked;
  const lockTargetsAggiornati = useMemo(() => {
    if (!isCoordinatoreLockActive) return [];
    // Il lock copre tutti i negozi del coordinatore; le statistiche della pagina
    // riguardano solo il negozio/mese selezionato e non devono cambiare i target.
    return coordinatoreLock?.allTargets ?? (coordinatoreLock?.firstTarget ? [coordinatoreLock.firstTarget] : []);
  }, [isCoordinatoreLockActive, coordinatoreLock]);
  const isCoordinatoreLocked = isCoordinatoreLockActive && lockTargetsAggiornati.length > 0;
  // Per generazione-orario: solo i negozi DIRETTI con dipendenti negativi (da allTargets, senza temporanei)
  const lockNegoziConNegativi = useMemo(() => {
    if (!isCoordinatoreLocked) return [];
    return Array.from(new Set(lockTargetsAggiornati.map(t => t.negozio).filter(Boolean)));
  }, [isCoordinatoreLocked, lockTargetsAggiornati]);
  // Mask specific absence codes for users with AbilitaInsOrari=1
  const shouldMaskAssenze = !!(userInfo as any)?.abilitaInsOrari;

  useEffect(() => {
    const loadCoordinatoreLock = async () => {
      if (!isCoordinatore) {
        setCoordinatoreLock(null);
        return;
      }
      try {
        const lock = await coordinatoreOreLockService.getStatus();
        setCoordinatoreLock(lock);
      } catch (e) {
        console.error('Errore caricamento lock coordinatore:', e);
      }
    };
    loadCoordinatoreLock();
  }, [isCoordinatore, location.pathname, location.search]);

  // Listener per aggiornamento real-time del lock status quando il cache viene invalidato
  useEffect(() => {
    if (!isCoordinatore) return;

    const handleLockCacheInvalidated = async () => {
      try {
        console.log('[GenerazioneOrario] Event ricevuto - refetching lock status');
        const lock = await coordinatoreOreLockService.getStatus({ force: true });
        setCoordinatoreLock(lock);
        console.log('[GenerazioneOrario] Lock status aggiornato:', lock.locked);
      } catch (e) {
        console.error('[GenerazioneOrario] Errore refetch lock:', e);
      }
    };

    window.addEventListener('coordinatore-ore-lock-cache-invalidated', handleLockCacheInvalidated);
    console.log('[GenerazioneOrario] Listener registrato per coordinatore-ore-lock-cache-invalidated');

    return () => {
      window.removeEventListener('coordinatore-ore-lock-cache-invalidated', handleLockCacheInvalidated);
    };
  }, [isCoordinatore]);

  // Helper per gestire errori API in modo uniforme (mostra messaggio blocco se presente)
  const handleApiError = useCallback((err: any, defaultMsg: string) => {
    const status = err?.response?.status ?? err?.status;
    const lockReason = err?.response?.headers?.['x-lock-reason'];
    const errorMessage = err?.response?.headers?.['x-error-message'];
    const serverMsg = err?.response?.data ? String(err.response.data) : null;
    if (status === 409) {
      setError(lockReason || errorMessage || serverMsg || 'Operazione non consentita: periodo bloccato');
    } else if (status === 403) {
      setError(errorMessage || 'Non hai i permessi per questa operazione');
    } else {
      setError(serverMsg || defaultMsg);
    }
  }, []);
  
  // Stato weekend per ciclo di generazione: DOMENICHE -> SABATI -> RIPOSI -> RESTO -> DOMENICHE
  const [weekendPhase, setWeekendPhase] = useState<'domeniche' | 'sabati' | 'riposi' | 'resto'>('domeniche');
  // Menu per "Generazione a step"
  const [stepMenuAnchor, setStepMenuAnchor] = useState<null | HTMLElement>(null);
  const openStepMenu = Boolean(stepMenuAnchor);
  const handleOpenStepMenu = (event: React.MouseEvent<HTMLButtonElement>) => setStepMenuAnchor(event.currentTarget);
  const handleCloseStepMenu = () => setStepMenuAnchor(null);
  const handleSelectGenerazioneStep = (fase: 'domeniche' | 'sabati' | 'riposi' | 'resto') => {
    handleCloseStepMenu();
    generaWeekendPerFase(fase);
  };

  // Menu PDF: scelta Screenshot vs Pdf tabellare
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState<null | HTMLElement>(null);
  const openPdfMenu = Boolean(pdfMenuAnchor);
  const handleOpenPdfMenu = (event: React.MouseEvent<HTMLElement>) => setPdfMenuAnchor(event.currentTarget);
  const handleClosePdfMenu = () => setPdfMenuAnchor(null);
  // Modalità speciale per migliorare la resa dello screenshot nelle celle
  const [screenshotMode, setScreenshotMode] = useState<boolean>(false);
  
  // Stati per il menu contestuale
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    dipendente: string;
    day: number;
    dateIso: string; // data assoluta della cella
    orario: TurnoCreato | null;
  } | null>(null);

  // Sottomenu per inserire assenza tramite CODICE_TIPO_ORARIO
  const [assenzaMenu, setAssenzaMenu] = useState<{
    mouseX: number;
    mouseY: number;
    dipendente: string;
    day: number;
    dateIso: string;
  } | null>(null);
  // Sottomenu per aggiungere un turno aggiuntivo su celle già occupate
  const [addMenu, setAddMenu] = useState<{
    mouseX: number;
    mouseY: number;
    dipendente: string;
    day: number;
    dateIso: string;
  } | null>(null);
  const baselineReadyRef = useRef(new Set<string>());
  const baselineInFlightRef = useRef(new Map<string, Promise<void>>());
  const standardTurniRef = useRef(new Map<string, { oraInizio: string | null; oraFine: string | null }>());
  // Nuovi menu contestuali dedicati
  const [dayMenu, setDayMenu] = useState<{
    mouseX: number;
    mouseY: number;
    day: number;
    dateIso: string;
  } | null>(null);
  const [dipMenu, setDipMenu] = useState<{
    mouseX: number;
    mouseY: number;
    dipendente: string;
  } | null>(null);
  // Menu contestuale globale (header Dipendenti)
  const [globalMenu, setGlobalMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  // Dialog "Aggiungi preferenza" dal context menu di Generazione Orari
  const [prefDialog, setPrefDialog] = useState<{
    dipendente: string;
    dateIso: string;
    neg: string; // negozio corrente al momento del click (supporta temporanei)
  } | null>(null);
  const [prefDialogLoading, setPrefDialogLoading] = useState(false);
  const [prefDialogError, setPrefDialogError] = useState<string | null>(null);
  
  // Filtra i codici di assenza in base al ruolo utente
  const CODICI_ASSENZA_BASE: string[] = [
    'CORSI AI DIPENDENTI',
    'CHIUSO DITTA',
    'ROL',
    'FERIE',
    'MALATTIA',
    "MATERNITA'",
    'LEGGE 104 PERMESSO', // Visibile per Admin e Coordinatori (il backend controlla le autorizzazioni)
    'AVIS',
    // 'ALLATTAMENTO', // disabilitato: gestione via IDMat=7 con overlay 🍼, nessuna creazione turno specifico
    'LUTTO',
    'PERMESSI PARENTALI',
    'SMART WORKING',
    'CONGEDO MATRIMONIALE',
    'PERMESSI ELETTORALI',
    'ASPETTATIVA',
    'PERMESSI STUDIO',
    'CHIUSO ORDINANAZA',
    'QUARANTENA',
    'ASSENZA N.R. DL 127',
    'ASSENZA NON RETRIBUITA',
    'LEGGE 118 PERMESSO'
  ];
  
  const currentUserRole = authService.getCurrentUser()?.ruolo;
  const currentUserAbilitaInsOrari = authService.getCurrentUser()?.abilitaInsOrari === true;
  // Solo Admin, Coordinatore e abilitaInsOrari=1 possono aggiungere preferenze per i dipendenti
  const canAddPreferenze = currentUserRole === 'Admin'
    || currentUserRole === 'Coordinatore'
    || currentUserAbilitaInsOrari;
  // Mostra LEGGE 104 PERMESSO anche ai Coordinatori e agli utenti con abilitaInsOrari=1 (il controllo autorizzazioni è nel backend)
  const CODICI_ASSENZA: string[] = currentUserRole === 'Admin' || currentUserRole === 'Coordinatore' || currentUserAbilitaInsOrari
    ? CODICI_ASSENZA_BASE 
    : CODICI_ASSENZA_BASE.filter(code => code !== 'LEGGE 104 PERMESSO');
  const CODICI_FESTIVITA_CONSENTITI = new Set(['LAVORATO', 'MALATTIA', "MATERNITA'", 'CHIUSO DITTA', 'LEGGE 104 PERMESSO']);
  const isFestivita = (dateIso: string) => !!festivitaData?.some(f => f.dataFestivita === dateIso);
  const isCodiceConsentitoInFestivita = (codice: string) => CODICI_FESTIVITA_CONSENTITI.has(codice);

  // Helper: turni presenti in una cella (supporta prev/curr/next mese)
  const getTurniInCella = useCallback((dipendente: string, dateIso: string): TurnoCreato[] => {
    const riga = matriceOrari.find(r => r.dipendente === dipendente);
    if (!riga) return [];
    const d = dayjs(dateIso);
    const dayNum = d.date();
    const isCurrMonthDate = d.month() === (selectedMese - 1) && d.year() === selectedAnno;
    const isPrevMonthDate = d.isBefore(dayjs().year(selectedAnno).month(selectedMese - 1).date(1), 'day');
    const arr = isCurrMonthDate ? (riga.orari[dayNum] || []) : (isPrevMonthDate ? (riga.orariPrev?.[dayNum] || []) : (riga.orariNext?.[dayNum] || []));
    return (arr || []).filter(t => t?.data === dateIso);
  }, [matriceOrari, selectedAnno, selectedMese]);

  

  const renderContextMenuItems = useCallback((): React.ReactNode => {
    if (!contextMenu) return null;

    // Se esiste una selezione multi-giorni sulla stessa riga includi voci dedicate
    const multiSel = !!(rowSelection && rowSelection.dipendente === contextMenu.dipendente && !rowSelection.selecting && (rowSelection.startIdx !== rowSelection.endIdx));
    const clickedIdx = allGridDays.findIndex(d => d.format('YYYY-MM-DD') === contextMenu.dateIso);
    const isInsideSelection = !!(multiSel && clickedIdx >= Math.min(rowSelection!.startIdx, rowSelection!.endIdx) && clickedIdx <= Math.max(rowSelection!.startIdx, rowSelection!.endIdx));
    const multiSelLabelRange = multiSel ? `${Math.min(rowSelection!.startIdx,rowSelection!.endIdx)+1}→${Math.max(rowSelection!.startIdx,rowSelection!.endIdx)+1}` : '';

    // Requisito: se clicco DENTRO la selezione multipla mostro SOLO le opzioni dedicate
    if (isInsideSelection) {
      return [
        <MenuItem key="delete-sel" onClick={handleDeleteSelectedTurni}>
          Elimina turni selezionati ({multiSelLabelRange})
        </MenuItem>,
        <MenuItem key="ins-assenze" onClick={() => {
          const firstIdx = Math.min(rowSelection!.startIdx,rowSelection!.endIdx);
          const d = allGridDays[firstIdx];
          if (d) setAssenzaMenu({ mouseX: contextMenu.mouseX, mouseY: contextMenu.mouseY, dipendente: contextMenu.dipendente, day: d.date(), dateIso: d.format('YYYY-MM-DD') });
          setContextMenu(null);
        }}>
          Inserisci assenze ({multiSelLabelRange}) ▶
        </MenuItem>,
        <MenuItem key="cambio-turno" onClick={() => { handleOpenSwapDialog(); }}>
          Cambio turno…
        </MenuItem>
      ];
    }

    // Se la cella ha più di 1 turno: mostra SOLO le azioni richieste
    const turniCella = getTurniInCella(contextMenu.dipendente, contextMenu.dateIso);
    if (turniCella.length > 1) {
      const hasSbloccabili = turniCella.some(t => !isBaselineTurno(t) && !t.blocco);
      const hasBloccati    = turniCella.some(t => !!t.blocco);
      const items = [
        <MenuItem key="add" onClick={handleOpenAddMenu}>Aggiungi turno ▶</MenuItem>,
        <MenuItem key="change" onClick={handleOpenChangeMenu}>Cambia turno ▶</MenuItem>,
        <MenuItem key="delete" onClick={handleOpenDeleteMenu}>Elimina turno ▶</MenuItem>,
        <MenuItem key="copy" onClick={handleCopyFromCurrentCell}>Copia</MenuItem>
      ];
      if (hasSbloccabili) {
        items.push(
          <MenuItem key="blocca-tutti" onClick={() => handleBloccaTuttiInCella(contextMenu.dipendente, contextMenu.dateIso)}>
            <LockIcon sx={{ mr: 1, fontSize: 16 }} />
            Blocca tutti
          </MenuItem>
        );
      }
      if (hasBloccati) {
        items.push(
          <MenuItem key="sblocca-tutti" onClick={() => handleSbloccaTuttiInCella(contextMenu.dipendente, contextMenu.dateIso)}>
            <LockOpenIcon sx={{ mr: 1, fontSize: 16 }} />
            Sblocca tutti
          </MenuItem>
        );
      }
      if (copiedTurns) {
        items.push(<MenuItem key="paste" onClick={handlePasteToCurrentCell}>Incolla</MenuItem>);
      }
      return items;
    }

    if (contextMenu.orario) {
      // Baseline: turno vuoto + RIPOSO + Sistema => solo CAMBIA, nessun elimina / aggiungi / blocca / copia
      if (isBaselineTurno(contextMenu.orario)) {
        const current = (contextMenu.orario.turno || '').toLowerCase();
        const options = [
          { key: 'Mattina', label: 'Cambia in Mattina' },
          { key: 'Pomeriggio', label: 'Cambia in Pomeriggio' },
          { key: 'Intermezzo', label: 'Cambia in Intermezzo' },
          { key: 'Personalizzato', label: 'Cambia in Personalizzato' },
          { key: 'Riposo', label: 'Cambia in Riposo' }
        ];
        const items = [
          ...options
            .filter(opt => (opt.key === 'Personalizzato' || opt.key.toLowerCase() !== current)
              && (!isFestivita(contextMenu.dateIso) || opt.key !== 'Riposo'))
            .map(opt => (
              <MenuItem key={`baseline-${opt.key}`} onClick={() => handleAddOrUpdateTurno(opt.key)}>
                {opt.label}
              </MenuItem>
            )),
          <MenuItem key="assenza" onClick={handleOpenAssenzaMenu}>Cambia in Assenza ▶</MenuItem>
        ];
        if (copiedTurns) {
          items.push(<MenuItem key="paste" onClick={handlePasteToCurrentCell}>Incolla</MenuItem>);
        }
        if (canAddPreferenze) {
          items.push(
            <Divider key="div-pref-baseline" />,
            <MenuItem key="add-pref-baseline" onClick={() => { setPrefDialog({ dipendente: contextMenu.dipendente, dateIso: contextMenu.dateIso, neg: selectedNegozio }); setContextMenu(null); }}>
              <FavoriteBorderIcon sx={{ mr: 1, fontSize: 16, color: '#e91e63' }} />
              Aggiungi preferenza
            </MenuItem>
          );
        }
        return items;
      }
      if (contextMenu.orario.blocco) {
        return [
          <MenuItem key="sblocca" onClick={handleSbloccaTurno}>
            <LockOpenIcon sx={{ mr: 1, fontSize: 16 }} />
            Sblocca
          </MenuItem>
        ];
      }

      const current = (contextMenu.orario.turno || '').toLowerCase();
      const options = [
        { key: 'Mattina', label: 'Cambia in Mattina' },
        { key: 'Pomeriggio', label: 'Cambia in Pomeriggio' },
        { key: 'Intermezzo', label: 'Cambia in Intermezzo' },
        { key: 'Personalizzato', label: 'Cambia in Personalizzato' },
        { key: 'Riposo', label: 'Cambia in Riposo' }
      ];

      const turnoLabel = (contextMenu.orario?.turno || '').toUpperCase();
      const canCopy = ['MATTINA P.', 'POMERIGGIO P.', 'INTERMEZZO P.', 'MATTINA', 'POMERIGGIO', 'INTERMEZZO', 'PERSONALIZZATO'].includes(turnoLabel);

      const items: React.ReactNode[] = [
        <MenuItem key="delete" onClick={handleDeleteTurno}>
          Elimina Turno
        </MenuItem>,
        <MenuItem key="add" onClick={handleOpenAddMenu}>
          Aggiungi turno ▶
        </MenuItem>,
        ...options
          .filter(opt => (opt.key === 'Personalizzato' || opt.key.toLowerCase() !== current)
            && (!isFestivita(contextMenu.dateIso) || opt.key !== 'Riposo'))
          .map(opt => (
            <MenuItem key={opt.key.toLowerCase()} onClick={() => handleAddOrUpdateTurno(opt.key)}>
              {opt.label}
            </MenuItem>
          )),
        <MenuItem key="assenza" onClick={handleOpenAssenzaMenu}>
          Cambia in Assenza ▶
        </MenuItem>
      ];

      if (canCopy) {
        items.push(<MenuItem key="copy" onClick={handleCopyFromCurrentCell}>Copia</MenuItem>);
      }

      if (copiedTurns) {
        items.push(<MenuItem key="paste" onClick={handlePasteToCurrentCell}>Incolla</MenuItem>);
      }

      items.push(
        <MenuItem key="blocca" onClick={handleBloccaTurno}>
          <LockIcon sx={{ mr: 1, fontSize: 16 }} />
          Blocca
        </MenuItem>
      );

      if (canAddPreferenze) {
        items.push(
          <Divider key="div-pref-turno" />,
          <MenuItem key="add-pref-turno" onClick={() => { setPrefDialog({ dipendente: contextMenu.dipendente, dateIso: contextMenu.dateIso, neg: selectedNegozio }); setContextMenu(null); }}>
            <FavoriteBorderIcon sx={{ mr: 1, fontSize: 16, color: '#e91e63' }} />
            Aggiungi preferenza
          </MenuItem>
        );
      }

      if (multiSel && !isInsideSelection) {
        items.push(
          <Divider key="divider-multi" />,
          <MenuItem key="del-sel" onClick={handleDeleteSelectedTurni}>
            Elimina turni selezionati ({multiSelLabelRange})
          </MenuItem>,
          <MenuItem key="ins-ass" onClick={() => {
            const firstIdx = Math.min(rowSelection!.startIdx,rowSelection!.endIdx);
            const d = allGridDays[firstIdx];
            if (d) setAssenzaMenu({ mouseX: contextMenu.mouseX, mouseY: contextMenu.mouseY, dipendente: contextMenu.dipendente, day: d.date(), dateIso: d.format('YYYY-MM-DD') });
            setContextMenu(null);
          }}>
            Inserisci assenze ({multiSelLabelRange}) ▶
          </MenuItem>,
          <MenuItem key="cambio" onClick={() => { handleOpenSwapDialog(); }}>
            Cambio turno…
          </MenuItem>
        );
      }

      return items;
    }

    const emptyItems: React.ReactNode[] = [
      <MenuItem key="add-matt" onClick={() => handleAddOrUpdateTurno('Mattina')}>
        Aggiungi Mattina
      </MenuItem>,
      <MenuItem key="add-pom" onClick={() => handleAddOrUpdateTurno('Pomeriggio')}>
        Aggiungi Pomeriggio
      </MenuItem>,
      <MenuItem key="add-int" onClick={() => handleAddOrUpdateTurno('Intermezzo')}>
        Aggiungi Intermezzo
      </MenuItem>,
      ...(!isFestivita(contextMenu.dateIso) ? [
        <MenuItem key="add-rip" onClick={() => handleAddOrUpdateTurno('Riposo')}>
          Aggiungi Riposo
        </MenuItem>
      ] : []),
      <MenuItem key="add-pers" onClick={() => handleAddOrUpdateTurno('Personalizzato')}>
        Aggiungi Personalizzato
      </MenuItem>,
      <MenuItem key="add-ass" onClick={handleOpenAssenzaMenu}>
        Aggiungi Assenza ▶
      </MenuItem>
    ];

    // Mostra "Incolla" se hai copiato turni (indipendentemente dallo stato della cella)
    if (copiedTurns) {
      emptyItems.push(<MenuItem key="incolla" onClick={handlePasteToCurrentCell}>Incolla</MenuItem>);
    }

    if (canAddPreferenze) {
      emptyItems.push(
        <Divider key="div-pref-empty" />,
        <MenuItem key="add-pref-empty" onClick={() => { setPrefDialog({ dipendente: contextMenu.dipendente, dateIso: contextMenu.dateIso, neg: selectedNegozio }); setContextMenu(null); }}>
          <FavoriteBorderIcon sx={{ mr: 1, fontSize: 16, color: '#e91e63' }} />
          Aggiungi preferenza
        </MenuItem>
      );
    }

    if (multiSel && !isInsideSelection) {
      emptyItems.push(
        <Divider key="divider-multi2" />,
        <MenuItem key="del-sel2" onClick={handleDeleteSelectedTurni}>
          Elimina turni selezionati ({multiSelLabelRange})
        </MenuItem>,
        <MenuItem key="ins-ass2" onClick={() => {
          const firstIdx = Math.min(rowSelection!.startIdx,rowSelection!.endIdx);
          const d = allGridDays[firstIdx];
          if (d) setAssenzaMenu({ mouseX: contextMenu.mouseX, mouseY: contextMenu.mouseY, dipendente: contextMenu.dipendente, day: d.date(), dateIso: d.format('YYYY-MM-DD') });
          setContextMenu(null);
        }}>
          Inserisci assenze ({multiSelLabelRange}) ▶
        </MenuItem>,
        <MenuItem key="cambio2" onClick={() => { handleOpenSwapDialog(); }}>
          Cambio turno…
        </MenuItem>
      );
    }

    return emptyItems;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMenu, rowSelection, allGridDays, getTurniInCella]);
  
  // Stati per il dialogo turno personalizzato
  const [customTurnoDialog, setCustomTurnoDialog] = useState(false);
  const [customTurnoData, setCustomTurnoData] = useState({
    dipendente: '',
    day: 0,
    dateIso: '' as string,
    oraInizio: '',
    oraFine: '',
    turnoId: null as number | null, // ID del turno esistente se stiamo aggiornando
    isEsterno: false // true se il turno originale appartiene a un altro negozio
  });
  
  // Stati per il dialogo orari previsti obbligatori (CORSI AI DIPENDENTI, SMART WORKING)
  const [orariPrevistiDialog, setOrariPrevistiDialog] = useState(false);
  const [orariPrevistiData, setOrariPrevistiData] = useState({
    codice: '' as string,  // CORSI AI DIPENDENTI o SMART WORKING
    oraPrevInizio: '',
    oraPrevFine: ''
  });
  
  // Dialog conferma incolla su turno esistente
  const [confirmPasteDialog, setConfirmPasteDialog] = useState<{
    open: boolean;
    dipendente: string;
    dateIso: string;
  } | null>(null);

  // Dialog Cambio Turno (bulk swap) per selezione multipla
  const [swapDialog, setSwapDialog] = useState<{ open: boolean; source: string } | null>(null);
  const [swapSearch, setSwapSearch] = useState<string>('');
  const handleOpenSwapDialog = () => {
    if (!rowSelection) return;
    setSwapDialog({ open: true, source: rowSelection.dipendente });
    handleCloseContextMenu();
  };
  const handleCloseSwapDialog = () => { setSwapDialog(null); setSwapSearch(''); };
  const handleExecuteSwapWith = async (target: string) => {
    if (!rowSelection || !swapDialog) return;
    const { startIdx, endIdx, dipendente: source } = rowSelection;
    if (source === target) { setError('Seleziona un dipendente diverso'); return; }
    // Chiudi subito il dialog prima di eseguire lo swap
    handleCloseSwapDialog();
    await executeBulkSwap(source, target, Math.min(startIdx,endIdx), Math.max(startIdx,endIdx));
  };

  // Stati per la gestione dipendenti temporanei
  const [dipendentiTemporanei, setDipendentiTemporanei] = useState<DipendenteTemporaneo[]>([]); // Lista dipendenti temporanei
  const [aggiungiDipendentiDialog, setAggiungiDipendentiDialog] = useState(false);
  const [dipendentiDisponibili, setDipendentiDisponibili] = useState<Dipendente[]>([]);
  const [ricercaDipendente, setRicercaDipendente] = useState<string>('');
  
  // Persistenza stato "Attivo" per negozio+mese+anno (definito più in alto)
  
  const [schedulingRules, setSchedulingRules] = useState<SchedulingRule[]>([
    // 1. MonthlyHoursRule
    {
      id: 'monthly-hours',
      name: 'Ore Mensili',
      description: 'Distribuisce equamente le ore lavorative mensili per far raggiungere le ore contrattuali del dipendente',
      priority: 1,
      enabled: true
    },
    // 2. SundayRestDistributionRule
    {
      id: 'sunday-rest-distribution',
      name: 'Distribuzione Riposi Domenicali',
      description: 'Distribuisce equamente i riposi domenicali tra i dipendenti',
      priority: 2,
      enabled: true
    },
    // 3. NoConsecutiveRestsRule
    {
      id: 'no-consecutive-rests',
      name: 'Niente riposi consecutivi',
      description: 'Impedisce di assegnare due giorni di riposo consecutivi allo stesso dipendente',
      priority: 3,
      enabled: true
    },
    // 4. NewEmployeesSeparationRule
    {
      id: 'new-employees-separation',
      name: 'Separazione Dipendenti Nuovi',
      description: 'Evita di mettere insieme dipendenti assunti da meno di 6 settimane',
      priority: 4,
      enabled: false
    },
    // 5. IntermezzoDistributionRule
    {
      id: 'intermezzo-distribution',
      name: 'Distribuzione Intermezzi',
      description: 'Distribuisce equamente i turni di intermezzo tra tutti i dipendenti',
      priority: 5,
      enabled: true
    },
    // 6. TurnoAlternanceRule
    {
      id: 'turno-alternance',
      name: 'Alternanza Turni',
      description: 'Blocca l\'assegnazione se il dipendente ha già 2 turni dello stesso tipo in sequenza (Riposo e Assenza non interrompono la catena)',
      priority: 6,
      enabled: true
    },
    // 7. CoworkerRotationRule
    {
      id: 'coworker-rotation',
      name: 'Rotazione Colleghi',
      description: 'Favorisce che le persone lavorino con colleghi diversi (evita accoppiate ripetute)',
      priority: 7,
      enabled: true
    },
    // 8. WeeklyShiftLimitRule
    {
      id: 'weekly-shift-limit',
      name: 'Limite Settimanale Aperture/Chiusure',
      description: 'Limita a 3 le aperture (Mattina) e a 3 le chiusure (Pomeriggio) per dipendente per settimana (lun-dom)',
      priority: 8,
      enabled: true
    }
  ]);

  const mesi = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  const anni = [2025, 2026, 2027, 2028];

  const { data: negoziData, isLoading: negoziLoading, isError: negoziError, refetch: refetchNegozi } = useNegozi();

  // Ref per tracciare se i parametri iniziali sono già stati impostati
  const initialParamsProcessed = useRef(false);

  useEffect(() => {
    // Esegui solo una volta all'inizio per impostare i valori iniziali
    if (initialParamsProcessed.current) return;

    // Leggi eventuali parametri query (neg, mese, anno)
    const params = new URLSearchParams(location.search);
    const qNeg = params.get('neg');
    const qMese = params.get('mese');
    const qAnno = params.get('anno');
    const parsedMese = qMese ? parseInt(qMese, 10) : NaN;
    const parsedAnno = qAnno ? parseInt(qAnno, 10) : NaN;

    if (settings?.month && settings?.year && !qMese && !qAnno) {
      setSelectedMese(settings.month);
      setSelectedAnno(settings.year);
    } else if (!isNaN(parsedMese) && parsedMese >= 1 && parsedMese <= 12 && !isNaN(parsedAnno)) {
      setSelectedMese(parsedMese);
      setSelectedAnno(parsedAnno);
    } else {
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      setSelectedMese(nextMonth.getMonth() + 1);
      setSelectedAnno(nextMonth.getFullYear());
    }

    if (qNeg) {
      // Forza sempre il negozio dalla URL, ignorando il valore precedente
      setSelectedNegozio(qNeg);
    }

    // Marca i parametri come processati
    initialParamsProcessed.current = true;
  }, [settings?.month, settings?.year, location.search]);

  useEffect(() => {
    if (negoziData && negoziData.length > 0) {
      const filtered = isCoordinatoreLocked
        ? negoziData.filter((n) => lockNegoziConNegativi.includes(n.neg))
        : negoziData;
      setNegoziAccessibili(filtered);
      // Non sovrascrivere il negozio se è già stato impostato dalla query string (?neg=...)
      const qNeg = new URLSearchParams(location.search).get('neg');
      const defaultNeg = filtered[0]?.neg;
      if (!selectedNegozio && defaultNeg) {
        const requested = qNeg || settings?.storeId || defaultNeg;
        if (!isCoordinatoreLocked || lockNegoziConNegativi.includes(requested)) {
          setSelectedNegozio(requested);
        } else {
          setSelectedNegozio(defaultNeg);
        }
      }
    }
  }, [negoziData, settings?.storeId, selectedNegozio, isCoordinatoreLocked, lockNegoziConNegativi, location.search]);

  useEffect(() => {
    if (!isCoordinatoreLocked || lockNegoziConNegativi.length === 0) return;
    if (!selectedNegozio || !lockNegoziConNegativi.includes(selectedNegozio)) {
      setSelectedNegozio(lockNegoziConNegativi[0]);
    }
  }, [isCoordinatoreLocked, lockNegoziConNegativi, selectedNegozio]);

  // Persisti preferenze quando cambiano selezioni
  useEffect(() => {
    if (selectedNegozio && selectedMese && selectedAnno) {
      setSettings(prev => ({
        ...(prev || { storeId: selectedNegozio, month: selectedMese, year: selectedAnno }),
        storeId: selectedNegozio,
        month: selectedMese,
        year: selectedAnno,
      }));
    }
  }, [selectedNegozio, selectedMese, selectedAnno, setSettings]);

  useEffect(() => {
    if (!selectedNegozio || !selectedMese || !selectedAnno) return;

    const params = new URLSearchParams(location.search);
    if (params.get('neg') === selectedNegozio
        && params.get('mese') === String(selectedMese)
        && params.get('anno') === String(selectedAnno)) {
      return;
    }

    params.set('neg', selectedNegozio);
    params.set('mese', String(selectedMese));
    params.set('anno', String(selectedAnno));
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  }, [selectedNegozio, selectedMese, selectedAnno, location.pathname, location.search, navigate]);

  const { data: dipData, isLoading: dipLoading, isError: dipError, refetch: refetchDip } = useDipendenti(selectedNegozio);

  // Carica i dipendenti autorizzati per LEGGE 104 PERMESSO all'avvio
  useEffect(() => {
    const fetchAutorizzati104 = async () => {
      try {
        const codiciAutorizzati = await orariGeneratiService.getDipendentiAutorizzati104();
        setDipendentiAutorizzati104(codiciAutorizzati);
        console.log('Dipendenti autorizzati 104:', codiciAutorizzati);
      } catch (err) {
        console.error('Errore caricamento dipendenti autorizzati 104:', err);
        // In caso di errore, assumiamo nessun filtro (Admin)
        setDipendentiAutorizzati104([]);
      }
    };
    fetchAutorizzati104();
  }, []); // Solo all'avvio

  // Carica utilizzo giorni LEGGE 104 per il mese selezionato (aggiornato al cambio mese/anno)
  const refetchUtilizzo104 = useCallback(async () => {
    if (!selectedMese || !selectedAnno) return;
    const monthStr = `${selectedAnno}-${String(selectedMese).padStart(2, '0')}`;
    try {
      const data = await orariGeneratiService.getUtilizzo104(monthStr);
      setUtilizzo104(data);
    } catch (err) {
      console.error('Errore caricamento utilizzo 104:', err);
      setUtilizzo104({});
    }
  }, [selectedMese, selectedAnno]);

  useEffect(() => {
    refetchUtilizzo104();
  }, [refetchUtilizzo104]);

  const period = useMemo(() => {
    if (!selectedMese || !selectedAnno) return null;
    const monthIndex = selectedMese - 1; // 0-based
    const firstOfMonth = dayjs().year(selectedAnno).month(monthIndex).date(1);
    const lastOfMonth = firstOfMonth.endOf('month');
    // Estensione: inizio dal lunedì precedente (se il 1 non è lunedì), fine alla domenica successiva (se l'ultimo non è domenica)
    const daysToMondayFirst = (firstOfMonth.day() + 6) % 7; // 0..6, 0 se lunedì
    const startDay = firstOfMonth.subtract(daysToMondayFirst, 'day');
    const daysToSundayLast = (7 - lastOfMonth.day()) % 7; // 0..6, 0 se domenica
    const endDay = lastOfMonth.add(daysToSundayLast, 'day');
    const startDate = startDay.format('YYYY-MM-DD');
    const endDate = endDay.format('YYYY-MM-DD');
    return { startDate, endDate };
  }, [selectedMese, selectedAnno]);

  // Admin bypassa tutti i lock di competenza
  const isLocked = useMemo(() => isAdmin ? false : isCompetenzaLocked(selectedAnno, selectedMese), [isAdmin, selectedAnno, selectedMese]);
  const isLockedForUser = useCallback((year: number, month: number) => isAdmin ? false : isCompetenzaLocked(year, month), [isAdmin]);

  const { data: turniData, isLoading: turniLoading, refetch: refetchTurni } = useTurniNegozio(
    selectedNegozio,
    period?.startDate,
    period?.endDate,
    { refetchInterval: false }
  );

  const refetchAnnotazioniGiorno = useCallback(async () => {
    if (!selectedNegozio || !period?.startDate || !period?.endDate) return;
    try {
      const annParams = new URLSearchParams({
        negozio: selectedNegozio,
        dataInizio: period.startDate,
        dataFine: period.endDate
      });
      const annData = await apiRequest<Record<string, string[]>>(`/orari-generati/annotazioni/giorno?${annParams.toString()}`);
      setAnnotazioniGiorno(annData || {});
    } catch {
      setAnnotazioniGiorno({});
    }
  }, [selectedNegozio, period?.startDate, period?.endDate]);

  // Carica dipendenti comprensivi dei temporanei ogni volta che cambiano negozio/mese/anno
  // o quando arrivano lista base dipendenti / turni già caricati da useTurniNegozio.
  useEffect(() => {
    if (selectedNegozio && selectedMese && selectedAnno && !dipLoading && Array.isArray(turniData)) {
      // Resetta i ref: il salvataggio sarà bloccato finché fetchDipendenti non ha caricato i dati
      attiviCaricatiRef.current = false;
      attiviKeyRef.current = '';
      // Usa la funzione che unisce dipendenti normali, temporanei attivi e storici
      fetchDipendenti(turniData as TurnoCreato[]);
    }
  }, [selectedNegozio, selectedMese, selectedAnno, dipLoading, dipData, turniData]);

  // Periodi maternità per disabilitare Attivo e informare la UI
  const { data: maternitaData } = useMaternitaNegozio(
    selectedNegozio,
    period?.startDate,
    period?.endDate
  );

  // Mappa rapida nome->true se in maternità nel periodo selezionato (almeno un giorno)
  // Mappa periodi per maternità (IDMat 1..6) e allattamento (IDMat 7) separati
  const periodiMaternita = useMemo(() => {
    const map: Record<string, Array<{ start: string; end: string }>> = {};
    if (Array.isArray(maternitaData)) {
      (maternitaData as any[]).forEach(p => {
        if (p.idMat >= 1 && p.idMat <= 6) {
          const nome = p.nome as string;
          const start = String(p.dataInizio);
          const end = String(p.dataFine);
          if (!map[nome]) map[nome] = [];
          map[nome].push({ start, end });
        }
      });
    }
    return map;
  }, [maternitaData]);

  const periodiAllattamento = useMemo(() => {
    const map: Record<string, Array<{ start: string; end: string }>> = {};
    if (Array.isArray(maternitaData)) {
      (maternitaData as any[]).forEach(p => {
        // Allattamento identificato da idMat == 7
        if (p.idMat === 7) {
          const nome = p.nome as string;
          const start = String(p.dataInizio);
          const end = String(p.dataFine);
          if (!map[nome]) map[nome] = [];
          map[nome].push({ start, end });
        }
      });
    }
    return map;
  }, [maternitaData]);

  // (spostato più in basso dopo isNuovoAssunto)

  const { data: statsData, isLoading: statsLoading, refetch: refetchStats } = useStatsNegozio(
    selectedNegozio,
    selectedMese ? mesi[selectedMese - 1] : undefined,
    selectedAnno || undefined as any
  );

  // Carica stato visibilità per negozio/mese/anno selezionati
  useEffect(() => {
    const loadVisibilita = async () => {
      try {
        if (!selectedNegozio || !selectedMese || !selectedAnno) { setMeseVisibile(null); return; }
        const meseNome = mesi[selectedMese - 1];
        const res = await visibilitaService.getStatus(selectedNegozio, meseNome, selectedAnno);
        setMeseVisibile(!!res?.visibile);
      } catch (e) {
        // Se non esiste riga o errore, considera non visibile => bottone MOSTRA
        setMeseVisibile(false);
      }
    };
    loadVisibilita();
  }, [selectedNegozio, selectedMese, selectedAnno, mesi]);

  const handleToggleVisibilita = useCallback(async () => {
    if (!selectedNegozio || !selectedMese || !selectedAnno || meseVisibile === null) return;
    setVisibilitaBusy(true);
    try {
      const meseNome = mesi[selectedMese - 1];
      const next = !meseVisibile;
      await visibilitaService.setVisibilita(selectedNegozio, meseNome, selectedAnno, next);
      setMeseVisibile(next);
    } catch (e) {
      setError('Errore aggiornamento visibilità mese');
    } finally {
      setVisibilitaBusy(false);
    }
  }, [selectedNegozio, selectedMese, selectedAnno, meseVisibile, mesi]);

  // Sincronizza statistiche query -> stato locale per la tabella
  useEffect(() => {
    if (Array.isArray(statsData)) {
      setStatistiche(statsData as any);
    }
  }, [statsData]);

  useEffect(() => {
    if (selectedNegozio && selectedMese && selectedAnno && turniData) {
      // Ricostruisci matrice includendo i turni del mese precedente e successivo visibili nella griglia (lun->dom)
      const data = turniData as any[];
      const tutti = dipendenti;
      const currMonth = dayjs().year(selectedAnno).month(selectedMese - 1);
      const prevMonth = currMonth.subtract(1, 'month');
      const nextMonth = currMonth.add(1, 'month');
      const daysInCurr = currMonth.daysInMonth();
      const daysInPrev = prevMonth.daysInMonth();
      const daysInNext = nextMonth.daysInMonth();
      const matrice = tutti.map(dipendente => {
        const orariDip: { [giorno: number]: TurnoCreato[] } = {};
        const orariPrevDip: { [giorno: number]: TurnoCreato[] } = {};
        const orariNextDip: { [giorno: number]: TurnoCreato[] } = {};
        for (let day = 1; day <= daysInCurr; day++) orariDip[day] = [];
        for (let d = 1; d <= daysInPrev; d++) orariPrevDip[d] = [];
        for (let d = 1; d <= daysInNext; d++) orariNextDip[d] = [];
        data.forEach(orario => {
          if (orario.descrizione === dipendente.nome) {
            const d = dayjs(orario.data);
            if (d.isSame(currMonth, 'month')) {
              orariDip[d.date()].push(orario);
            } else if (d.isSame(prevMonth, 'month')) {
              orariPrevDip[d.date()].push(orario);
            } else if (d.isSame(nextMonth, 'month')) {
              orariNextDip[d.date()].push(orario);
            }
          }
        });
        return { dipendente: dipendente.nome, orari: orariDip, orariPrev: orariPrevDip, orariNext: orariNextDip } as OrarioMatrice;
      });
      setMatriceOrari(matrice);
    }
  }, [selectedNegozio, selectedMese, selectedAnno, dipendenti, turniData]);

  useEffect(() => {
    refetchAnnotazioniGiorno();
  }, [refetchAnnotazioniGiorno]);

  // Reset fase weekend SOLO quando cambiano negozio/mese/anno
  useEffect(() => {
    if (selectedNegozio && selectedMese && selectedAnno) {
      setWeekendPhase('domeniche');
    }
  }, [selectedNegozio, selectedMese, selectedAnno]);

  // L'effetto che calcola le statistiche viene definito più in basso,
  // dopo la dichiarazione di calcolaStatisticheTurni (memoizzata con useCallback)

  // Disabilita il menu contestuale del browser quando il menu custom è aperto
  useEffect(() => {
    if (!contextMenu) return;
    const blockBrowserContext = (e: MouseEvent) => {
      // Impedisci il menu nativo finché è aperto il nostro
      e.preventDefault();
    };
    document.addEventListener('contextmenu', blockBrowserContext, { capture: true });
    return () => {
      document.removeEventListener('contextmenu', blockBrowserContext, { capture: true } as any);
    };
  }, [contextMenu, rowSelection, allGridDays]);

  // Helper function per riconoscere dipendenti temporanei
  const isDipendenteTemporaneo = (nomeDipendente: string) => {
    return dipendentiTemporanei.some(dt => dt.nome === nomeDipendente);
  };

  // Helper function per riconoscere dipendenti momentanei (nome termina con '*')
  const isDipendenteMomentaneo = (nomeDipendente: string) => {
    return (nomeDipendente || '').trimEnd().endsWith('*');
  };

  // Helper: colore del nome dipendente nella griglia
  // momentaneo → viola, temporaneo → arancione, normale → inherit
  const getDipendenteColor = (nomeDipendente: string): string => {
    if (isDipendenteMomentaneo(nomeDipendente)) return '#9C27B0';
    if (isDipendenteTemporaneo(nomeDipendente)) return '#FF9800';
    return 'inherit';
  };

  // Helper: negozio di provenienza per dipendente temporaneo
  const getNegozioProvenienza = (nomeDipendente: string): string | undefined => {
    const found = dipendentiTemporanei.find(dt => dt.nome === nomeDipendente);
    // Preferisci il negozio di provenienza (home store) se presente
    return found?.negozioOriginale || undefined;
  };

  // Helper: nuovo assunto riferito al periodo selezionato (< 6 settimane dal primo giorno del mese visualizzato)
  // Così l'indicatore "●" è coerente con la logica backend che valuta la data del turno
  const isNuovoAssunto = useCallback((nomeDipendente: string) => {
    // 1) Se presente override manuale (checkbox), usa quello
    const ov = neoassuntaOverride[nomeDipendente];
    if (typeof ov === 'boolean') return ov;
    // 2) Default basato sulla data di assunzione (<= 42 giorni)
    // Cerca tra dipendenti normali e temporanei
    const all = [...dipendenti, ...dipendentiTemporanei];
    const found = all.find(d => d.nome === nomeDipendente);
    if (!found || !found.dataAssunzione) return false;
    const assunzione = dayjs(found.dataAssunzione);
    if (!assunzione.isValid()) return false;
    // Riferimento: primo giorno del mese selezionato (coerente con generazione del periodo)
    if (!selectedAnno || !selectedMese) return false;
    const refDate = dayjs().year(selectedAnno).month(selectedMese - 1).date(1);
    const diffDays = refDate.diff(assunzione, 'day');
    // Meno di 6 settimane: < 42 giorni
    return diffDays >= 0 && diffDays < 42;
  }, [neoassuntaOverride, dipendenti, dipendentiTemporanei, selectedAnno, selectedMese]);

  // Ordine richiesto:
  // 1) Attivi normali (in alto)
  // 2) Attivi "speciali" (Neoassunta o con almeno un giorno in maternità/allattamento) subito sotto
  // 3) Non attivi (in fondo)
  const sortedMatriceOrari = useMemo(() => {
    const isSpecial = (nome: string) => {
      const neo = isNuovoAssunto(nome);
      const hasMat = (periodiMaternita[nome] || []).length > 0;
      const hasAll = (periodiAllattamento[nome] || []).length > 0;
      return neo || hasMat || hasAll;
    };
    const withIndex = matriceOrari.map((r, idx) => ({ r, idx }));
    withIndex.sort((a, b) => {
      const aActive = !!dipendentiAttivi[a.r.dipendente];
      const bActive = !!dipendentiAttivi[b.r.dipendente];
      if (aActive !== bActive) return aActive ? -1 : 1; // attivi sopra non attivi
      if (aActive) {
        const aSpecial = isSpecial(a.r.dipendente);
        const bSpecial = isSpecial(b.r.dipendente);
        if (aSpecial !== bSpecial) return aSpecial ? 1 : -1; // special sotto i normali
      }
      return a.idx - b.idx; // ordine originario stabile nei gruppi
    });
    return withIndex.map(x => x.r);
  }, [matriceOrari, dipendentiAttivi, isNuovoAssunto, periodiMaternita, periodiAllattamento]);

  // L'ordine per statistiche segue quello visuale
  const dipendentiOrder = useMemo(
    () => sortedMatriceOrari.map(r => normalizeName(r.dipendente)),
    [sortedMatriceOrari, normalizeName]
  );
  const statisticheSorted = useMemo(() => {
    const attivi = statistiche.filter(s => dipendentiAttivi[`${s.nomeDipendente} ${s.cognomeDipendente}`] !== false);
    if (!dipendentiOrder.length) return attivi;
    const indexOf = (s: DipendenteStatistiche) => dipendentiOrder.indexOf(normalizeName(`${s.nomeDipendente} ${s.cognomeDipendente}`));
    return [...attivi].sort((a, b) => {
      const ia = indexOf(a);
      const ib = indexOf(b);
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  }, [statistiche, dipendentiOrder, normalizeName, dipendentiAttivi]);
  const statisticheTurniSorted = useMemo(() => {
    const attivi = statisticheTurni.filter(s => dipendentiAttivi[`${s.nomeDipendente} ${s.cognomeDipendente}`] !== false);
    if (!dipendentiOrder.length) return attivi;
    const indexOf = (s: DipendenteStatisticheTurni) => dipendentiOrder.indexOf(normalizeName(`${s.nomeDipendente} ${s.cognomeDipendente}`));
    return [...attivi].sort((a, b) => {
      const ia = indexOf(a);
      const ib = indexOf(b);
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  }, [statisticheTurni, dipendentiOrder, normalizeName, dipendentiAttivi]);

  // Helper function per riconoscere turni "esterni" (dipendente del negozio che lavora altrove)
  const isTurnoEsterno = (turno: TurnoCreato) => {
    // Normalizza confronto (evita mismatch per spazi/case)
    const tneg = (turno?.neg || '').toString().trim().toLowerCase();
    const snego = (selectedNegozio || '').toString().trim().toLowerCase();
    return tneg !== '' && tneg !== snego;
  };

  // Candidati per Cambio turno: dipendenti del negozio selezionato e temporanei assegnati al negozio
  const swapCandidates = useMemo(() => {
    const src = (swapDialog?.source) || (rowSelection?.dipendente) || '';
    const list = dipendenti.filter(d => d.nome !== src && (d.neg === selectedNegozio || isDipendenteTemporaneo(d.nome)));
    // Ordina alfabeticamente
    return list.sort((a,b) => a.nome.localeCompare(b.nome));
  }, [swapDialog, rowSelection, dipendenti, selectedNegozio, dipendentiTemporanei, isDipendenteTemporaneo]);

  const handleRuleToggle = (ruleId: string) => {
    setSchedulingRules(prev => 
      prev.map(rule => 
        rule.id === ruleId 
          ? { ...rule, enabled: !rule.enabled }
          : rule
      )
    );
  };

  // ── Log Pianificazione ────────────────────────────────────────────────────
  const handleOpenLogs = async () => {
    if (!selectedNegozio || !selectedMese || !selectedAnno) return;
    setLogsDrawerOpen(true);
    setLogsLoading(true);
    setLogsError(null);
    try {
      // Passa gli stessi filtri usati durante la generazione:
      // – dipendentiAttivi: solo quelli con il flag attivo
      // – neoassunte: dipendenti marcati come neoassunti (override manuale o regola 42 giorni)
      const dipendentiAttiviSelezionati = Object.entries(dipendentiAttivi)
        .filter(([_, attivo]) => attivo)
        .map(([nome]) => nome);
      const neoassunteSelezionate = [...dipendenti, ...dipendentiTemporanei]
        .map(d => d.nome)
        .filter(nome => (dipendentiAttivi[nome] ?? true) && isNuovoAssunto(nome));

      const data = await fetchSchedulingLogs(
        selectedNegozio,
        mesi[selectedMese - 1],
        selectedAnno,
        dipendentiAttiviSelezionati.length > 0 ? dipendentiAttiviSelezionati : undefined,
        neoassunteSelezionate.length > 0 ? neoassunteSelezionate : undefined
      );
      setLogsData(data);
    } catch (err: any) {
      setLogsError(err?.response?.data || err?.message || 'Errore nel caricamento dei log');
    } finally {
      setLogsLoading(false);
    }
  };

  // fetchNegoziAccessibili sostituito da useNegozi hook

  const fetchDipendenti = async (turniPeriodo: TurnoCreato[] = []) => {
    if (!selectedNegozio) return;
    
    try {
      // Fetch dipendenti normali
      const dipendentiNormali = await apiRequest<Dipendente[]>(`/dipendenti/negozio/${selectedNegozio}`);
      
      // Carica anagrafica completa per mappare il negozio di provenienza dei temporanei
      let dipendentiTutti: Dipendente[] = [];
      try {
        dipendentiTutti = await apiRequest<Dipendente[]>('/dipendenti');
      } catch (e) {
        console.warn('WARN: impossibile caricare elenco completo dipendenti per mappare negozio di provenienza', e);
      }
      const origineByCodice: Record<string, string> = {};
      dipendentiTutti.forEach(d => {
        if (d.codicePersonale) origineByCodice[d.codicePersonale] = d.neg;
      });
      
      // Fetch dipendenti temporanei attivi per il mese/anno selezionato
      let dipendentiTempData: any[] = [];
      if (selectedMese && selectedAnno) {
        const meseNome = mesi[selectedMese - 1];
        dipendentiTempData = await apiRequest<any[]>(`/orari-generati/dipendenti-temporanei/${selectedNegozio}?mese=${meseNome}&anno=${selectedAnno}`);
      } else {
        // Fallback per compatibilità - prende tutti i dipendenti temporanei
        dipendentiTempData = await apiRequest<any[]>(`/orari-generati/dipendenti-temporanei/${selectedNegozio}`);
      }
      
      // Converti dipendenti temporanei nel formato Dipendente SENZA suffisso (TEMP)
      const dipendentiTempFormattati: DipendenteTemporaneo[] = dipendentiTempData.map(dt => {
        const codice = dt.codicePersonale as string | undefined;
        // Trova negozio di provenienza dall'anagrafica completa per stesso codice personale
        const negozioOriginale = codice ? origineByCodice[codice] : undefined;
        return {
          codicePersonale: dt.codicePersonale,
          nome: dt.nome, // NON aggiungere (TEMP)
          neg: dt.neg, // questo può rappresentare la destinazione corrente
          oreSettimanali: dt.oreSettimanali,
          livello: dt.livello,
          dataAssunzione: dt.dataAssunzione,
          dataFineContratto: dt.dataFineContratto,
          lunedi: dt.lunedi,
          martedi: dt.martedi,
          mercoledi: dt.mercoledi,
          giovedi: dt.giovedi,
          venerdi: dt.venerdi,
          sabato: dt.sabato,
          domenica: dt.domenica,
          negozioOriginale,
        } as DipendenteTemporaneo;
      });
      
      // Mantieni traccia separata dei dipendenti temporanei ATTIVI
      setDipendentiTemporanei(dipendentiTempFormattati);
      
      // Se mese e anno sono selezionati, cerca anche dipendenti con turni storici
      let dipendentiStorici: Dipendente[] = [];
      if (selectedMese && selectedAnno) {
        // Recupera TUTTI i dipendenti temporanei del negozio (di tutti i mesi) per escluderli dai dipendenti storici
        let tuttiDipendentiTemp: any[] = [];
        try {
          tuttiDipendentiTemp = await apiRequest<any[]>(`/orari-generati/dipendenti-temporanei/${selectedNegozio}`);
        } catch (err) {
          console.warn('WARN: impossibile recuperare tutti i dipendenti temporanei', err);
        }
        const nomiDipendentiTempTuttiIMesi = new Set(tuttiDipendentiTemp.map(dt => dt.nome));
        
        // Estrai nomi unici dai turni che non sono dipendenti del negozio né temporanei attivi
        const nomiEsistenti = new Set([
          ...dipendentiNormali.map(d => d.nome),
          ...dipendentiTempFormattati.map(d => d.nome)
        ]);
        
        const nomiStorici = new Set<string>();
        turniPeriodo.forEach(turno => {
          // Se il turno è "esterno" (dipendente del negozio che lavora altrove) oppure
          // è un dipendente temporaneo che aveva turni ma non è più attivo
          if (turno.neg !== selectedNegozio || !nomiEsistenti.has(turno.descrizione)) {
            // ESCLUDE i dipendenti temporanei di altri mesi: devono essere visibili solo nel mese in cui sono stati aggiunti
            if (!nomiDipendentiTempTuttiIMesi.has(turno.descrizione)) {
              nomiStorici.add(turno.descrizione);
            }
          }
        });
        
        // Recupera i dettagli dei dipendenti storici dalla tabella principale
        if (nomiStorici.size > 0) {
          try {
            // usa l'elenco già caricato se disponibile
            const elenco = dipendentiTutti.length ? dipendentiTutti : await apiRequest<Dipendente[]>('/dipendenti');
            dipendentiStorici = elenco.filter(d => nomiStorici.has(d.nome));
          } catch (err) {
            console.error('Errore nel recupero dipendenti storici:', err);
          }
        }
      }
      
      // Combina dipendenti normali, temporanei attivi e storici (evitando duplicati)
      const nomiCombinati = new Set<string>();
      const tuttiDipendenti: Dipendente[] = [];

      // Calcola l'intervallo del mese selezionato per filtrare i momentanei per data
      const meseSelezionatoInizio = (selectedMese && selectedAnno)
        ? dayjs().year(selectedAnno).month(selectedMese - 1).startOf('month')
        : null;
      const meseSelezionatoFine = (selectedMese && selectedAnno)
        ? dayjs().year(selectedAnno).month(selectedMese - 1).endOf('month')
        : null;
      
      // Aggiungi dipendenti normali (i momentanei TEST### vengono filtrati per data assunzione/fine contratto)
      dipendentiNormali.forEach(d => {
        if (!nomiCombinati.has(d.nome)) {
          // Filtra i momentanei (codice TEST###) in base al mese visualizzato
          if (d.codicePersonale?.startsWith('TEST') && meseSelezionatoInizio && meseSelezionatoFine) {
            const assunzione = d.dataAssunzione ? dayjs(d.dataAssunzione) : null;
            const fineContratto = d.dataFineContratto ? dayjs(d.dataFineContratto) : null;
            // Mostra solo se il mese selezionato è >= mese assunzione
            if (assunzione && meseSelezionatoFine.isBefore(assunzione, 'month')) return;
            // Mostra solo se il mese selezionato è <= mese fine contratto (se presente)
            if (fineContratto && meseSelezionatoInizio.isAfter(fineContratto, 'month')) return;
          }
          tuttiDipendenti.push(d);
          nomiCombinati.add(d.nome);
        }
      });
      
      // Aggiungi dipendenti temporanei attivi
      dipendentiTempFormattati.forEach(d => {
        if (!nomiCombinati.has(d.nome)) {
          tuttiDipendenti.push(d);
          nomiCombinati.add(d.nome);
        }
      });
      
      // Aggiungi dipendenti storici
      dipendentiStorici.forEach(d => {
        if (!nomiCombinati.has(d.nome)) {
          tuttiDipendenti.push(d);
          nomiCombinati.add(d.nome);
        }
      });
      
      setDipendenti(tuttiDipendenti);
      
      // Inizializza stato Attivo: localStorage è la fonte primaria (sincrona, sempre aggiornata),
      // il backend è il fallback (può essere ritardato o avere problemi di rete)
      const savedKey = getAttiviKey();
      let saved: Record<string, boolean> = {};
      try {
        if (savedKey) {
          // 1. Prima prova localStorage (sincrono, aggiornato immediatamente al toggle)
          const cached = localStorage.getItem(`apporari-${savedKey}`);
          if (cached) {
            saved = JSON.parse(cached);
          } else {
            // 2. Fallback backend solo se localStorage è vuoto (es. primo accesso o altro device)
            const resp = await impostazioniService.getImpostazione(savedKey);
            if (resp && resp.valore) {
              saved = JSON.parse(resp.valore);
              // Popola anche localStorage per i prossimi reload
              try { localStorage.setItem(`apporari-${savedKey}`, resp.valore); } catch {}
            }
          }
        }
      } catch (e) {
        console.warn('Lettura preferenze Attivi fallita, uso defaults:', e);
      }

      const attivi: {[nome: string]: boolean} = {};
      tuttiDipendenti.forEach(d => {
        // Cerca prima per codicePersonale (formato nuovo), poi per nome (formato legacy)
        let val: boolean;
        if (saved && d.codicePersonale && Object.prototype.hasOwnProperty.call(saved, d.codicePersonale)) {
          val = saved[d.codicePersonale];
        } else if (saved && Object.prototype.hasOwnProperty.call(saved, d.nome)) {
          val = saved[d.nome]; // compatibilità con dati salvati nel vecchio formato
        } else {
          val = true; // default: attivo
        }
        attivi[d.nome] = val;
      });
      // Segnala che il caricamento per questa chiave è avvenuto: da ora il salvataggio è abilitato
      attiviCaricatiRef.current = true;
      attiviKeyRef.current = savedKey;
      setDipendentiAttivi(attivi);
      
    } catch (err) {
      setError('Errore nel caricamento dei dipendenti');
      console.error('Error fetching dipendenti:', err);
    }
  };

  const generaWeekendPerFase = async (fase: 'domeniche' | 'sabati' | 'riposi' | 'resto') => {
    try {
      if (!selectedNegozio || !selectedMese || !selectedAnno) {
        setError('Seleziona negozio, mese e anno');
        return;
      }

      resetGenerationFeedback();
      setLoading(true);

      const regoleAttive = schedulingRules.filter(rule => rule.enabled).map(rule => rule.id);
      const dipendentiAttiviSelezionati = Object.entries(dipendentiAttivi)
        .filter(([_, attivo]) => attivo)
        .map(([nome]) => nome);

      // Calcola i nominativi da trattare come "neoassunte" lato backend (override + regola 42 giorni)
      const neoassunteSelezionate = [...dipendenti, ...dipendentiTemporanei]
        .map(d => d.nome)
        .filter(nome => (dipendentiAttivi[nome] ?? true) && isNuovoAssunto(nome));

      const requestData: any = {
        negozio: selectedNegozio,
        mese: mesi[selectedMese - 1],
        anno: selectedAnno, // invia come numero
        fase,
        regoleAttive, // invia sempre (anche se array vuoto)
        dipendentiAttivi: dipendentiAttiviSelezionati, // invia sempre (anche se array vuoto)
        neoassunte: neoassunteSelezionate
      };

      // Preferenze specifiche richieste per i nuovi comportamenti
      if (fase === 'domeniche') {
        // Alterna il tipo di turno tra domeniche consecutive per lo stesso dipendente
        requestData.alternanzaTurniDomeniche = true;
      }
      if (fase === 'sabati') {
        // Evita di assegnare al sabato lo stesso turno dato alla domenica della stessa settimana
        requestData.sabatoDiversoDaDomenica = true;
      }

      const rawResponse = await apiRequest<string>('/orari-generati/genera-weekend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });
      handleGenerationResponse(rawResponse, fase);

  // Ricarica i turni e le statistiche a fine operazione e POI avanza la fase
  await refetchTurni();
  await refetchStats();
  await refetchUtilizzo104();
  
  // Notifica al guard che cache deve essere invalidata (trigger check immediato)
  console.log('[GenerazioneOrario] Generazione weekend per fase completata - emetto evento di invalidazione cache');
  window.dispatchEvent(new CustomEvent('coordinatore-ore-lock-cache-invalidated'));
  
      setWeekendPhase(prev => {
        if (prev === 'domeniche') return 'sabati';
        if (prev === 'sabati') return 'riposi';
        if (prev === 'riposi') return 'resto';
        return 'domeniche';
      });
    } catch (err: any) {
      // Gestione errori Axios: err.response?.status e err.response?.data
      const status = err?.response?.status ?? err?.status;
      const serverMsg = err?.response?.data ? String(err.response.data) : null;
      if (status === 404) {
        // Crea matrice vuota se non ci sono orari
        const daysInMonth = dayjs().year(selectedAnno).month(selectedMese - 1).daysInMonth();
        
        // I dipendenti temporanei sono già inclusi nella lista dipendenti dal database
        const tuttiDipendenti = dipendenti;
        
        const matriceVuota = tuttiDipendenti.map(dipendente => {
          const orariVuoti: { [giorno: number]: TurnoCreato[] } = {};
          for (let day = 1; day <= daysInMonth; day++) {
            orariVuoti[day] = [];
          }
          return {
            dipendente: dipendente.nome,
            orari: orariVuoti
          };
        });
        setMatriceOrari(matriceVuota);
        
        // Prova comunque a caricare le statistiche
        try {
          await refetchStats();
        } catch (statErr) {
          console.error('Error fetching statistiche:', statErr);
        }
      } else if (status === 409) {
        // Periodo bloccato (lock competenza)
        setError(serverMsg || 'Operazione non consentita: periodo bloccato');
      } else if (status === 400 || status === 500) {
        // Messaggio lato server (es. validazioni o eccezioni)
        setError(serverMsg || 'Errore nella generazione degli orari');
        // Prova comunque a ricaricare per riflettere eventuali cambi parziali
        try { await refetchTurni(); await refetchStats(); await refetchUtilizzo104(); } catch {}
      } else {
        setError(serverMsg || 'Errore nel caricamento degli orari generati');
      }
      console.error('Errore generazione weekend:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper per etichetta del pulsante a seconda della fase corrente
  const getWeekendPhaseLabel = (fase: 'domeniche' | 'sabati' | 'riposi' | 'resto') => {
    switch (fase) {
      case 'domeniche':
        return 'DOMENICHE';
      case 'sabati':
        return 'SABATI';
      case 'riposi':
        return 'RIPOSI SETTIMANALI';
      case 'resto':
        return 'RESTO SETTIMANA';
      default:
        return 'DOMENICHE';
    }
  };

  // La risposta descrive l'esito della generazione; i turni ricaricati possono
  // risolvere una segnalazione anche dopo un'altra fase o una modifica della cella.
  const activeGenerationWarnings = useMemo(() => {
    if (!Array.isArray(turniData)) return generationWarnings;
    const assignedByDate = new Map<string, TurnoCreato[]>();
    (turniData as TurnoCreato[]).forEach(turno => {
      if (isBaselineTurno(turno)) return;
      const date = dayjs(turno.data).format('YYYY-MM-DD');
      const assigned = assignedByDate.get(date) ?? [];
      assigned.push(turno);
      assignedByDate.set(date, assigned);
    });
    return generationWarnings.filter(warning => {
      const assigned = assignedByDate.get(warning.data) ?? [];
      if (warning.dipendente) {
        return !assigned.some(turno => turno.descrizione === warning.dipendente);
      }
      if (warning.turno && ['Mattina', 'Pomeriggio', 'Intermezzo'].includes(warning.turno)
          && warning.required > 0) {
        const covered = new Set(assigned.filter(turno => turno.neg === selectedNegozio
          && turno.turno?.trim().toLowerCase() === warning.turno?.toLowerCase())
          .map(turno => turno.descrizione));
        return covered.size < warning.required;
      }
      if (warning.reason.startsWith('Copertura incompleta:')
          || warning.reason.startsWith('Piano riposi sabati incompleto:')) {
        const activeEmployees = dipendenti.filter(dipendente => dipendentiAttivi[dipendente.nome] ?? true);
        return activeEmployees.length === 0 || activeEmployees.some(dipendente =>
          !assigned.some(turno => turno.descrizione === dipendente.nome));
      }
      return true;
    });
  }, [generationWarnings, turniData, selectedNegozio, dipendenti, dipendentiAttivi]);

  // Evidenzia solo le date con segnalazioni ancora irrisolte.
  const isWarningDay = (dateObj: dayjs.Dayjs) => {
    if (activeGenerationWarnings.length === 0) return false;
    const iso = dateObj.format('YYYY-MM-DD');
    return activeGenerationWarnings.some(w => w.data === iso);
  };

  const handleExportPdf = () => {
    try {
      if (!selectedNegozio || !selectedMese || !selectedAnno || matriceOrari.length === 0) {
        setError('Niente da esportare: seleziona periodo e genera gli orari');
        return;
      }
      setExporting(true);
      esportaPdfMatrice({
        negozio: selectedNegozio,
        meseIndex: selectedMese - 1,
        anno: selectedAnno,
        mesi,
        matrice: matriceOrari,
        dipendentiAttivi,
        periodiMaternita,
        shouldMaskAssenze,
        isPrivileged,
        getDisplayTurnoLabel
      });
    } catch (e) {
      console.error('PDF export error:', e);
      setError('Errore durante l\'esportazione PDF');
    } finally {
      setExporting(false);
    }
  };

  // Esporta screenshot dell'intero calendario (modalità "Screenshot") in un unico PDF A4 orizzontale
  const handleExportScreenshotPdf = async () => {
    try {
      if (!matrixScrollRef.current || !selectedNegozio || !selectedMese || !selectedAnno) {
        setError('Parametri mancanti per export screenshot');
        return;
      }
      setExporting(true);
      await esportaScreenshotMatrice({
        container: matrixScrollRef.current,
        negozio: selectedNegozio,
        meseIndex: selectedMese - 1,
        anno: selectedAnno,
        mesi,
        setScreenshotMode,
        delayMs: 75
      });
    } catch (e) {
      console.error('Screenshot PDF error:', e);
      setError('Errore durante export screenshot PDF');
    } finally {
      setExporting(false);
    }
  };

  // Recupera dipendenti disponibili dagli altri negozi (escluso quello selezionato)
  const fetchDipendentiDisponibili = async () => {
    try {
      const negoziDaInterrogare = negoziAccessibili.filter(n => n.neg !== selectedNegozio);
      const results = await Promise.all(
        negoziDaInterrogare.map(n => apiRequest<Dipendente[]>(`/dipendenti/negozio/${n.neg}`))
      );
      const tutti = results.flat();
      tutti.sort((a, b) => (a.neg === b.neg ? a.nome.localeCompare(b.nome) : a.neg.localeCompare(b.neg)));
      setDipendentiDisponibili(tutti);
    } catch (err) {
      console.error('Error fetching dipendenti disponibili:', err);
    }
  };

  // Dialog: apri aggiunta dipendenti temporanei
  const handleOpenAggiungiDipendenti = async () => {
    await fetchDipendentiDisponibili();
    setAggiungiDipendentiDialog(true);
  };

  // Aggiungi dipendente temporaneo e chiudi dialog
  const handleAggiungiDipendente = async (dip: Dipendente) => {
    await aggiungiDipendenteTemporaneo(dip.nome);
    setAggiungiDipendentiDialog(false);
  };

  // Rimuovi dipendente temporaneo (per mese/anno correnti) e ricarica
  const handleRimuoviDipendente = async (nome: string) => {
    try {
      const params = new URLSearchParams({ nomeDipendente: nome, negozio: selectedNegozio || '' });
      if (selectedMese && selectedAnno) {
        params.append('mese', mesi[selectedMese - 1]);
        params.append('anno', String(selectedAnno));
      }
      await apiRequest(`/orari-generati/dipendenti-temporanei/rimuovi`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString()
      });
      await fetchDipendenti();
  await refetchTurni();
      setSuccess(`Dipendente ${nome} rimosso con successo`);
    } catch (err) {
      console.error('Errore nella rimozione del dipendente temporaneo:', err);
      setError('Errore nella rimozione del dipendente temporaneo');
    }
  };

  // Rimuovi dipendente momentaneo dalla tabella dipendenti_momentanei
  const handleRimuoviDipendenteMomentaneo = async (nomeDipendente: string) => {
    try {
      const dip = dipendenti.find(d => d.nome === nomeDipendente);
      if (!dip?.codicePersonale) {
        setError('Codice personale non trovato per il dipendente momentaneo');
        return;
      }
      await apiRequest(`/dipendenti-momentanei/${encodeURIComponent(dip.codicePersonale)}`, {
        method: 'DELETE'
      });
      await fetchDipendenti();
      await refetchTurni();
      setSuccess(`Dipendente momentaneo ${nomeDipendente} eliminato con successo`);
    } catch (err) {
      console.error('Errore nella rimozione del dipendente momentaneo:', err);
      setError('Errore nella rimozione del dipendente momentaneo');
    }
  };

  // Toggle attivo per singolo dipendente
  const handleToggleDipendenteAttivo = (nomeDipendente: string) => {
    setDipendentiAttivi(prev => ({ ...prev, [nomeDipendente]: !prev[nomeDipendente] }));
  };

  // Apri pagina Orario Dipendente con query params
  const handleOpenOrarioDipendente = async (nomeDipendente: string) => {
    if (!selectedNegozio || !selectedMese || !selectedAnno) return;

    // Se è un dipendente temporaneo, prova a risalire al negozio ORIGINARIO
    let negozioPerNavigazione = selectedNegozio;
    try {
      if (isDipendenteTemporaneo(nomeDipendente)) {
        // Recupera il record selezionato per ottenere il codice personale
        const dipSel = dipendenti.find(d => d.nome === nomeDipendente);
        if (dipSel && dipSel.codicePersonale) {
          // Carica anagrafica completa e trova il dipendente originale per codice
          const tutti = await apiRequest<Dipendente[]>(`/dipendenti`);
          const originale = tutti.find(x => x.codicePersonale === dipSel.codicePersonale);
          if (originale && originale.neg) {
            negozioPerNavigazione = originale.neg; // usa negozio originario
          }
        }
      }
    } catch (err) {
      console.warn('WARN: impossibile risolvere negozio originario, fallback al negozio corrente', err);
    }

    const params = new URLSearchParams({
      neg: negozioPerNavigazione,
      dip: nomeDipendente,
      mese: String(selectedMese),
      anno: String(selectedAnno)
    });
    navigate(`/orario-dipendente?${params.toString()}`);
  };

  // Aggiorna dati visualizzazione: i turni arrivano solo dalla query cache useTurniNegozio.
  const fetchOrariGenerati = async () => {
    if (!selectedNegozio || !selectedMese || !selectedAnno) return;
    try {
      setLoading(true);
      await refetchTurni();
      await refetchAnnotazioniGiorno();
      await refetchStats();
      await refetchUtilizzo104();
      setError(null);
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      const serverMsg = err?.response?.data ? String(err.response.data) : null;
      if (status === 403) {
        setError('Non hai i permessi per visualizzare questo negozio');
      } else if (status === 400 || status === 500) {
        setError(serverMsg || 'Errore nel caricamento degli orari generati');
      } else {
        setError('Errore nel caricamento degli orari generati');
      }
      console.error('Error fetching orari generati:', err);
    } finally {
      setLoading(false);
    }
  };

  // Il baseline mensile e gli orari standard sono invarianti per negozio/mese.
  // Manteniamo il primo controllo autorevole backend e riusiamo il risultato per
  // le aggiunte successive, evitando il costo di una scansione mensile per click.
  const ensureBaselineForSelectedPeriod = useCallback(async () => {
    const meseNome = mesi[selectedMese - 1];
    const key = `${selectedNegozio}|${meseNome}|${selectedAnno}`;
    if (baselineReadyRef.current.has(key)) return;

    const inFlight = baselineInFlightRef.current.get(key);
    if (inFlight) return inFlight;

    const request = apiRequest(
      `/orari-generati/genera-baseline?negozio=${selectedNegozio}&mese=${meseNome}&anno=${selectedAnno}`,
      { method: 'POST' }
    ).then(() => {
      baselineReadyRef.current.add(key);
    }).finally(() => {
      baselineInFlightRef.current.delete(key);
    });
    baselineInFlightRef.current.set(key, request);
    return request;
  }, [selectedNegozio, selectedMese, selectedAnno]);

  const getStandardTurnoOrari = useCallback(async (tipoTurno: string, dateIso: string) => {
    const key = `${selectedNegozio}|${dateIso}|${tipoTurno}`;
    const cached = standardTurniRef.current.get(key);
    if (cached) return cached;

    try {
      const dayName = dayjs(dateIso).format('dddd');
      const params = new URLSearchParams({ negozio: selectedNegozio, tipo: tipoTurno, giorno: dayName });
      const standard = await apiRequest<{ oraInizio: string | null; oraFine: string | null }>(`/turni/standard?${params.toString()}`);
      standardTurniRef.current.set(key, standard);
      return standard;
    } catch (error) {
      console.warn('Nessun turno standard trovato, fallback agli orari di default', error);
      const fallback = tipoTurno === 'Mattina'
        ? { oraInizio: '08:00', oraFine: '14:30' }
        : tipoTurno === 'Pomeriggio'
          ? { oraInizio: '14:30', oraFine: '21:30' }
          : { oraInizio: '13:00', oraFine: '19:00' };
      standardTurniRef.current.set(key, fallback);
      return fallback;
    }
  }, [selectedNegozio]);

  // Il turno è già salvato quando parte questo refresh: non bloccare il menu
  // né attendere statistiche/annotazioni indipendenti prima di rendere la UI riusabile.
  const refreshAfterTurnWrite = useCallback(() => {
    void Promise.allSettled([
      refetchTurni(),
      refetchAnnotazioniGiorno(),
      refetchStats(),
      refetchUtilizzo104()
    ]);
  }, [refetchTurni, refetchAnnotazioniGiorno, refetchStats, refetchUtilizzo104]);

  const fetchStatistiche = async () => {
    if (!selectedNegozio || !selectedMese || !selectedAnno) return;
    
    try {
      const data = await apiRequest<DipendenteStatistiche[]>(
        `/dipendenti/statistiche?negozio=${selectedNegozio}&mese=${mesi[selectedMese - 1]}&anno=${selectedAnno}`
      );
      setStatistiche(data);
    } catch (err) {
      console.error('Error fetching statistiche:', err);
      setStatistiche([]);
    }
  };

  const calcolaStatisticheTurni = useCallback(() => {
    if (matriceOrari.length === 0) return;

    const statisticheTurniCalcolate: DipendenteStatisticheTurni[] = matriceOrari.map(riga => {
      let mattine = 0;
      let pomeriggi = 0;
      let intermezzi = 0;
      let riposiSettimanali = 0;
      let riposiSabati = 0;
      let riposiDomeniche = 0;

      // Conta i turni per ogni giorno del mese
      const daysInMonth = dayjs().year(selectedAnno).month(selectedMese - 1).daysInMonth();
      for (let day = 1; day <= daysInMonth; day++) {
        const orari = riga.orari[day] || [];
        const date = dayjs().year(selectedAnno).month(selectedMese - 1).date(day);
        const dayOfWeek = date.day(); // 0 = Domenica, 6 = Sabato

        if (orari.length > 0) {
          // Ha almeno un turno assegnato
          orari.forEach(orario => {
            const turnoLower = orario.turno?.toLowerCase() || '';
            if (turnoLower === 'mattina' || turnoLower === 'mattina p.') {
              mattine++;
              return;
            }
            if (turnoLower === 'pomeriggio' || turnoLower === 'pomeriggio p.') {
              pomeriggi++;
              return;
            }
            if (turnoLower === 'intermezzo' || turnoLower === 'intermezzo p.') {
              intermezzi++;
              return;
            }
            if (turnoLower === 'personalizzato') {
              // Classifica in base a oraPrevInizio
              const hhmm = (orario.oraPrevInizio || '').substring(0, 5);
              const [hhStr, mmStr] = hhmm.split(':');
              const hh = parseInt(hhStr || '0', 10);
              const mm = parseInt(mmStr || '0', 10);
              if (!isNaN(hh) && !isNaN(mm)) {
                const minutes = hh * 60 + mm;
                if (minutes <= 11 * 60) {
                  mattine++;
                } else if (minutes > 11 * 60 && minutes < 13 * 60) {
                  intermezzi++;
                } else if (minutes >= 13 * 60) {
                  pomeriggi++;
                } else {
                  // fallback: considera non riposo
                  mattine++;
                }
                return;
              }
              // Se orario non parsabile, non contare come riposo; ignora
              return;
            }
            // Altri casi (es. RIPOSO/ASSENZA) contano come riposi
            if (dayOfWeek === 0) {
              riposiDomeniche++;
            } else if (dayOfWeek === 6) {
              riposiSabati++;
            } else {
              riposiSettimanali++;
            }
          });
        } else {
          // È a riposo (nessun turno assegnato)
          if (dayOfWeek === 0) {
            riposiDomeniche++;
          } else if (dayOfWeek === 6) {
            riposiSabati++;
          } else {
            riposiSettimanali++;
          }
        }
      }

      return {
        nomeDipendente: riga.dipendente.split(' ')[0] || '',
        cognomeDipendente: riga.dipendente.split(' ').slice(1).join(' ') || '',
        mattine,
        pomeriggi,
        intermezzi,
        riposiSettimanali,
        riposiSabati,
        riposiDomeniche
      };
    });

    setStatisticheTurni(statisticheTurniCalcolate);
  }, [matriceOrari, selectedMese, selectedAnno]);

  useEffect(() => {
    // Calcolo statistiche dipendente dalla matrice e dal periodo; la funzione è memoizzata
    if (matriceOrari.length > 0) {
      calcolaStatisticheTurni();
    }
  }, [calcolaStatisticheTurni, matriceOrari.length]);

  const generaOrariAutomatici = async () => {
    if (!selectedNegozio || !selectedMese || !selectedAnno) return;
    
    // Chiamata diretta senza popup
    executeGeneraOrariAutomatici();
  };

  const executeGeneraOrariAutomatici = async () => {
    if (!selectedNegozio || !selectedMese || !selectedAnno) return;
    
    try {
      setLoading(true);
      resetGenerationFeedback();
      
      // Ottieni le regole attive
      const regoleAttive = schedulingRules.filter(rule => rule.enabled).map(rule => rule.id);

      // I dipendenti temporanei sono gestiti dal DB; inviamo solo elenco attivi selezionati
      const dipendentiAttiviSelezionati = Object.keys(dipendentiAttivi).filter(nome => dipendentiAttivi[nome]);

      // Prepara body JSON coerente con DTO backend
      const neoassunteSelezionate = [...dipendenti, ...dipendentiTemporanei]
        .map(d => d.nome)
        .filter(nome => (dipendentiAttivi[nome] ?? true) && isNuovoAssunto(nome));
      const requestData: Record<string, any> = {
        negozio: selectedNegozio,
        mese: mesi[selectedMese - 1],
        anno: selectedAnno, // invia come numero
        regoleAttive, // invia sempre (anche se vuoto)
        dipendentiAttivi: dipendentiAttiviSelezionati, // invia sempre (anche se vuoto)
        neoassunte: neoassunteSelezionate
      };
      
      const response = await apiRequest<any>(`/orari-generati/genera`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });
      handleGenerationResponse(response, 'resto');
  await Promise.all([refetchTurni(), refetchStats(), refetchUtilizzo104()]);
  await fetchDipendenti();
  
  // Notifica al guard che cache deve essere invalidata (trigger check immediato)
  console.log('[GenerazioneOrario] Generazione automatica completata - emetto evento di invalidazione cache');
  window.dispatchEvent(new CustomEvent('coordinatore-ore-lock-cache-invalidated'));
    } catch (err: any) {
      handleApiError(err, 'Errore nella generazione automatica degli orari');
      console.error('Error generating orari:', err);
    } finally {
      setLoading(false);
    }
  };

  const generaWeekend = async () => {
    if (!selectedNegozio || !selectedMese || !selectedAnno) return;
    
    // Chiamata diretta senza popup
    executeGeneraWeekend();
  };

  const executeGeneraWeekend = async () => {
    if (!selectedNegozio || !selectedMese || !selectedAnno) return;
    
    try {
      setLoading(true);
      resetGenerationFeedback();
      
  // Ottieni le regole attive
  const regoleAttive = schedulingRules.filter(rule => rule.enabled).map(rule => rule.id);
      
      // I dipendenti temporanei ora sono gestiti direttamente nella tabella del database

      // Aggiungi i dipendenti attivi alla richiesta (solo quelli selezionati)
      const dipendentiAttiviSelezionati = Object.keys(dipendentiAttivi).filter(nome => dipendentiAttivi[nome]);

      const requestData: Record<string, any> = {
        negozio: selectedNegozio,
        mese: mesi[selectedMese - 1],
        anno: selectedAnno, // numero
        fase: weekendPhase,
        regoleAttive,
        dipendentiAttivi: dipendentiAttiviSelezionati,
        neoassunte: [...dipendenti, ...dipendentiTemporanei]
          .map(d => d.nome)
          .filter(nome => (dipendentiAttivi[nome] ?? true) && isNuovoAssunto(nome))
      };
      
    const rawResponse = await apiRequest<string>('/orari-generati/genera-weekend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });
      handleGenerationResponse(rawResponse, weekendPhase);
  await refetchTurni(); // Ricarica gli orari generati
  await refetchStats(); // Aggiorna statistiche dipendenti
  await refetchUtilizzo104(); // Aggiorna conteggio giorni 104
  await fetchDipendenti();
  
  // Notifica al guard che cache deve essere invalidata (trigger check immediato)
  console.log('[GenerazioneOrario] Generazione weekend completata - emetto evento di invalidazione cache');
  window.dispatchEvent(new CustomEvent('coordinatore-ore-lock-cache-invalidated'));
      
      // Avanza alla fase successiva: domeniche -> sabati -> resto -> domeniche
      if (weekendPhase === 'domeniche') {
        setWeekendPhase('sabati');
      } else if (weekendPhase === 'sabati') {
        setWeekendPhase('resto');
      } else {
        // Dopo "resto", ricomincia dal primo step
        setWeekendPhase('domeniche');
      }
      
    } catch (err: any) {
      handleApiError(err, 'Errore nella generazione weekend');
      console.error('Error generating weekend:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditOrario = async (orarioId: number) => {
    if (isLocked) { setError(LOCK_MESSAGE); return; }
    // TODO: Implementare la logica di modifica
    // Per ora manteniamo la funzionalità di ripristino a baseline
    if (!window.confirm('Sei sicuro di voler ripristinare questo orario a riposo?')) return;
    
    try {
      await apiRequest(`/orari-generati/${orarioId}/baseline`, { method: 'PUT' });
  await refetchTurni();
  await refetchUtilizzo104();
      setSuccess('Orario ripristinato a riposo con successo');
    } catch (err) {
      handleApiError(err, 'Errore nel ripristino dell\'orario a riposo');
    }
  };

  // Render banner lock se periodo bloccato
  const LockBanner = () => (
    isLocked ? (
      <Alert severity="warning" sx={{ mb: 2 }}>
        {LOCK_MESSAGE}
      </Alert>
    ) : null
  );

  // Funzioni per il menu contestuale
  const handleContextMenu = (event: React.MouseEvent, dipendente: string, day: number, dateIso: string, orario: TurnoCreato | null) => {
    event.preventDefault();
    // Blocca il menu contestuale se una generazione automatica è in corso:
    // evita che operazioni manuali si intreccino con generateWeekendSchedule/generateSchedule
    // che stanno leggendo/scrivendo la tabella (race condition → duplicati).
    if (loading) return;
    // La selezione singola non influenza le azioni del menu; aggiornarla qui
    // costringeva la matrice intera a ridisegnarsi ad ogni click destro.
    // Conserviamo invece il reset quando esiste una vera selezione multipla,
    // perché quella determina il range delle operazioni batch.
    try {
      const idx = allGridDays.findIndex(d => d.format('YYYY-MM-DD') === dateIso);
      const hasMultiSelection = rowSelection && rowSelection.startIdx !== rowSelection.endIdx;
      const inside = hasMultiSelection && rowSelection.dipendente === dipendente
        && idx >= Math.min(rowSelection.startIdx, rowSelection.endIdx)
        && idx <= Math.max(rowSelection.startIdx, rowSelection.endIdx);
      if (hasMultiSelection && !inside) {
        setRowSelection({ dipendente, startIdx: idx, endIdx: idx, selecting: false });
      }
    } catch {}

    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      dipendente,
      day,
      dateIso,
      orario
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleOpenAssenzaMenu = () => {
    if (!contextMenu) return;
    setAssenzaMenu({
      mouseX: contextMenu.mouseX,
      mouseY: contextMenu.mouseY,
      dipendente: contextMenu.dipendente,
      day: contextMenu.day,
      dateIso: contextMenu.dateIso
    });
    setContextMenu(null);
  };

  const handleCloseAssenzaMenu = () => setAssenzaMenu(null);
  
  // Sottomenu CAMBIA TURNO (celle con più di un turno)
  const [changeMenu, setChangeMenu] = useState<{
    mouseX: number;
    mouseY: number;
    dipendente: string;
    day: number;
    dateIso: string;
  } | null>(null);
  const handleOpenChangeMenu = () => {
    if (!contextMenu) return;
    setChangeMenu({
      mouseX: contextMenu.mouseX,
      mouseY: contextMenu.mouseY,
      dipendente: contextMenu.dipendente,
      day: contextMenu.day,
      dateIso: contextMenu.dateIso,
    });
    setContextMenu(null);
  };
  const handleCloseChangeMenu = () => setChangeMenu(null);
  
  // Sottomenu ELIMINA TURNO (celle con più di un turno)
  const [deleteMenu, setDeleteMenu] = useState<{
    mouseX: number;
    mouseY: number;
    dipendente: string;
    day: number;
    dateIso: string;
  } | null>(null);
  const handleOpenDeleteMenu = () => {
    if (!contextMenu) return;
    setDeleteMenu({
      mouseX: contextMenu.mouseX,
      mouseY: contextMenu.mouseY,
      dipendente: contextMenu.dipendente,
      day: contextMenu.day,
      dateIso: contextMenu.dateIso,
    });
    setContextMenu(null);
  };
  const handleCloseDeleteMenu = () => setDeleteMenu(null);
  
  // Helper spostato più in alto
  
  const handleChangeTurnoFromMenu = (dipendente: string, dateIso: string, turno: TurnoCreato) => {
    setCustomTurnoData({
      dipendente,
      day: dayjs(dateIso).date(),
      dateIso,
      oraInizio: (turno.oraPrevInizio || '').substring(0, 5),
      oraFine: (turno.oraPrevFine || '').substring(0, 5),
      turnoId: turno.id || null,
      isEsterno: isTurnoEsterno(turno),
    });
    setCustomTurnoDialog(true);
    setChangeMenu(null);
  };
  
  const handleDeleteAllTurniInCella = async (dipendente: string, dateIso: string) => {
    try {
      setLoading(true);
      const turni = getTurniInCella(dipendente, dateIso);
      
      // Separa turni baseline da turni non-baseline
      const turniBaseline = turni.filter((t: TurnoCreato) => isBaselineTurno(t));
      const turniNonBaseline = turni.filter((t: TurnoCreato) => !isBaselineTurno(t));
      
      let operazioniEseguite = 0;
      
      if (turniBaseline.length > 0) {
        // Esiste già almeno un baseline: DELETE tutti i non-baseline + DELETE baseline extra
        // Elimina tutti i turni non-baseline
        if (turniNonBaseline.length > 0) {
          await Promise.allSettled(turniNonBaseline.map((t: TurnoCreato) => apiRequest(`/orari-generati/${t.id}`, { method: 'DELETE' })));
          operazioniEseguite += turniNonBaseline.length;
        }
        // Se ci sono più baseline, elimina tutti tranne il primo
        if (turniBaseline.length > 1) {
          const baselineExtra = turniBaseline.slice(1);
          await Promise.allSettled(baselineExtra.map((t: TurnoCreato) => apiRequest(`/orari-generati/${t.id}`, { method: 'DELETE' })));
          operazioniEseguite += baselineExtra.length;
        }
      } else {
        // Nessun baseline esistente: DELETE tutti tranne uno, UPDATE a baseline l'ultimo
        if (turniNonBaseline.length === 0) {
          setSuccess('Nessun turno da eliminare');
          return;
        }
        
        if (turniNonBaseline.length === 1) {
          // Un solo turno: UPDATE a baseline
          await apiRequest(`/orari-generati/${turniNonBaseline[0].id}/baseline`, { method: 'PUT' });
          operazioniEseguite = 1;
        } else {
          // Più turni: DELETE tutti tranne l'ultimo, UPDATE a baseline l'ultimo
          const turniDaDelete = turniNonBaseline.slice(0, -1);
          const turnoUltimo = turniNonBaseline[turniNonBaseline.length - 1];
          
          await Promise.allSettled(turniDaDelete.map((t: TurnoCreato) => apiRequest(`/orari-generati/${t.id}`, { method: 'DELETE' })));
          await apiRequest(`/orari-generati/${turnoUltimo.id}/baseline`, { method: 'PUT' });
          operazioniEseguite = turniNonBaseline.length;
        }
      }
      
      await fetchOrariGenerati();
      if (operazioniEseguite > 0) {
        setSuccess(`Eliminati ${operazioniEseguite} turni del ${dayjs(dateIso).format('DD/MM/YYYY')} per ${dipendente}`);
      } else {
        setSuccess('Nessun turno da eliminare');
      }
    } catch (e) {
      console.error(e);
      setError("Errore nell'eliminazione dei turni della cella");
    } finally {
      setLoading(false);
      setDeleteMenu(null);
    }
  };
  
  const handleDeleteTurnoById = async (turnoId: number) => {
    try {
      // Individua il turno prima di modificarlo per verificare baseline
      let target: TurnoCreato | undefined;
      let targetDipendente: string | undefined;
      let targetDateIso: string | undefined;
      
      matriceOrari.forEach(r => {
        Object.values(r.orari).forEach(arr => arr.forEach(t => { 
          if (t.id === turnoId) { 
            target = t; 
            targetDipendente = r.dipendente;
            targetDateIso = t.data;
          } 
        }));
      });
      
      // Conta quanti turni ci sono nella cella
      const turniCella = targetDipendente && targetDateIso ? getTurniInCella(targetDipendente, targetDateIso) : [];
      const turniBaseline = turniCella.filter(t => isBaselineTurno(t));
      const turniNonBaseline = turniCella.filter(t => !isBaselineTurno(t));
      
      // Se è un baseline, permetti eliminazione se ci sono altri turni (baseline o non)
      if (target && isBaselineTurno(target)) {
        if (turniBaseline.length > 1 || turniNonBaseline.length > 0) {
          // Ci sono altri turni (baseline extra o turni normali): permetti DELETE
          await apiRequest(`/orari-generati/${turnoId}`, { method: 'DELETE' });
          await fetchOrariGenerati();
          setSuccess('Turno baseline eliminato con successo');
        } else {
          // È l'unico turno nella cella: non può essere eliminato
          setError('Il turno baseline RIPOSO non può essere eliminato (solo cambio)');
        }
        setDeleteMenu(null);
        return;
      }
      
      // Turno non-baseline
      if (targetDipendente && targetDateIso) {
        const haBaseline = turniBaseline.length > 0;
        
        if (turniNonBaseline.length > 1 || haBaseline) {
          // Più di un turno non-baseline OPPURE esiste già un baseline: DELETE
          await apiRequest(`/orari-generati/${turnoId}`, { method: 'DELETE' });
          await fetchOrariGenerati();
          setSuccess('Turno eliminato con successo');
        } else {
          // È l'ultimo turno non-baseline e non c'è baseline: UPDATE a baseline
          await apiRequest(`/orari-generati/${turnoId}/baseline`, { method: 'PUT' });
          await fetchOrariGenerati();
          setSuccess('Turno ripristinato a riposo con successo');
        }
      } else {
        // Fallback: UPDATE a baseline se non riusciamo a determinare il contesto
        await apiRequest(`/orari-generati/${turnoId}/baseline`, { method: 'PUT' });
        await fetchOrariGenerati();
        setSuccess('Turno ripristinato a riposo con successo');
      }
    } catch (err) {
      handleApiError(err, "Errore nell'eliminazione del turno");
      console.error('Error deleting turno:', err);
    }
    setDeleteMenu(null);
  };

  // Swap helper: scambia TUTTI i turni tra due dipendenti per una data ISO
  // Usa UPDATE invece di DELETE+INSERT per generare eventi UPD nell'outbox
  const swapAllTurnsForDateBetween = useCallback(async (dip1: string, dip2: string, dateIso: string) => {
    const d = dayjs(dateIso);
    if (isLockedForUser(d.year(), d.month() + 1)) {
      throw new Error(LOCK_MESSAGE);
    }
    const turni1 = getTurniInCella(dip1, dateIso);
    const turni2 = getTurniInCella(dip2, dateIso);
    if (turni1.some(t => t.blocco === 1) || turni2.some(t => t.blocco === 1)) {
      throw new Error('Non è possibile scambiare turni bloccati');
    }

    // Filtra turni non-baseline per ciascun dipendente
    const turni1NonBaseline = turni1.filter(t => !isBaselineTurno(t));
    const turni2NonBaseline = turni2.filter(t => !isBaselineTurno(t));
    const turni1Baseline = turni1.filter(t => isBaselineTurno(t));
    const turni2Baseline = turni2.filter(t => isBaselineTurno(t));
    const giornoSettimana = d.format('dddd');

    // Caso semplice: entrambi hanno esattamente 1 turno (baseline o non)
    // Facciamo UPDATE incrociato
    if (turni1.length === 1 && turni2.length === 1) {
      const t1 = turni1[0];
      const t2 = turni2[0];
      const isT1Baseline = isBaselineTurno(t1);
      const isT2Baseline = isBaselineTurno(t2);

      // UPDATE turno1 con dati di turno2 (o baseline se t2 era baseline)
      if (isT2Baseline) {
        await apiRequest(`/orari-generati/${t1.id}/baseline`, { method: 'PUT' });
      } else {
        const payload1: any = {
          neg: selectedNegozio, // Usa sempre il negozio corrente (supporta temporanei)
          descrizione: t1.descrizione ?? dip1,
          data: dateIso,
          giornoSettimana,
          oraPrevInizio: t2.oraPrevInizio ?? null,
          oraPrevFine: t2.oraPrevFine ?? null,
          oraConsInizio: t2.oraConsInizio ?? null,
          oraConsFine: t2.oraConsFine ?? null,
          turno: t2.turno,
          codiceTipoOrario: t2.codiceTipoOrario ?? null,
          utenteModifica: userInfo?.username || 'Utente',
        };
        await apiRequest(`/orari-generati/${t1.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload1)
        });
      }

      // UPDATE turno2 con dati di turno1 (o baseline se t1 era baseline)
      if (isT1Baseline) {
        await apiRequest(`/orari-generati/${t2.id}/baseline`, { method: 'PUT' });
      } else {
        const payload2: any = {
          neg: selectedNegozio, // Usa sempre il negozio corrente (supporta temporanei)
          descrizione: t2.descrizione ?? dip2,
          data: dateIso,
          giornoSettimana,
          oraPrevInizio: t1.oraPrevInizio ?? null,
          oraPrevFine: t1.oraPrevFine ?? null,
          oraConsInizio: t1.oraConsInizio ?? null,
          oraConsFine: t1.oraConsFine ?? null,
          turno: t1.turno,
          codiceTipoOrario: t1.codiceTipoOrario ?? null,
          utenteModifica: userInfo?.username || 'Utente',
        };
        await apiRequest(`/orari-generati/${t2.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload2)
        });
      }
    } else {
      // Caso complesso: turni multipli - usa la logica precedente DELETE+INSERT
      // ma ottimizzata: mantieni almeno 1 record per lato e fai UPDATE
      
      // Per ogni lato: tieni 1 turno per UPDATE, elimina gli altri
      const keepT1 = turni1[0]; // primo turno di dip1 da tenere
      const keepT2 = turni2[0]; // primo turno di dip2 da tenere
      const deleteT1 = turni1.slice(1); // turni extra di dip1 da eliminare
      const deleteT2 = turni2.slice(1); // turni extra di dip2 da eliminare

      // Elimina turni extra
      const delIds = [...deleteT1, ...deleteT2].map(t => t.id).filter(id => id != null) as number[];
      if (delIds.length) {
        await Promise.allSettled(delIds.map(id => apiRequest(`/orari-generati/${id}`, { method: 'DELETE' })));
      }

      // UPDATE keepT1 con dati del primo turno non-baseline di dip2 (o baseline se tutti baseline)
      const sourceForT1 = turni2NonBaseline[0] || turni2Baseline[0];
      if (sourceForT1 && isBaselineTurno(sourceForT1)) {
        await apiRequest(`/orari-generati/${keepT1.id}/baseline`, { method: 'PUT' });
      } else if (sourceForT1) {
        const payload1: any = {
          neg: selectedNegozio, // Usa sempre il negozio corrente (supporta temporanei)
          descrizione: keepT1.descrizione ?? dip1,
          data: dateIso,
          giornoSettimana,
          oraPrevInizio: sourceForT1.oraPrevInizio ?? null,
          oraPrevFine: sourceForT1.oraPrevFine ?? null,
          oraConsInizio: sourceForT1.oraConsInizio ?? null,
          oraConsFine: sourceForT1.oraConsFine ?? null,
          turno: sourceForT1.turno,
          codiceTipoOrario: sourceForT1.codiceTipoOrario ?? null,
          utenteModifica: userInfo?.username || 'Utente',
        };
        await apiRequest(`/orari-generati/${keepT1.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload1)
        });
      }

      // UPDATE keepT2 con dati del primo turno non-baseline di dip1 (o baseline se tutti baseline)
      const sourceForT2 = turni1NonBaseline[0] || turni1Baseline[0];
      if (sourceForT2 && isBaselineTurno(sourceForT2)) {
        await apiRequest(`/orari-generati/${keepT2.id}/baseline`, { method: 'PUT' });
      } else if (sourceForT2) {
        const payload2: any = {
          neg: selectedNegozio, // Usa sempre il negozio corrente (supporta temporanei)
          descrizione: keepT2.descrizione ?? dip2,
          data: dateIso,
          giornoSettimana,
          oraPrevInizio: sourceForT2.oraPrevInizio ?? null,
          oraPrevFine: sourceForT2.oraPrevFine ?? null,
          oraConsInizio: sourceForT2.oraConsInizio ?? null,
          oraConsFine: sourceForT2.oraConsFine ?? null,
          turno: sourceForT2.turno,
          codiceTipoOrario: sourceForT2.codiceTipoOrario ?? null,
          utenteModifica: userInfo?.username || 'Utente',
        };
        await apiRequest(`/orari-generati/${keepT2.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload2)
        });
      }

      // Se c'erano turni extra non-baseline da una parte, creali sull'altra
      // (es. dip1 aveva 2 turni, dip2 ne aveva 1: crea il secondo turno di dip1 su dip2)
      const extraT1 = turni1NonBaseline.slice(1); // turni extra non-baseline di dip1
      const extraT2 = turni2NonBaseline.slice(1); // turni extra non-baseline di dip2
      const createPayloads: any[] = [];
      
      for (const t of extraT1) {
        createPayloads.push({
          neg: selectedNegozio,
          descrizione: dip2,
          data: dateIso,
          oraPrevInizio: t.oraPrevInizio ?? null,
          oraPrevFine: t.oraPrevFine ?? null,
          turno: t.turno,
          codiceTipoOrario: t.codiceTipoOrario ?? null,
          giornoSettimana,
          utenteModifica: userInfo?.username || 'Utente',
        });
      }
      for (const t of extraT2) {
        createPayloads.push({
          neg: selectedNegozio,
          descrizione: dip1,
          data: dateIso,
          oraPrevInizio: t.oraPrevInizio ?? null,
          oraPrevFine: t.oraPrevFine ?? null,
          turno: t.turno,
          codiceTipoOrario: t.codiceTipoOrario ?? null,
          giornoSettimana,
          utenteModifica: userInfo?.username || 'Utente',
        });
      }
      
      if (createPayloads.length) {
        await Promise.allSettled(createPayloads.map(p => apiRequest('/orari-generati', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(p) })));
      }
    }

    await fetchOrariGenerati();
  }, [getTurniInCella, selectedNegozio, userInfo, fetchOrariGenerati, isLockedForUser]);
  
  // Apertura/chiusura sottomenu "Aggiungi turno" su cella con turno già presente
  const handleOpenAddMenu = () => {
    if (!contextMenu) return;
    setAddMenu({
      mouseX: contextMenu.mouseX,
      mouseY: contextMenu.mouseY,
      dipendente: contextMenu.dipendente,
      day: contextMenu.day,
      dateIso: contextMenu.dateIso
    });
    setContextMenu(null);
  };
  const handleCloseAddMenu = () => setAddMenu(null);
  
  // Copia/Incolla turni di cella
  const [copiedTurns, setCopiedTurns] = useState<{ turni: TurnoCreato[] } | null>(null);
  const handleCopyFromCurrentCell = () => {
    if (!contextMenu) return;
    const list = getTurniInCella(contextMenu.dipendente, contextMenu.dateIso);
    if (!list.length) { setContextMenu(null); return; }
    setCopiedTurns({ turni: list });
    setSuccess('Turno copiato');
    setContextMenu(null);
  };
  
  const handlePasteToCurrentCell = async () => {
    if (!contextMenu || !copiedTurns) return;
    const { dipendente, dateIso } = contextMenu;
    const targetDate = dayjs(dateIso);
    if (isLockedForUser(targetDate.year(), targetDate.month() + 1)) { 
      setError(LOCK_MESSAGE); 
      setContextMenu(null); 
      return; 
    }
    
    // Verifica se la cella ha turni non-baseline
    const turniCella = getTurniInCella(dipendente, dateIso);
    const turniNonBaseline = turniCella.filter(t => !isBaselineTurno(t));
    
    if (turniNonBaseline.length > 0) {
      // Cella piena: chiedi conferma
      setConfirmPasteDialog({ open: true, dipendente, dateIso });
      setContextMenu(null);
    } else {
      // Cella vuota o solo baseline: incolla direttamente
      setContextMenu(null);
      await executeActualPaste(dipendente, dateIso);
    }
  };
  
  const executeActualPaste = async (dipendente: string, dateIso: string) => {
    if (!copiedTurns) return;
    const targetDate = dayjs(dateIso);
    
    try {
      setLoading(true);
      
      const turniCella = getTurniInCella(dipendente, dateIso);
      const turniNonBaseline = turniCella.filter(t => !isBaselineTurno(t));
      const baselineTurno = turniCella.find(t => isBaselineTurno(t) && !isTurnoEsterno(t));
      
      const giornoSettimana = targetDate.format('dddd');
      let updated = 0;
      
      // Se la cella ha turni non-baseline, prima li eliminiamo tutti
      if (turniNonBaseline.length > 0) {
        for (const t of turniNonBaseline) {
          await apiRequest(`/orari-generati/${t.id}`, { method: 'DELETE' });
        }
      }
      
      // Ora incolliamo i turni copiati
      let isFirstTurno = true;
      for (const t of copiedTurns.turni) {
        const payload: any = {
          neg: selectedNegozio,
          descrizione: dipendente,
          data: dateIso,
          oraPrevInizio: t.oraPrevInizio ?? null,
          oraPrevFine: t.oraPrevFine ?? null,
          turno: t.turno,
          giornoSettimana,
          utenteModifica: userInfo?.username || 'Utente',
        };
        if ((t.turno || '').toUpperCase() === 'ASSENZA' && t.codiceTipoOrario) {
          payload.codiceTipoOrario = t.codiceTipoOrario;
        }
        
        // Se è il primo turno e c'è un baseline, aggiorna il baseline
        if (isFirstTurno && baselineTurno) {
          await apiRequest(`/orari-generati/${baselineTurno.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          isFirstTurno = false;
        } else {
          // Altrimenti crea nuovo turno
          await apiRequest('/orari-generati', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
        }
        updated++;
      }
      
      await fetchOrariGenerati();
      setSuccess(`Incollati ${updated} turno/i`);
    } catch (e) {
      console.error('Errore incolla turni:', e);
      setError('Errore durante incolla turni');
    } finally {
      setLoading(false);
    }
  };
  
  // Apertura/chiusura menu per giorno (Blocco Verticale)
  const handleOpenDayMenu = (event: React.MouseEvent, day: number, dateIso: string) => {
    event.preventDefault();
    if (loading) return;
    setDayMenu({ mouseX: event.clientX + 2, mouseY: event.clientY - 6, day, dateIso });
  };
  const handleCloseDayMenu = () => setDayMenu(null);

  // Apertura/chiusura menu per dipendente (Blocco Orizzontale)
  const handleOpenDipMenu = (event: React.MouseEvent, dipendente: string) => {
    event.preventDefault();
    if (loading) return;
    setDipMenu({ mouseX: event.clientX + 2, mouseY: event.clientY - 6, dipendente });
  };
  const handleCloseDipMenu = () => setDipMenu(null);

  // Funzione per salvare assenze con orari previsti obbligatori (CORSI AI DIPENDENTI, SMART WORKING)
  const handleOrariPrevistiSave = async () => {
    // Validazione: orari previsti obbligatori
    if (!orariPrevistiData.oraPrevInizio || !orariPrevistiData.oraPrevFine) {
      setError('Inserire sia Ora Prevista Inizio che Ora Prevista Fine');
      return;
    }
    if (!assenzaMenu || !selectedNegozio || !selectedMese || !selectedAnno) {
      setOrariPrevistiDialog(false);
      setOrariPrevistiData({ codice: '', oraPrevInizio: '', oraPrevFine: '' });
      setAssenzaMenu(null);
      return;
    }

    const { dipendente, dateIso } = assenzaMenu;
    const codice = orariPrevistiData.codice;

    // Chiudi i dialog
    setOrariPrevistiDialog(false);
    setOrariPrevistiData({ codice: '', oraPrevInizio: '', oraPrevFine: '' });
    setAssenzaMenu(null);

    try {
      setLoading(true);

      // PRIMO STEP: Genera baseline RIPOSO per tutti i giorni/dipendenti del mese
      try {
        const meseNome = [
          'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
          'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
        ][selectedMese - 1];
        
        await apiRequest(
          `/orari-generati/genera-baseline?negozio=${selectedNegozio}&mese=${meseNome}&anno=${selectedAnno}`,
          { method: 'POST' }
        );
      } catch (err) {
        console.warn('Errore nella generazione baseline (non bloccante):', err);
      }

      // Calcola le celle target: se esiste una selezione per lo stesso dipendente, usa l'intervallo; altrimenti solo la cella cliccata
      const singleIdx = allGridDays.findIndex(d => d.format('YYYY-MM-DD') === dateIso);
      const useSelection = rowSelection && rowSelection.dipendente === dipendente && singleIdx >= Math.min(rowSelection.startIdx, rowSelection.endIdx) && singleIdx <= Math.max(rowSelection.startIdx, rowSelection.endIdx);
      const startIdx = useSelection ? Math.min(rowSelection!.startIdx, rowSelection!.endIdx) : singleIdx;
      const endIdx = useSelection ? Math.max(rowSelection!.startIdx, rowSelection!.endIdx) : singleIdx;

      // Itera sulle date dell'intervallo e applica l'assenza dove non bloccato
      let skippedLocked = 0;
      for (let i = startIdx; i <= endIdx; i++) {
        const d = allGridDays[i];
        if (!d) continue;
        const lockForDay = isLockedForUser(d.year(), d.month() + 1);
        if (lockForDay) { skippedLocked++; continue; }
        const day = d.date();
        const dateIsoLoop = d.format('YYYY-MM-DD');

        // Identifica turni esistenti
        const riga = matriceOrari.find(r => r.dipendente === dipendente);
        const isPrevMonthDate = d.isBefore(dayjs().year(selectedAnno).month(selectedMese - 1).date(1), 'day');
        const orariGiorno = (d.month() === (selectedMese - 1) && d.year() === selectedAnno)
          ? (riga?.orari[day] || [])
          : (isPrevMonthDate ? (riga?.orariPrev?.[day] || []) : (riga?.orariNext?.[day] || []));
        
        const modifiableTurni = orariGiorno.filter(t => t.blocco !== 1);

        // Prepara payload con orari previsti forniti dall'utente
        const giornoSettimana = d.format('dddd');
        const payload: any = {
          neg: selectedNegozio,
          descrizione: dipendente,
          data: dateIsoLoop,
          oraPrevInizio: orariPrevistiData.oraPrevInizio,
          oraPrevFine: orariPrevistiData.oraPrevFine,
          turno: 'ASSENZA',
          codiceTipoOrario: codice,
          giornoSettimana,
          utenteModifica: userInfo?.username || 'Utente',
          // Per CORSI AI DIPENDENTI e SMART WORKING: oraConsInizio e oraConsFine restano null (da inserire manualmente)
          oraConsInizio: null,
          oraConsFine: null
        };

        if (modifiableTurni.length > 0) {
          // UPDATE: usa il primo turno modificabile
          const target = modifiableTurni[0];
          // DELETE degli altri turni in eccesso
          for (let k = 1; k < modifiableTurni.length; k++) {
            try { await apiRequest(`/orari-generati/${modifiableTurni[k].id}`, { method: 'DELETE' }); } catch(e) {}
          }
          await apiRequest<TurnoCreato>(`/orari-generati/${target.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
        } else {
          // INSERT: nessun turno modificabile esistente
          await apiRequest<TurnoCreato>('/orari-generati', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
        }
      }

      await fetchOrariGenerati();
      
      // Notifica al guard che cache deve essere invalidata (trigger check immediato)
      console.log('[GenerazioneOrario] Orari previsti salvati - emetto evento di invalidazione cache');
      window.dispatchEvent(new CustomEvent('coordinatore-ore-lock-cache-invalidated'));
      
      const totalApplied = (endIdx - startIdx + 1) - skippedLocked;
      if (useSelection && totalApplied > 1) {
        setSuccess(`${codice} impostato per ${dipendente} su ${totalApplied} giorni${skippedLocked > 0 ? ' (saltati ' + skippedLocked + ' giorni bloccati)' : ''}`);
      } else {
        setSuccess(`${codice} impostato per ${dipendente} il ${dayjs(dateIso).format('DD/MM/YYYY')}`);
      }
    } catch (err) {
      console.error('Errore impostando assenza con orari previsti:', err);
      handleApiError(err, 'Errore nell\'impostare l\'assenza');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAssenza = async (codice: string) => {
    if (!assenzaMenu || !selectedNegozio || !selectedMese || !selectedAnno) { setAssenzaMenu(null); return; }
    
    const { dipendente, dateIso } = assenzaMenu;
    setAssenzaMenu(null);

    // Se il mese selezionato è bloccato, annulla
    // Il lock ora viene valutato per ogni giorno selezionato (può includere giorni fuori mese)
    try {
      setLoading(true);

      // PRIMO STEP: Genera baseline RIPOSO per tutti i giorni/dipendenti del mese
      try {
        const meseNome = [
          'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
          'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
        ][selectedMese - 1];
        
        await apiRequest(
          `/orari-generati/genera-baseline?negozio=${selectedNegozio}&mese=${meseNome}&anno=${selectedAnno}`,
          { method: 'POST' }
        );
      } catch (err) {
        console.warn('Errore nella generazione baseline (non bloccante):', err);
      }

      // Calcola le celle target: se esiste una selezione per lo stesso dipendente, usa l'intervallo; altrimenti solo la cella cliccata
      const singleIdx = allGridDays.findIndex(d => d.format('YYYY-MM-DD') === dateIso);
      const useSelection = rowSelection && rowSelection.dipendente === dipendente && singleIdx >= Math.min(rowSelection.startIdx, rowSelection.endIdx) && singleIdx <= Math.max(rowSelection.startIdx, rowSelection.endIdx);
      const startIdx = useSelection ? Math.min(rowSelection!.startIdx, rowSelection!.endIdx) : singleIdx;
      const endIdx = useSelection ? Math.max(rowSelection!.startIdx, rowSelection!.endIdx) : singleIdx;

      // Helper: converte ore (numero) in stringa HH:MM
      const hoursToHHMM = (h: number) => {
        if (!h || Number.isNaN(Number(h))) return '00:00';
        const totalMinutes = Math.round(Number(h) * 60);
        const hh = Math.floor(totalMinutes / 60);
        const mm = totalMinutes % 60;
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${pad(hh)}:${pad(mm)}`;
      };

  // Nuova logica: per OGNI assenza impostiamo Prev uguale a Cons in base alle ore contrattuali del dipendente per quel giorno.

      // Itera sulle date dell'intervallo (anche fuori dal mese corrente visibile) e applica la creazione assenza dove non bloccato
      let skippedLocked = 0;
      let skippedFestivita = 0;
      for (let i = startIdx; i <= endIdx; i++) {
  const d = allGridDays[i];
        if (!d) continue;
        const lockForDay = isLockedForUser(d.year(), d.month() + 1);
        if (lockForDay) { skippedLocked++; continue; }
        const day = d.date();
        const dateIso = d.format('YYYY-MM-DD');
        if (isFestivita(dateIso) && !isCodiceConsentitoInFestivita(codice)) { skippedFestivita++; continue; }

        // Controllo ROL: se ore contrattuali = 0, blocca inserimento
        if (codice === 'ROL') {
          const dipObj = dipendenti.find(dp => dp.nome === dipendente);
          if (dipObj) {
            const dayOfWeek = d.day(); // 0=Dom, 1=Lun...
            let hours = 0;
            switch (dayOfWeek) {
              case 1: hours = dipObj.lunedi; break;
              case 2: hours = dipObj.martedi; break;
              case 3: hours = dipObj.mercoledi; break;
              case 4: hours = dipObj.giovedi; break;
              case 5: hours = dipObj.venerdi; break;
              case 6: hours = dipObj.sabato; break;
              case 0: hours = dipObj.domenica; break;
            }
            if (hours <= 0) {
              alert(`Attenzione: non puoi inserire un ROL il ${dateIso} perché non sono previste ore contrattuali.`);
              continue;
            }
          }
        }

        // 1) Identifica turni esistenti
        const riga = matriceOrari.find(r => r.dipendente === dipendente);
        const isPrevMonthDate = d.isBefore(dayjs().year(selectedAnno).month(selectedMese - 1).date(1), 'day');
        const orariGiorno = (d.month() === (selectedMese - 1) && d.year() === selectedAnno)
          ? (riga?.orari[day] || [])
          : (isPrevMonthDate ? (riga?.orariPrev?.[day] || []) : (riga?.orariNext?.[day] || []));
        
        const modifiableTurni = orariGiorno.filter(t => t.blocco !== 1);

        // 2) Prepara payload per ASSENZA
        const giornoSettimana = d.format('dddd');
        let oraConsInizio: string | null = null;
        let oraConsFine: string | null = null;
        let oraPrevInizioPayload: string | null = null;
        let oraPrevFinePayload: string | null = null;

        // trova il dipendente anagrafico per estrarre le ore del giorno
        const dipInfo = dipendenti.find(dd => dd.nome === dipendente) || dipendenti.find(dd => `${dd.nome}` === dipendente);
        const dayKey = d.format('dddd')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
        let hoursForDay: number = 0;
        try {
          if (dipInfo) {
            // @ts-ignore
            hoursForDay = Number(dipInfo[dayKey]) || 0;
          }
        } catch (e) { hoursForDay = 0; }
        // consuntivo: start at midnight, end at midnight + hoursForDay (se ore contrattuali=0 manteniamo 00:00-00:00)
        oraConsInizio = '00:00';
        oraConsFine = hoursForDay > 0 ? hoursToHHMM(hoursForDay) : '00:00';
        // Prev uguale a Cons (requisito)
        oraPrevInizioPayload = oraConsInizio;
        oraPrevFinePayload = oraConsFine;

        const payload: any = {
          neg: selectedNegozio,
          descrizione: dipendente,
          data: dateIso,
          oraPrevInizio: oraPrevInizioPayload,
          oraPrevFine: oraPrevFinePayload,
          turno: 'ASSENZA',
          codiceTipoOrario: codice,
          giornoSettimana,
          utenteModifica: userInfo?.username || 'Utente',
          oraConsInizio: oraConsInizio,
          oraConsFine: oraConsFine
        };

        if (modifiableTurni.length > 0) {
            // UPDATE: usa il primo turno modificabile
            const target = modifiableTurni[0];
            // DELETE degli altri turni in eccesso (es. split shift) - genera eventi DEL
            for (let k = 1; k < modifiableTurni.length; k++) {
                 try { await apiRequest(`/orari-generati/${modifiableTurni[k].id}`, { method: 'DELETE' }); } catch(e) {}
            }
            // Aggiorna il target con l'assenza
            await apiRequest<TurnoCreato>(`/orari-generati/${target.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
        } else {
            // INSERT: nessun turno modificabile esistente (es. baseline o vuoto)
            await apiRequest<TurnoCreato>('/orari-generati', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
        }
      }

      await fetchOrariGenerati();
      
      // Notifica al guard che cache deve essere invalidata (trigger check immediato)
      console.log('[GenerazioneOrario] Assenza impostata - emetto evento di invalidazione cache');
      window.dispatchEvent(new CustomEvent('coordinatore-ore-lock-cache-invalidated'));
      
      if (useSelection) {
        const totalApplied = (endIdx - startIdx + 1) - skippedLocked - skippedFestivita;
  // Fix template literal: avoid nested backticks causing TS parse errors
  setSuccess(`Assenza (${codice}) impostata per ${dipendente} su ${totalApplied} giorni${skippedLocked > 0 ? ' (saltati ' + skippedLocked + ' giorni bloccati)' : ''}${skippedFestivita > 0 ? ' (saltate ' + skippedFestivita + ' festività)' : ''}`);
      } else {
        setSuccess(`Assenza (${codice}) impostata per ${dipendente} il ${dayjs(assenzaMenu.dateIso).format('DD/MM/YYYY')}`);
      }
    } catch (err) {
      console.error('Errore impostando assenza:', err);
      handleApiError(err, 'Errore nell\'impostare l\'assenza');
    } finally {
      setAssenzaMenu(null);
      setLoading(false);
    }
  };

  // Nuova funzione: elimina tutti i turni (qualsiasi tipo) per i giorni selezionati della riga corrente.
  const handleDeleteSelectedTurni = async () => {
    handleCloseContextMenu();
    if (!rowSelection) return;
    const { dipendente, startIdx, endIdx } = rowSelection;
    if (!window.confirm(`Eliminare tutti i turni per ${dipendente} nei giorni selezionati?`)) return;
    try {
      setLoading(true);
      let deleted = 0; let skippedLocked = 0;
      for (let i = Math.min(startIdx,endIdx); i <= Math.max(startIdx,endIdx); i++) {
  const d = allGridDays[i];
        if (!d) continue;
        if (isLockedForUser(d.year(), d.month()+1)) { skippedLocked++; continue; }
        // individua la riga
        const riga = matriceOrari.find(r => r.dipendente === dipendente);
        if (!riga) continue;
        const dayNum = d.date();
        const isCurrMonth = d.month() === (selectedMese-1) && d.year() === selectedAnno;
        const isPrevMonth = d.isBefore(dayjs().year(selectedAnno).month(selectedMese-1).date(1), 'day');
        const orariGiorno = isCurrMonth ? (riga.orari[dayNum]||[]) : (isPrevMonth ? (riga.orariPrev?.[dayNum]||[]) : (riga.orariNext?.[dayNum]||[]));
        for (const t of orariGiorno) {
          if (isBaselineTurno(t)) continue; // non toccare baseline
          if (t.blocco === 1) { skippedLocked++; continue; } // non toccare turni bloccati
          try { await apiRequest(`/orari-generati/${t.id}/baseline`, { method: 'PUT' }); deleted++; } catch(e){ console.warn('Reset baseline fallito', t.id, e); }
        }
      }
      await fetchOrariGenerati();
  // Fix template literal: avoid nested backticks causing TS parse errors
  setSuccess(`Ripristinati a riposo ${deleted} turni${skippedLocked > 0 ? ' (saltati ' + skippedLocked + ' bloccati)' : ''}`);
    } catch (e) {
      console.error(e);
      setError('Errore nell\'eliminazione dei turni selezionati');
    } finally {
      setLoading(false);
    }
  };

  // Scambio massivo via drag & drop multi-selezione
  const executeBulkSwap = async (source: string, target: string, startIdx: number, endIdx: number) => {
    try {
      setLoading(true);
      let swapped = 0; let skippedLocked = 0; let errors = 0;
      for (let i = startIdx; i <= endIdx; i++) {
  const d = allGridDays[i];
        if (!d) continue;
        if (isLockedForUser(d.year(), d.month() + 1)) { skippedLocked++; continue; }
        try {
          await swapAllTurnsForDateBetween(source, target, d.format('YYYY-MM-DD'));
          swapped++;
        } catch (e) { console.warn('Errore swap giorno', d.format('YYYY-MM-DD'), e); errors++; }
      }
      setSuccess(`Scambio multiplo: ${swapped} giorni scambiati${skippedLocked?`, ${skippedLocked} bloccati`:''}${errors?`, errori: ${errors}`:''}`);
    } catch (e) {
      console.error(e);
      handleApiError(e, 'Errore nello scambio multiplo');
    } finally {
      setLoading(false);
    }
  };

  // Aggiunge un nuovo turno (senza aggiornare quello esistente) sulla cella selezionata
  const handleAddTurno = async (tipoTurno: string) => {
    if (!addMenu) return;
    const { dipendente, dateIso } = addMenu;
    const singleIdx = allGridDays.findIndex(d => d.format('YYYY-MM-DD') === dateIso);
    const useSelection = rowSelection && rowSelection.dipendente === dipendente && singleIdx >= Math.min(rowSelection.startIdx, rowSelection.endIdx) && singleIdx <= Math.max(rowSelection.startIdx, rowSelection.endIdx);
    const startIdx = useSelection ? Math.min(rowSelection!.startIdx, rowSelection!.endIdx) : singleIdx;
    const endIdx = useSelection ? Math.max(rowSelection!.startIdx, rowSelection!.endIdx) : singleIdx;
    if (isLockedForUser(selectedAnno, selectedMese)) { setError(LOCK_MESSAGE); handleCloseAddMenu(); return; }

    // Chiudi subito il menu
    handleCloseAddMenu();

    let oraPrevInizio = '' as string | null;
    let oraPrevFine = '' as string | null;

    if (tipoTurno !== 'Riposo' && tipoTurno !== 'Personalizzato') {
      const standard = await getStandardTurnoOrari(tipoTurno, dateIso);
      oraPrevInizio = standard.oraInizio || null;
      oraPrevFine = standard.oraFine || null;
    } else if (tipoTurno === 'Riposo') {
      oraPrevInizio = null;
      oraPrevFine = null;
    } else if (tipoTurno === 'Personalizzato') {
      setCustomTurnoData({
        dipendente,
        day: allGridDays[singleIdx]?.date() || 0,
        dateIso: dateIso,
        oraInizio: '',
        oraFine: '',
        turnoId: null,
        isEsterno: false,
      });
      setCustomTurnoDialog(true);
      return;
    }

    try {
      // PRIMO STEP: Genera baseline RIPOSO per tutti i giorni/dipendenti del mese
      // (solo se non già presenti) PRIMA di iniziare ad aggiungere turni
      try {
        await ensureBaselineForSelectedPeriod();
      } catch (err) {
        console.warn('Errore nella generazione baseline (non bloccante):', err);
        // Non blocchiamo l'operazione se la generazione baseline fallisce
      }

      let createdCount = 0;
      for (let i = startIdx; i <= endIdx; i++) {
        const d = allGridDays[i];
        if (!d) continue;
        if (d.month() !== (selectedMese - 1) || d.year() !== selectedAnno) continue; // solo mese corrente
        const data = d.format('YYYY-MM-DD');
        const turnoData = {
          neg: selectedNegozio,
          descrizione: dipendente,
          data,
          oraPrevInizio: oraPrevInizio,
          oraPrevFine: oraPrevFine,
          turno: tipoTurno,
          giornoSettimana: d.format('dddd'),
          utenteModifica: userInfo?.username || 'Utente',
        };

        // Cerca se esiste un turno baseline nella cella
        const turniCella = getTurniInCella(dipendente, data);
        const baselineTurno = turniCella.find(t => isBaselineTurno(t));

        if (baselineTurno) {
          // UPDATE: aggiorna il turno baseline esistente
          await apiRequest<any>(`/orari-generati/${baselineTurno.id}`, {
            method: 'PUT',
            body: JSON.stringify(turnoData),
            headers: { 'Content-Type': 'application/json' },
          });
        } else {
          // INSERT: cella completamente vuota, crea nuovo turno
          await apiRequest<any>('/orari-generati', {
            method: 'POST',
            body: JSON.stringify(turnoData),
            headers: { 'Content-Type': 'application/json' },
          });
        }
        createdCount++;
      }
      setSuccess(useSelection ? `Turno creato su ${createdCount} giorni` : 'Turno creato con successo');
      refreshAfterTurnWrite();
    } catch (err) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setSuccess(null);
      handleApiError(err, 'Errore nella creazione del turno');
      console.error('Error creating turno:', err);
    }
  };

  const handleDeleteTurno = async () => {
    if (!contextMenu?.orario) return;
    
    const { dipendente, dateIso, orario } = contextMenu;
    const turnoId = orario.id;
    
    // Conta quanti turni ci sono nella cella
    const turniCella = getTurniInCella(dipendente, dateIso);
    const turniBaseline = turniCella.filter(t => isBaselineTurno(t));
    const turniNonBaseline = turniCella.filter(t => !isBaselineTurno(t));
    
    // Se è un baseline, permetti eliminazione se ci sono altri turni (baseline o non)
    if (isBaselineTurno(orario)) {
      if (turniBaseline.length > 1 || turniNonBaseline.length > 0) {
        // Ci sono altri turni: permetti DELETE del baseline
        handleCloseContextMenu();
        try {
          await apiRequest(`/orari-generati/${turnoId}`, { method: 'DELETE' });
          await fetchOrariGenerati();
          setSuccess('Turno baseline eliminato con successo');
          console.log('[GenerazioneOrario] Turno eliminato - emetto evento di invalidazione cache');
          window.dispatchEvent(new CustomEvent('coordinatore-ore-lock-cache-invalidated'));
        } catch (err) {
          handleApiError(err, 'Errore nell\'eliminazione del turno baseline');
          console.error('Error deleting baseline:', err);
        }
      } else {
        // È l'unico turno nella cella: non può essere eliminato
        setError('Il turno baseline RIPOSO non può essere eliminato (solo cambio)');
        handleCloseContextMenu();
      }
      return;
    }
    
    // Chiudi subito il menu per evitare doppi click
    handleCloseContextMenu();

    try {
      const haBaseline = turniBaseline.length > 0;
      
      if (turniNonBaseline.length > 1 || haBaseline) {
        // Più di un turno non-baseline OPPURE esiste già un baseline: DELETE
        await apiRequest(`/orari-generati/${turnoId}`, { method: 'DELETE' });
        await fetchOrariGenerati();
        setSuccess('Turno eliminato con successo');
        console.log('[GenerazioneOrario] Turno eliminato - emetto evento di invalidazione cache');
        window.dispatchEvent(new CustomEvent('coordinatore-ore-lock-cache-invalidated'));
      } else {
        // È l'ultimo turno non-baseline e non c'è baseline: UPDATE a baseline
        await apiRequest(`/orari-generati/${turnoId}/baseline`, { method: 'PUT' });
        await fetchOrariGenerati();
        setSuccess('Turno ripristinato a riposo');
        console.log('[GenerazioneOrario] Turno ripristinato a baseline - emetto evento di invalidazione cache');
        window.dispatchEvent(new CustomEvent('coordinatore-ore-lock-cache-invalidated'));
      }
    } catch (err) {
      handleApiError(err, 'Errore nell\'eliminazione del turno');
      console.error('Error deleting turno:', err);
    }
  };

  const handleAddOrUpdateTurno = async (tipoTurno: string) => {
    if (!contextMenu) return;
    const { dipendente, day, dateIso, orario } = contextMenu;
    const dC = dayjs(dateIso);
    if (isLockedForUser(dC.year(), dC.month() + 1)) { setError(LOCK_MESSAGE); handleCloseContextMenu(); return; }
    if (isFestivita(dateIso) && tipoTurno === 'Riposo') {
      setError('In un giorno festivo non è possibile creare RIPOSO.');
      handleCloseContextMenu();
      return;
    }

    // Chiudi subito il menu
    handleCloseContextMenu();

    let oraPrevInizio = '' as string | null;
    let oraPrevFine = '' as string | null;

    // If not Riposo/Personalizzato, try to fetch standard hours for this store/day/tipo from backend
    if (tipoTurno !== 'Riposo' && tipoTurno !== 'Personalizzato') {
      try {
        const dayName = dayjs(dateIso).format('dddd');
        const params = new URLSearchParams({ negozio: selectedNegozio, tipo: tipoTurno, giorno: dayName });
        const standard = await apiRequest<{ turno: string; oraInizio: string | null; oraFine: string | null }>(`/turni/standard?${params.toString()}`, { method: 'GET' });
        oraPrevInizio = standard.oraInizio || null;
        oraPrevFine = standard.oraFine || null;
      } catch (e) {
        console.warn('Nessun turno standard trovato, fallback agli orari di default', e);
        // Fallback to previous hardcoded defaults
        switch (tipoTurno) {
          case 'Mattina':
            oraPrevInizio = '08:00';
            oraPrevFine = '14:30';
            break;
          case 'Pomeriggio':
            oraPrevInizio = '14:30';
            oraPrevFine = '21:30';
            break;
          case 'Intermezzo':
            oraPrevInizio = '13:00';
            oraPrevFine = '19:00';
            break;
        }
      }
    } else if (tipoTurno === 'Riposo') {
      oraPrevInizio = null;
      oraPrevFine = null;
    } else if (tipoTurno === 'Personalizzato') {
      setCustomTurnoData({
        dipendente,
        day,
        dateIso: dateIso,
        oraInizio: orario ? orario.oraPrevInizio?.substring(0, 5) || '' : '',
        oraFine: orario ? orario.oraPrevFine?.substring(0, 5) || '' : '',
        turnoId: orario ? orario.id : null,
        isEsterno: !!(orario && isTurnoEsterno(orario)),
      });
      setCustomTurnoDialog(true);
      return;
    }

    try {
      // Genera baseline RIPOSO prima di creare/modificare il turno
      try {
        const meseNome = [
          'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
          'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
        ][selectedMese - 1];
        
        await apiRequest(
          `/orari-generati/genera-baseline?negozio=${selectedNegozio}&mese=${meseNome}&anno=${selectedAnno}`,
          { method: 'POST' }
        );
      } catch (err) {
        console.warn('Errore nella generazione baseline (non bloccante):', err);
      }

      const turnoData = {
        neg: selectedNegozio,
        descrizione: dipendente,
        data: dateIso,
        oraPrevInizio: oraPrevInizio,
        oraPrevFine: oraPrevFine,
        turno: tipoTurno,
        giornoSettimana: dayjs(dateIso).format('dddd'),
        utenteModifica: userInfo?.username || 'Utente',
      };

      // Cerca se esiste un turno (anche baseline) nella cella
      const turniCella = getTurniInCella(dipendente, dateIso);
      const baselineTurno = turniCella.find(t => isBaselineTurno(t) && !isTurnoEsterno(t));
      const turnoEsistente = (orario && (!isTurnoEsterno(orario) || isBaselineTurno(orario))) ? orario : baselineTurno;

      if (turnoEsistente) {
        // UPDATE: aggiorna il turno esistente (anche se baseline)
        await apiRequest<any>(`/orari-generati/${turnoEsistente.id}`, {
          method: 'PUT',
          body: JSON.stringify(turnoData),
          headers: { 'Content-Type': 'application/json' },
        });
        setSuccess('Turno aggiornato con successo');
      } else {
        // INSERT: cella completamente vuota, crea nuovo turno
        await apiRequest<any>('/orari-generati', {
          method: 'POST',
          body: JSON.stringify(turnoData),
          headers: { 'Content-Type': 'application/json' },
        });
        setSuccess('Turno creato con successo');
      }
      await fetchOrariGenerati();
      
      // Notifica al guard che cache deve essere invalidata (trigger check immediato)
      console.log('[GenerazioneOrario] Turno aggiunto/modificato - emetto evento di invalidazione cache');
      window.dispatchEvent(new CustomEvent('coordinatore-ore-lock-cache-invalidated'));
      
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      const lockReason = err?.response?.headers?.['x-lock-reason'];
      const serverMsg = err?.response?.data ? String(err.response.data) : null;
      if (status === 409) {
        setError(lockReason || serverMsg || 'Operazione non consentita: periodo bloccato');
      } else {
        setError(serverMsg || 'Errore nella gestione del turno');
      }
      console.error('Error managing turno:', err);
    }
  };

  // Blocca tutti i turni non-baseline nella cella (multi-turno)
  const handleBloccaTuttiInCella = async (dipendente: string, dateIso: string) => {
    const turni = getTurniInCella(dipendente, dateIso).filter(t => !isBaselineTurno(t) && !t.blocco);
    if (!turni.length) { setSuccess('Nessun turno da bloccare'); setContextMenu(null); return; }
    try {
      setLoading(true);
      await Promise.allSettled(turni.map(t =>
        apiRequest(`/orari-generati/${t.id}/blocca`, { method: 'PUT', headers: { 'Content-Type': 'application/json' } })
      ));
      await fetchOrariGenerati();
      setSuccess(`${turni.length} turno/i bloccato/i`);
    } catch (err) {
      setError('Errore nel bloccare i turni');
    } finally {
      setLoading(false);
      setContextMenu(null);
    }
  };

  // Sblocca tutti i turni bloccati nella cella (multi-turno)
  const handleSbloccaTuttiInCella = async (dipendente: string, dateIso: string) => {
    const turni = getTurniInCella(dipendente, dateIso).filter(t => !!t.blocco);
    if (!turni.length) { setSuccess('Nessun turno da sbloccare'); setContextMenu(null); return; }
    try {
      setLoading(true);
      await Promise.allSettled(turni.map(t =>
        apiRequest(`/orari-generati/${t.id}/sblocca`, { method: 'PUT', headers: { 'Content-Type': 'application/json' } })
      ));
      await fetchOrariGenerati();
      setSuccess(`${turni.length} turno/i sbloccato/i`);
    } catch (err) {
      setError('Errore nello sbloccare i turni');
    } finally {
      setLoading(false);
      setContextMenu(null);
    }
  };

  // Funzioni per bloccare/sbloccare turni
  const handleBloccaTurno = async () => {
    if (!contextMenu?.orario) return;
    if (isBaselineTurno(contextMenu.orario)) { setError('Il turno baseline RIPOSO non può essere bloccato'); handleCloseContextMenu(); return; }

    try {
      await apiRequest(`/orari-generati/${contextMenu.orario.id}/blocca`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });
      
      setSuccess('Turno bloccato con successo');
      await fetchOrariGenerati();
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      const lockReason = err?.response?.headers?.['x-lock-reason'];
      const serverMsg = err?.response?.data ? String(err.response.data) : null;
      if (status === 409) {
        setError(lockReason || serverMsg || 'Operazione non consentita: periodo bloccato');
      } else {
        setError(serverMsg || 'Errore nel bloccare il turno');
      }
      console.error('Error blocking turno:', err);
    }
    
    handleCloseContextMenu();
  };

  const handleSbloccaTurno = async () => {
    if (!contextMenu?.orario) return;

    const turnoId = contextMenu.orario.id;
    handleCloseContextMenu();

    try {
      await apiRequest(`/orari-generati/${turnoId}/sblocca`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });
      
      setSuccess('Turno sbloccato con successo');
      await fetchOrariGenerati();
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      const lockReason = err?.response?.headers?.['x-lock-reason'];
      const serverMsg = err?.response?.data ? String(err.response.data) : null;
      if (status === 409) {
        setError(lockReason || serverMsg || 'Operazione non consentita: periodo bloccato');
      } else {
        setError(serverMsg || 'Errore nello sbloccare il turno');
      }
      console.error('Error unblocking turno:', err);
    }
  };

  // Blocca Verticali: tutti i turni del giorno (colonna) per il mese/negozio corrente
  const handleBloccaVerticali = async () => {
    if (!contextMenu) return;
    const d = dayjs(contextMenu.dateIso);
    const day = d.date();
    try {
      setLoading(true);
      // Raccogli tutti i turni nel giorno selezionato (tutte le righe)
      const turniDaBloccare: number[] = [];
      matriceOrari.forEach(r => {
        const isCurrMonthDate = d.month() === (selectedMese - 1) && d.year() === selectedAnno;
        const isPrevMonthDate = d.isBefore(dayjs().year(selectedAnno).month(selectedMese - 1).date(1), 'day');
        const orariGiorno = isCurrMonthDate
          ? (r.orari[day] || [])
          : (isPrevMonthDate ? (r.orariPrev?.[day] || []) : (r.orariNext?.[day] || []));
        orariGiorno.forEach(t => { if (t && !isBaselineTurno(t) && t.id != null && t.blocco !== 1) turniDaBloccare.push(t.id); });
      });

      // Esegui le chiamate in parallelo e raccogli gli ID con esito positivo
      const results = await Promise.allSettled(
        turniDaBloccare.map(id => apiRequest(`/orari-generati/${id}/blocca`, { method: 'PUT', headers: { 'Content-Type': 'application/json' } }).then(() => id))
      );
      const successIds = new Set<number>(
        results
          .filter(r => r.status === 'fulfilled')
          .map((r: any) => r.value as number)
      );

      await fetchOrariGenerati();
      setSuccess(`Bloccati ${turniDaBloccare.length} turni nel giorno ${d.format('DD/MM/YYYY')}`);
    } catch (err) {
      setError('Errore nel blocco verticale dei turni');
      console.error('Error blocking vertical turns:', err);
    } finally {
      setLoading(false);
      handleCloseContextMenu();
    }
  };

  // Variante: blocco verticale da menu giorno
  const handleBloccaVerticaliByDay = async (dateIso: string) => {
    handleCloseDayMenu();
    try {
      setLoading(true);
      const d = dayjs(dateIso);
      const day = d.date();
      const turniDaBloccare: number[] = [];
      matriceOrari.forEach(r => {
        const isCurrMonthDate = d.month() === (selectedMese - 1) && d.year() === selectedAnno;
        const isPrevMonthDate = d.isBefore(dayjs().year(selectedAnno).month(selectedMese - 1).date(1), 'day');
        const orariGiorno = isCurrMonthDate
          ? (r.orari[day] || [])
          : (isPrevMonthDate ? (r.orariPrev?.[day] || []) : (r.orariNext?.[day] || []));
        orariGiorno.forEach(t => { if (t && !isBaselineTurno(t) && t.id != null && t.blocco !== 1) turniDaBloccare.push(t.id); });
      });

      const results = await Promise.allSettled(
        turniDaBloccare.map(id => apiRequest(`/orari-generati/${id}/blocca`, { method: 'PUT', headers: { 'Content-Type': 'application/json' } }).then(() => id))
      );
      const successIds = new Set<number>(
        results
          .filter(r => r.status === 'fulfilled')
          .map((r: any) => r.value as number)
      );

      await fetchOrariGenerati();
      setSuccess(`Bloccati ${turniDaBloccare.length} turni nel giorno ${d.format('DD/MM/YYYY')}`);
    } catch (err) {
      setError('Errore nel blocco verticale dei turni');
      console.error('Error blocking vertical turns:', err);
    } finally {
      setLoading(false);
    }
  };

  // Sblocca Verticali: tutti i turni bloccati del giorno (colonna)
  const handleSbloccaVerticaliByDay = async (dateIso: string) => {
    handleCloseDayMenu();
    try {
      setLoading(true);
      const d = dayjs(dateIso);
      const day = d.date();
      const turniDaSbloccare: number[] = [];
      matriceOrari.forEach(r => {
        const isCurrMonthDate = d.month() === (selectedMese - 1) && d.year() === selectedAnno;
        const isPrevMonthDate = d.isBefore(dayjs().year(selectedAnno).month(selectedMese - 1).date(1), 'day');
        const orariGiorno = isCurrMonthDate
          ? (r.orari[day] || [])
          : (isPrevMonthDate ? (r.orariPrev?.[day] || []) : (r.orariNext?.[day] || []));
        orariGiorno.forEach(t => { if (t && t.id != null && t.blocco === 1) turniDaSbloccare.push(t.id); });
      });

      const results = await Promise.allSettled(
        turniDaSbloccare.map(id => apiRequest(`/orari-generati/${id}/sblocca`, { method: 'PUT', headers: { 'Content-Type': 'application/json' } }).then(() => id))
      );
      const successIds = new Set<number>(
        results
          .filter(r => r.status === 'fulfilled')
          .map((r: any) => r.value as number)
      );

      await fetchOrariGenerati();
      setSuccess(`Sbloccati ${turniDaSbloccare.length} turni nel giorno ${d.format('DD/MM/YYYY')}`);
    } catch (err) {
      setError('Errore nello sblocco verticale dei turni');
      console.error('Error unlocking vertical turns:', err);
    } finally {
      setLoading(false);
    }
  };

  // Elimina Verticale: elimina tutti i turni NON bloccati del giorno (colonna)
  const handleEliminaVerticaleByDay = async (dateIso: string) => {
    handleCloseDayMenu();
    try {
      const d = dayjs(dateIso);
      if (isLockedForUser(d.year(), d.month() + 1)) { setError(LOCK_MESSAGE); return; }
      // Conferma azione distruttiva
      if (!window.confirm(`Sei sicuro di voler eliminare TUTTI i turni del giorno ${d.format('DD/MM/YYYY')} per il negozio ${selectedNegozio}?`)) {
        return;
      }
      setLoading(true);
      const day = d.date();
      // Per ogni cella (riga × giorno) separiamo: al massimo 1 viene convertito a baseline,
      // gli extra (turni doppi nello stesso giorno) vengono eliminati fisicamente.
      const idsToBaseline: number[] = [];
      const idsToDelete: number[] = [];
      matriceOrari.forEach(r => {
        const isCurrMonthDate = d.month() === (selectedMese - 1) && d.year() === selectedAnno;
        const isPrevMonthDate = d.isBefore(dayjs().year(selectedAnno).month(selectedMese - 1).date(1), 'day');
        const orariGiorno = isCurrMonthDate
          ? (r.orari[day] || [])
          : (isPrevMonthDate ? (r.orariPrev?.[day] || []) : (r.orariNext?.[day] || []));
        // Turni eliminabili: non baseline; le preferenze devono essere convertite/eliminate come gli altri turni
        const toReset = orariGiorno.filter(t => t && t.id != null && !isBaselineTurno(t) && (t.blocco !== 1 || isPreferenzaTurno(t)));
        if (toReset.length === 0) return;
        const hasExistingBaseline = orariGiorno.some(t => t && isBaselineTurno(t));
        if (hasExistingBaseline) {
          // Esiste già un baseline: tutti i turni extra vanno eliminati fisicamente
          toReset.forEach(t => idsToDelete.push(t.id!));
        } else {
          // Nessun baseline: il primo viene convertito a riposo, gli altri eliminati
          idsToBaseline.push(toReset[0].id!);
          toReset.slice(1).forEach(t => idsToDelete.push(t.id!));
        }
      });

      const totalAffected = idsToBaseline.length + idsToDelete.length;
      if (totalAffected === 0) {
        setSuccess(`Nessun turno da eliminare nel giorno ${day}`);
        return;
      }

      await Promise.allSettled([
        ...idsToBaseline.map(id => apiRequest(`/orari-generati/${id}/baseline`, { method: 'PUT' })),
        ...idsToDelete.map(id => apiRequest(`/orari-generati/${id}`, { method: 'DELETE' })),
      ]);

      await fetchOrariGenerati();
      setSuccess(`Ripristinati a riposo ${idsToBaseline.length} turni nel giorno ${d.format('DD/MM/YYYY')}${idsToDelete.length ? ` (eliminati ${idsToDelete.length} turni doppi)` : ''}`);
    } catch (err) {
      setError('Errore nell\'eliminazione verticale dei turni');
      console.error('Error deleting vertical turns:', err);
    } finally {
      setLoading(false);
    }
  };

  // Segna annotazione per tutte le righe del giorno (Inventario/Ricartellinamento/Giro)
  const handleSegnaAnnotazioneByDay = async (dateIso: string, label: 'Inventario' | 'Ricartellinamento' | 'Giro') => {
    handleCloseDayMenu();
    try {
      if (!selectedNegozio) { return; }
      const d = dayjs(dateIso);
      if (isLockedForUser(d.year(), d.month() + 1)) { setError(LOCK_MESSAGE); return; }
      setLoading(true);
      const params = new URLSearchParams({
        negozio: selectedNegozio,
        data: dateIso,
        azione: 'set',
        valore: label
      });
      await apiRequest(`/orari-generati/annotazioni/giorno?${params.toString()}`, { method: 'POST' });
      // Aggiorna lo stato locale senza re-fetch completo della matrice
      setAnnotazioniGiorno(prev => {
        const existing = prev[dateIso] || [];
        if (existing.some(l => l.toLowerCase() === label.toLowerCase())) return prev;
        return { ...prev, [dateIso]: [...existing, label] };
      });
      setSuccess(`Segnato ${label} per il ${d.format('DD/MM/YYYY')}`);
    } catch (err) {
      console.error('Errore segna annotazione giornata:', err);
      setError('Errore nel segnare annotazione per il giorno selezionato');
    } finally {
      setLoading(false);
    }
  };

  // Cancella annotazione per il giorno dal negozio (Inventario/Ricartellinamento/Giro)
  const handleCancellaAnnotazioneByDay = async (dateIso: string, label: 'Inventario' | 'Ricartellinamento' | 'Giro') => {
    handleCloseDayMenu();
    try {
      if (!selectedNegozio) { return; }
      const d = dayjs(dateIso);
      if (isLockedForUser(d.year(), d.month() + 1)) { setError(LOCK_MESSAGE); return; }
      setLoading(true);
      const params = new URLSearchParams({
        negozio: selectedNegozio,
        data: dateIso,
        azione: 'clear',
        valore: label
      });
      await apiRequest(`/orari-generati/annotazioni/giorno?${params.toString()}`, { method: 'POST' });
      // Aggiorna lo stato locale senza re-fetch completo della matrice
      setAnnotazioniGiorno(prev => {
        const existing = prev[dateIso] || [];
        const updated = existing.filter(l => l.toLowerCase() !== label.toLowerCase());
        if (updated.length === 0) {
          const { [dateIso]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [dateIso]: updated };
      });
      setSuccess(`Cancellato ${label} per il ${d.format('DD/MM/YYYY')}`);
    } catch (err) {
      console.error('Errore cancella annotazione giornata:', err);
      setError('Errore nel cancellare annotazione per il giorno selezionato');
    } finally {
      setLoading(false);
    }
  };

  // Aggiunge per TUTTI i dipendenti del giorno selezionato un'ASSENZA con codice "CHIUSO DITTA"
  const handleAggiungiChiusoDittaByDay = async (dateIso: string) => {
    handleCloseDayMenu();
    if (!selectedNegozio || !selectedMese || !selectedAnno) { return; }
    const d = dayjs(dateIso);
    if (isLockedForUser(d.year(), d.month() + 1)) { setError(LOCK_MESSAGE); return; }

    const dataIso = dateIso;
    const giornoSettimana = d.format('dddd');

    try {
      setLoading(true);

      // PRIMO STEP: Genera baseline RIPOSO per tutti i giorni/dipendenti del mese
      // (solo se non già presenti) PRIMA di iniziare ad aggiungere CHIUSO DITTA
      try {
        const meseNome = [
          'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
          'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
        ][selectedMese - 1];
        
        await apiRequest(
          `/orari-generati/genera-baseline?negozio=${selectedNegozio}&mese=${meseNome}&anno=${selectedAnno}`,
          { method: 'POST' }
        );
      } catch (err) {
        console.warn('Errore nella generazione baseline (non bloccante):', err);
        // Non blocchiamo l'operazione se la generazione baseline fallisce
      }

      // SECONDO STEP: Esegui in parallelo per tutti i dipendenti; per ciascuno:
      // - elimina turni non bloccati e non ASSENZA del giorno
      // - se esiste già un'ASSENZA, aggiorna il CODICE_TIPO_ORARIO a CHIUSO DITTA
      // - altrimenti crea una nuova ASSENZA con codice CHIUSO DITTA
      const day = d.date();
      const ops = matriceOrari.map(async (riga) => {
        const isCurrMonthDate = d.month() === (selectedMese - 1) && d.year() === selectedAnno;
        const isPrevMonthDate = d.isBefore(dayjs().year(selectedAnno).month(selectedMese - 1).date(1), 'day');
        const orariGiorno = isCurrMonthDate
          ? (riga.orari[day] || [])
          : (isPrevMonthDate ? (riga.orariPrev?.[day] || []) : (riga.orariNext?.[day] || []));
        // Nuova regola: se nel giorno esiste almeno un turno BLOCCATO per il dipendente, non applicare CHIUSO DITTA
        const hasBlocked = orariGiorno.some(t => t.blocco === 1);
        if (hasBlocked) {
          return;
        }

        // Calcola orari previsti in base alle ore contrattuali
        let oraPrevInizio = null;
        let oraPrevFine = null;
        const dip = dipendenti.find(dp => dp.nome === riga.dipendente);
        if (dip) {
          const g = giornoSettimana.toLowerCase();
          let ore = 0;
          if (g.includes('lun')) ore = dip.lunedi;
          else if (g.includes('mar')) ore = dip.martedi;
          else if (g.includes('mer')) ore = dip.mercoledi;
          else if (g.includes('gio')) ore = dip.giovedi;
          else if (g.includes('ven')) ore = dip.venerdi;
          else if (g.includes('sab')) ore = dip.sabato;
          else if (g.includes('dom')) ore = dip.domenica;

          if (ore > 0) {
            oraPrevInizio = '00:00';
            const h = Math.floor(ore);
            const m = Math.round((ore - h) * 60);
            const endH = h % 24;
            oraPrevFine = `${String(endH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
          }
        }

        // Identifica i turni modificabili
        const modifiableTurni = orariGiorno.filter(t => t.blocco !== 1);

        // Cerca un candidato per l'UPDATE:
        // 1. Un'assenza esistente (priorità massima)
        // 2. Un turno vuoto/riposo (per evitare DEL+INS su baseline)
        let targetTurno = modifiableTurni.find(t => (t.turno || '').toUpperCase() === 'ASSENZA');
        if (!targetTurno) {
          targetTurno = modifiableTurni.find(t => !t.turno || t.turno === '' || t.codiceTipoOrario === 'RIPOSO');
        }

        if (targetTurno) {
          // UPDATE: ricicla il turno esistente (Assenza o Vuoto/Riposo)
          // Usa le ore già calcolate sopra
          const oraConsInizio = oraPrevInizio || '00:00';
          const oraConsFine = oraPrevFine || '00:00';
          
          const updated = { 
             ...targetTurno, 
             codiceTipoOrario: 'CHIUSO DITTA',
             oraPrevInizio: oraConsInizio,
             oraPrevFine: oraConsFine,
             oraConsInizio: oraConsInizio,
             oraConsFine: oraConsFine,
             turno: 'ASSENZA',
             utenteModifica: userInfo?.username || 'Utente'
          };
          await apiRequest(`/orari-generati/${targetTurno.id}`, { 
             method: 'PUT',
             headers: { 'Content-Type': 'application/json' },
             body: JSON.stringify(updated)
          }).catch(() => {});

          // Ripristina a baseline eventuali altri turni in eccesso (es. se c'era sia turno vuoto che altro)
          const others = modifiableTurni.filter(t => t.id !== targetTurno!.id);
          if (others.length > 0) {
            const baselineOps = others.map(t => apiRequest(`/orari-generati/${t.id}/baseline`, { method: 'PUT' }).catch(() => {}));
            await Promise.allSettled(baselineOps);
          }
        } else {
          // FALLBACK: ripristina a baseline (se presenti) + INSERT
          // Se ci sono turni non riciclabili (es. lavorativi standard), li ripristiniamo a baseline prima di inserire
          if (modifiableTurni.length > 0) {
            const baselineOps = modifiableTurni.map(t => apiRequest(`/orari-generati/${t.id}/baseline`, { method: 'PUT' }).catch(() => {}));
            await Promise.allSettled(baselineOps);
          }

          // INSERT nuovo record (caso: nessun turno presente o dopo ripristino)
          const payload: any = {
            neg: selectedNegozio,
            descrizione: riga.dipendente,
            data: dataIso,
            oraPrevInizio: oraPrevInizio,
            oraPrevFine: oraPrevFine,
            oraConsInizio: oraPrevInizio,
            oraConsFine: oraPrevFine,
            turno: 'ASSENZA',
            codiceTipoOrario: 'CHIUSO DITTA',
            giornoSettimana,
            utenteModifica: userInfo?.username || 'Utente',
          };
          await apiRequest('/orari-generati', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          }).catch(() => {});
        }
      });

      await Promise.allSettled(ops);
  await fetchOrariGenerati();
  setSuccess(`Impostato CHIUSO DITTA per tutti i dipendenti il ${d.format('DD/MM/YYYY')}`);
    } catch (err) {
      console.error('Errore nel settaggio CHIUSO DITTA di giornata:', err);
      setError('Errore nell\'impostare CHIUSO DITTA per il giorno selezionato');
    } finally {
      setLoading(false);
    }
  };

  // Blocca Orizzontali: tutti i turni del dipendente nelle date visibili della riga.
  const handleBloccaOrizzontali = async () => {
    if (!contextMenu) return;
    await handleBloccaOrizzontaliByDip(contextMenu.dipendente);
  };

  // Blocco orizzontale su TUTTI i giorni visibili (mese corrente + prev/next visibili)
  const handleBloccaOrizzontaliByDip = async (dipendente: string) => {
    handleCloseDipMenu();
    try {
      setLoading(true);
      const ids = new Set<number>();
      allGridDays.forEach(d => {
        getTurniInCella(dipendente, d.format('YYYY-MM-DD'))
          .forEach(t => { if (t.id != null && t.blocco !== 1) ids.add(t.id); });
      });

      const results = await Promise.allSettled(Array.from(ids).map(id => apiRequest(`/orari-generati/${id}/blocca`, { method: 'PUT', headers: { 'Content-Type': 'application/json' } }).then(() => id)));
      const successIds = new Set<number>(results.filter(r => r.status === 'fulfilled').map((r: any) => r.value as number));

      await fetchOrariGenerati();
      setSuccess(`Bloccati ${successIds.size} turni visibili di ${dipendente}`);
      await refetchStats();
    } catch (err) {
      setError('Errore nel blocco orizzontale dei turni');
      console.error('Error blocking horizontal turns:', err);
    } finally {
      setLoading(false);
      handleCloseDipMenu();
    }
  };

  // Sblocca Orizzontali: tutti i turni bloccati del dipendente (riga)
  const handleSbloccaOrizzontaliByDip = async (dipendente: string) => {
    handleCloseDipMenu();
    try {
      setLoading(true);
      const ids = new Set<number>();
      allGridDays.forEach(d => {
        getTurniInCella(dipendente, d.format('YYYY-MM-DD'))
          .forEach(t => { if (t.id != null && t.blocco === 1) ids.add(t.id); });
      });

      const results = await Promise.allSettled(Array.from(ids).map(id => apiRequest(`/orari-generati/${id}/sblocca`, { method: 'PUT', headers: { 'Content-Type': 'application/json' } }).then(() => id)));
      const successIds = new Set<number>(results.filter(r => r.status === 'fulfilled').map((r: any) => r.value as number));

      await fetchOrariGenerati();
      setSuccess(`Sbloccati ${successIds.size} turni visibili di ${dipendente}`);
      await refetchStats();
    } catch (err) {
      setError('Errore nello sblocco orizzontale dei turni');
      console.error('Error unlocking horizontal turns:', err);
    } finally {
      setLoading(false);
      handleCloseDipMenu();
    }
  };

  // Elimina Orizzontale: elimina tutti i turni NON bloccati del dipendente (riga) per i giorni visibili
  const handleEliminaOrizzontaleByDip = async (dipendente: string) => {
    handleCloseDipMenu();
    try {
      if (!window.confirm(`Eliminare TUTTI i turni visibili (mese corrente + giorni mostrati fuori mese) per ${dipendente}?`)) { return; }
      setLoading(true);
      const riga = matriceOrari.find(r => r.dipendente === dipendente);
      if (!riga) { return; }

      // Per ogni giorno, al massimo 1 turno viene convertito a baseline;
      // i turni doppi nello stesso giorno vengono eliminati fisicamente.
      const idsToBaseline: number[] = [];
      const idsToDelete: number[] = [];
      let skippedLocked = 0;
      allGridDays.forEach(d => {
        const day = d.date();
        if (isLockedForUser(d.year(), d.month()+1)) { skippedLocked++; return; }
        const isCurrMonthDate = d.month() === (selectedMese - 1) && d.year() === selectedAnno;
        const isPrevMonthDate = d.isBefore(dayjs().year(selectedAnno).month(selectedMese - 1).date(1), 'day');
        const orariGiorno = isCurrMonthDate ? (riga.orari[day] || []) : (isPrevMonthDate ? (riga.orariPrev?.[day] || []) : (riga.orariNext?.[day] || []));
        // Turni eliminabili: non baseline; le preferenze devono essere convertite/eliminate come gli altri turni
        const toReset = orariGiorno.filter(t => t && t.id != null && !isBaselineTurno(t) && (t.blocco !== 1 || isPreferenzaTurno(t)));
        if (toReset.length === 0) return;
        const hasExistingBaseline = orariGiorno.some(t => t && isBaselineTurno(t));
        if (hasExistingBaseline) {
          // Esiste già un baseline: tutti i turni extra vanno eliminati fisicamente
          toReset.forEach(t => idsToDelete.push(t.id!));
        } else {
          // Nessun baseline: il primo viene convertito a riposo, gli altri eliminati
          idsToBaseline.push(toReset[0].id!);
          toReset.slice(1).forEach(t => idsToDelete.push(t.id!));
        }
      });

      const totalAffected = idsToBaseline.length + idsToDelete.length;
      if (!totalAffected) { setSuccess(`Nessun turno da eliminare per ${dipendente}`); handleCloseDipMenu(); return; }

      await Promise.allSettled([
        ...idsToBaseline.map(id => apiRequest(`/orari-generati/${id}/baseline`, { method: 'PUT' })),
        ...idsToDelete.map(id => apiRequest(`/orari-generati/${id}`, { method: 'DELETE' })),
      ]);
      // Ricarica dalla sorgente per riflettere i turni bloccati rimasti e gli eliminati
      await fetchOrariGenerati();
      setSuccess(`Ripristinati a riposo ${idsToBaseline.length} turni visibili per ${dipendente}${idsToDelete.length ? ` (eliminati ${idsToDelete.length} turni doppi)` : ''}${skippedLocked ? ` (saltati ${skippedLocked} giorni bloccati)` : ''}`);
      await refetchStats();
    } catch (err) {
      setError("Errore nell'eliminazione orizzontale dei turni");
      console.error('Error deleting horizontal turns:', err);
    } finally {
      setLoading(false);
      handleCloseDipMenu();
    }
  };

  // Gestore inserimento preferenza da GenerazioneOrario (bypass periodo)
  const handlePrefDialogInsert = async (turno: string) => {
    if (!prefDialog) return;
    setPrefDialogLoading(true);
    setPrefDialogError(null);
    try {
      await preferenzeService.insertPreference(prefDialog.dateIso, turno, prefDialog.dipendente, true, prefDialog.neg);
      setPrefDialog(null);
      // Aggiorna la griglia come fanno gli altri inserimenti turno
      await Promise.all([refetchTurni(), refetchStats()]);
    } catch (err: any) {
      setPrefDialogError(err?.response?.data?.message || err?.message || 'Errore nell\'inserimento preferenza');
    } finally {
      setPrefDialogLoading(false);
    }
  };

  const handleCustomTurnoSave = async () => {
    const { dipendente, day, dateIso, oraInizio, oraFine, turnoId, isEsterno } = customTurnoData;
    
    if (!oraInizio || !oraFine) {
      setError('Inserisci entrambi gli orari');
      return;
    }
    
  const data = dateIso || dayjs().year(selectedAnno).month(selectedMese - 1).date(day).format('YYYY-MM-DD');
    const turnoSovrapposto = getTurniInCella(dipendente, data).find((turno) => {
      if (turno.id === turnoId) return false;
      const inizioEsistente = turno.oraConsInizio && turno.oraConsFine
        ? turno.oraConsInizio
        : turno.oraPrevInizio;
      const fineEsistente = turno.oraConsInizio && turno.oraConsFine
        ? turno.oraConsFine
        : turno.oraPrevFine;
      return !!inizioEsistente && !!fineEsistente
        && oraInizio < fineEsistente && inizioEsistente < oraFine;
    });
    if (turnoSovrapposto) {
      const inizioEsistente = turnoSovrapposto.oraConsInizio && turnoSovrapposto.oraConsFine
        ? turnoSovrapposto.oraConsInizio
        : turnoSovrapposto.oraPrevInizio;
      const fineEsistente = turnoSovrapposto.oraConsInizio && turnoSovrapposto.oraConsFine
        ? turnoSovrapposto.oraConsFine
        : turnoSovrapposto.oraPrevFine;
      setCustomTurnoDialog(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setSuccess(null);
      setError(`Orario non valido: il turno ${oraInizio} - ${oraFine} si sovrappone al turno esistente ${inizioEsistente} - ${fineEsistente} dello stesso giorno.`);
      return;
    }
    
    // Chiudi subito il dialog per evitare doppi click
    setCustomTurnoDialog(false);
    
    try {
      // Genera baseline RIPOSO prima di creare/modificare il turno personalizzato
      try {
        await ensureBaselineForSelectedPeriod();
      } catch (err) {
        console.warn('Errore nella generazione baseline (non bloccante):', err);
      }

      const turnoData = {
        neg: selectedNegozio,
        descrizione: dipendente,
        data,
        oraPrevInizio: oraInizio,
        oraPrevFine: oraFine,
        turno: 'Personalizzato',
  giornoSettimana: dayjs(data).format('dddd'),
  utenteModifica: userInfo?.username || 'Utente'
      };
      
      let idDaAggiornare = isEsterno ? null : turnoId;
      if (!idDaAggiornare) {
        const turniCella = getTurniInCella(dipendente, data);
        const baselineTurno = turniCella.find(t => isBaselineTurno(t) && (!isEsterno || isTurnoEsterno(t)));
        if (baselineTurno) {
          idDaAggiornare = baselineTurno.id;
        }
      }

      if (idDaAggiornare) {
        // UPDATE: aggiorna turno esistente (baseline esterno incluso)
        await apiRequest(`/orari-generati/${idDaAggiornare}`, { 
          method: 'PUT',
          body: JSON.stringify(turnoData),
          headers: { 'Content-Type': 'application/json' }
        });
        setSuccess('Turno personalizzato aggiornato con successo');
      } else {
        // INSERT: cella completamente vuota, crea nuovo turno
        await apiRequest('/orari-generati', { 
          method: 'POST',
          body: JSON.stringify(turnoData),
          headers: { 'Content-Type': 'application/json' }
        });
        setSuccess('Turno personalizzato creato con successo');
      }
      
      refreshAfterTurnWrite();
    } catch (err) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setSuccess(null);
      handleApiError(err, 'Errore nella gestione del turno personalizzato');
      console.error('Error managing custom turno:', err);
    }
  };

  const handleCancellaOrari = async () => {
    if (!selectedNegozio || !selectedMese || !selectedAnno) {
      setError('Seleziona negozio, mese e anno prima di cancellare');
      return;
    }

    if (!window.confirm(`Sei sicuro di voler cancellare tutti gli orari di ${selectedNegozio} per ${selectedMese}/${selectedAnno}?`)) {
      return;
    }

    // Reset del ciclo dei pulsanti weekend alla fase iniziale indipendentemente dall'esito
    setWeekendPhase('domeniche');

    try {
      setLoading(true);
      await apiRequest(`/orari-generati/cancella?negozio=${selectedNegozio}&mese=${selectedMese}&anno=${selectedAnno}`, { 
        method: 'DELETE' 
      });
      await fetchOrariGenerati();
      await refetchStats(); // Aggiorna statistiche dopo cancellazione
      setSuccess('Tutti gli orari sono stati cancellati con successo');
    } catch (err) {
      setError('Errore nella cancellazione degli orari');
      console.error('Error cancelling orari:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancellaDuplicati = async () => {
    if (!selectedNegozio || !selectedMese || !selectedAnno) {
      setError('Seleziona negozio, mese e anno prima di cancellare i duplicati');
      return;
    }

    if (!window.confirm(`Cancellare i turni vuoti duplicati di ${selectedNegozio} per ${selectedMese}/${selectedAnno}?`)) {
      return;
    }

    try {
      setLoading(true);
      const message = await apiRequest<string>(`/orari-generati/cancella-duplicati?negozio=${selectedNegozio}&mese=${selectedMese}&anno=${selectedAnno}`, {
        method: 'DELETE'
      });
      await fetchOrariGenerati();
      await refetchStats();
      setSuccess(message || 'Duplicati vuoti cancellati con successo');
    } catch (err) {
      setError('Errore nella cancellazione dei duplicati');
      console.error('Error cancelling duplicate empty shifts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Funzioni per il drag and drop
  const handleDragStart = (e: React.DragEvent, dipendente: string, dateIso: string, orario: any) => {
    // Se siamo in modalità selezione orizzontale, non avviare drag
    if (selectionModeActiveRef.current) {
      e.preventDefault();
      return;
    }
    // Impedisci il drag sui turni bloccati
    if (orario && orario.blocco) {
      e.preventDefault();
      return;
    }
    
    setDragOverCell(null);
    draggedTurnoRef.current = {
      dipendenteSource: dipendente,
      dateIso,
      orario: orario
    };
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, dipendente: string, dateIso: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Evidenzia solo la stessa data reale, senza render React per ogni dragover.
    const draggedTurno = draggedTurnoRef.current;
    setDragOverCell(draggedTurno && draggedTurno.dateIso === dateIso ? e.currentTarget as HTMLElement : null);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    const relatedTarget = e.relatedTarget as Node | null;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) setDragOverCell(null);
  };

  const handleDrop = async (e: React.DragEvent, dipendenteTarget: string, dateIso: string) => {
    e.preventDefault();
    setDragOverCell(null);

    const draggedTurno = draggedTurnoRef.current;
    if (!draggedTurno) return;

    // Verifica che sia la stessa data (solo spostamento verticale).
    if (draggedTurno.dateIso !== dateIso) {
      draggedTurnoRef.current = null;
      return;
    }

    // Verifica che non sia lo stesso dipendente
    if (draggedTurno.dipendenteSource === dipendenteTarget) {
      draggedTurnoRef.current = null;
      return;
    }

    // Verifica che nessuno dei turni coinvolti sia bloccato
    const sourceRow = matriceOrari.find(r => r.dipendente === draggedTurno.dipendenteSource);
    const targetRow = matriceOrari.find(r => r.dipendente === dipendenteTarget);
    
    if (sourceRow && targetRow) {
      const sourceOrari = getTurniInCella(draggedTurno.dipendenteSource, dateIso);
      const targetOrari = getTurniInCella(dipendenteTarget, dateIso);
      
      // Verifica se c'è qualche turno bloccato nell'array
      const sourceBloccato = sourceOrari.some(orario => orario && orario.blocco);
      const targetBloccato = targetOrari.some(orario => orario && orario.blocco);
      
      if (sourceBloccato || targetBloccato) {
        setError('Non è possibile spostare turni bloccati (preferenze dipendenti)');
        draggedTurnoRef.current = null;
        return;
      }
    }

    try {
      setLoading(true);
      
      // Trova gli orari dei due dipendenti per questo giorno
      const sourceRow = matriceOrari.find(r => r.dipendente === draggedTurno.dipendenteSource);
      const targetRow = matriceOrari.find(r => r.dipendente === dipendenteTarget);
      
      if (!sourceRow || !targetRow) {
        throw new Error('Dipendenti non trovati');
      }

      const sourceOrari = getTurniInCella(draggedTurno.dipendenteSource, dateIso);
      const targetOrari = getTurniInCella(dipendenteTarget, dateIso);

      // Determina il testo per la conferma basato sul numero di turni
      const getOrarioText = (orari: TurnoCreato[]) => {
        if (orari.length === 0) return 'Riposo';
        if (orari.length === 1) {
          const orario = orari[0];
          return orario.turno || 'Riposo';
        }
        return `${orari.length} turni`;
      };

      const sourceText = getOrarioText(sourceOrari);
      const targetText = getOrarioText(targetOrari);

      // Conferma solo se avvisi attivi
      const proceed = avvisiEnabled ? window.confirm(`Vuoi scambiare "${sourceText}" di ${draggedTurno.dipendenteSource} con "${targetText}" di ${dipendenteTarget}?`) : true;
      if (!proceed) {
        draggedTurnoRef.current = null;
        setLoading(false);
        return;
      }
      // Esegui swap completo dei turni (front-end)
      await swapAllTurnsForDateBetween(draggedTurno.dipendenteSource, dipendenteTarget, dateIso);
      setSuccess('Turni scambiati con successo!');
      
    } catch (err) {
      console.error('Errore completo nello scambio dei turni:', err);
      if (err instanceof Error) {
        setError(`Errore nello scambio dei turni: ${err.message}`);
      } else {
        setError('Errore nello scambio dei turni');
      }
    } finally {
      setLoading(false);
      draggedTurnoRef.current = null;
    }
  };
  const handleDragEnd = () => {
    setDragOverCell(null);
    draggedTurnoRef.current = null;
    resetGesture();
  };

  // Apertura/chiusura menu globale (header Dipendenti)
  const handleOpenGlobalMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    if (loading) return;
    setGlobalMenu(
      globalMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null
    );
  };
  const handleCloseGlobalMenu = () => setGlobalMenu(null);

  // Blocca TUTTI i turni del mese/negozio corrente (su tutte le righe/colonne) + spillover via endpoint dedicato
  const handleBloccaTutto = async () => {
    try {
      if (isLocked) { setError(LOCK_MESSAGE); handleCloseGlobalMenu(); return; }
      setLoading(true);
      // Endpoint bulk: include anche spillover
      const msg = await apiRequest<string>(`/orari-generati/blocca-tutto?negozio=${selectedNegozio}&mese=${selectedMese}&anno=${selectedAnno}`, { 
        method: 'PUT', headers: { 'Content-Type': 'application/json' }
      });
      await refetchTurni();
      await refetchStats();
      setSuccess(msg || `Blocca tutto completato per ${selectedNegozio}`);
    } catch (err) {
      setError('Errore nel blocco di tutti i turni');
      console.error('Error blocking all turns:', err);
    } finally {
      setLoading(false);
      handleCloseGlobalMenu();
    }
  };

  // Sblocca TUTTI i turni bloccati del mese/negozio corrente + spillover via endpoint dedicato
  const handleSbloccaTutto = async () => {
    try {
      setLoading(true);
      // Endpoint bulk: include anche spillover
      const msg = await apiRequest<string>(`/orari-generati/sblocca-tutto?negozio=${selectedNegozio}&mese=${selectedMese}&anno=${selectedAnno}`, { 
        method: 'PUT', headers: { 'Content-Type': 'application/json' }
      });
      await refetchTurni();
      await refetchStats();
      setSuccess(msg || `Sblocca tutto completato per ${selectedNegozio}`);
    } catch (err) {
      setError('Errore nello sblocco di tutti i turni');
      console.error('Error unlocking all turns:', err);
    } finally {
      setLoading(false);
      handleCloseGlobalMenu();
    }
  };

  // Funzione per eliminare orario (attualmente non utilizzata)
  // const handleDeleteOrario = async (orarioId: number) => {
  //   if (!window.confirm('Sei sicuro di voler eliminare questo orario?')) return;
  //   
  //   try {
  //     await apiRequest(`/orari-generati/${orarioId}`, { method: 'DELETE' });
  //     await fetchOrariGenerati();
  //     setSuccess('Orario eliminato con successo');
  //   } catch (err) {
  //     setError('Errore nell\'eliminazione dell\'orario');
  //     console.error('Error deleting orario:', err);
  //   }
  // };

  // Funzioni per gestire i dipendenti temporanei
  const aggiungiDipendenteTemporaneo = async (nomeDipendente: string) => {
    if (!selectedNegozio) {
      setError('Seleziona prima un negozio');
      return;
    }

    try {
      
      const response = await apiRequest(`/orari-generati/dipendenti-temporanei/aggiungi`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          nomeDipendente: nomeDipendente,
          negozioDestinazione: selectedNegozio,
          mese: mesi[selectedMese - 1],
          anno: selectedAnno.toString()
        }).toString()
      });

      // Gestisce la risposta del backend
      const responseText = typeof response === 'string' ? response : response.data || response;
      
      // Controlla se è un messaggio di errore o informativo
      if (responseText.includes('appartiene già al negozio') || 
          responseText.includes('è già presente come temporaneo')) {
        setError(responseText);
        return;
      }
      
      // Se è un successo, ricarica i dati e mostra il messaggio
      await fetchDipendenti();
      setSuccess(responseText || `Dipendente ${nomeDipendente} aggiunto temporaneamente al negozio ${selectedNegozio} per ${mesi[selectedMese - 1]} ${selectedAnno}`);
    } catch (err: any) {
      console.error('Errore nell\'aggiunta del dipendente temporaneo:', err);
      
      // Prova a estrarre il messaggio di errore specifico dal backend
      let errorMessage = 'Errore nell\'aggiunta del dipendente temporaneo';
      
      if (err.response?.data) {
        // Se il backend ritorna un messaggio di errore specifico
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    }
  };

  const isRiposoPlaceholder = (orario: TurnoCreato): boolean => {
    // Reindirizza al nuovo helper baseline che aggiunge controllo su utenteModifica
    return isBaselineTurno(orario);
  };

  const getTurnoColor = (turno: string) => {
    switch (turno) {
      case 'Mattina': return 'info'; // giallo chiaro personalizzato
      case 'Mattina P.': return 'info'; // stesso colore di Mattina
      case 'Pomeriggio': return 'custom-yellow'; // azzurrino chiaro
      case 'Pomeriggio P.': return 'custom-yellow'; // stesso colore di Pomeriggio
      case 'Intermezzo': return 'success'; // verdino chiaro
      case 'Intermezzo P.': return 'success'; // stesso colore di Intermezzo
      case 'Personalizzato': return 'warning'; // arancione
      case 'Riposo': 
      case 'RIPOSO': return 'default'; // grigio
      case '-': return 'default';
      case 'ASSENZA': 
      case 'Assenza': return 'error'; // rosso non troppo acceso
      default: return 'default';
    }
  };

  // Iniziale da mostrare nel badge compatto in modalità "Mese intero"
  const getTurnoInitial = (label: string) => {
    switch (label) {
      case 'Mattina':
      case 'Mattina P.':
        return 'M';
      case 'Pomeriggio':
      case 'Pomeriggio P.':
        return 'P';
      case 'Intermezzo':
      case 'Intermezzo P.':
        return 'I';
      case 'Assenza':
        return 'A';
      case 'Riposo':
      case 'RIPOSO':
        return 'R';
      case '-':
        return '-';
      default:
        // Se non c'è etichetta (baseline) non mostrare nulla
        if (!label) return '';
        return label.charAt(0).toUpperCase();
    }
  };

  // Palette per il badge compatto in base al tipo di turno (nuovi colori richiesti)
  // Mattina:   #35abec (testo bianco)
  // Pomeriggio:#FDD65C (testo nero)
  // Intermezzo:#60b964 (testo bianco)
  // Riposo:    #d5d3d3 (testo nero)
  const getBadgePalette = (label: string): { bg: string; fg: string } => {
    switch (label) {
      case 'Mattina':
      case 'Mattina P.':
        return { bg: '#35abec', fg: '#ffffff' };
      case 'Pomeriggio':
      case 'Pomeriggio P.':
        return { bg: '#FDD65C', fg: '#000000' };
      case 'Intermezzo':
      case 'Intermezzo P.':
        return { bg: '#60b964', fg: '#ffffff' };
      case 'Riposo':
      case 'RIPOSO':
      case '-':
        return { bg: '#d5d3d3', fg: '#000000' };
      case 'ASSENZA':
      case 'Assenza':
        return { bg: 'error.main', fg: 'error.contrastText' };
      default:
        return { bg: 'grey.400', fg: 'grey.900' };
    }
  };

  const isCustomTurnoPalette = (label: string): boolean => {
    return (
      label === 'Mattina' || label === 'Mattina P.' ||
      label === 'Pomeriggio' || label === 'Pomeriggio P.' ||
      label === 'Intermezzo' || label === 'Intermezzo P.' ||
      label === 'Riposo' || label === 'RIPOSO' ||
      label === '-'
    );
  };

  // Deriva l'etichetta per i turni Personalizzati in base all'ora di inizio prevista
  // Nuove soglie:
  //  - Mattina P.    <= 09:44
  //  - Intermezzo P.  09:45 - 12:59
  //  - Pomeriggio P. >= 13:00
  const derivePersonalizzatoLabel = (oraPrevInizio?: string | null): string | null => {
    if (!oraPrevInizio) return null;
    // Estrai HH:MM e converti in minuti
    const hhmm = oraPrevInizio.substring(0, 5);
    const [hhStr, mmStr] = hhmm.split(':');
    const hh = parseInt(hhStr, 10);
    const mm = parseInt(mmStr || '0', 10);
    if (isNaN(hh) || isNaN(mm)) return null;
    const minutes = hh * 60 + mm;
    // 09:44 => 9*60 + 44 = 584; 12:59 => 12*60 + 59 = 779
    if (minutes <= (9 * 60 + 44)) return 'Mattina P.'; // <= 09:44
    if (minutes >= (9 * 60 + 45) && minutes <= (12 * 60 + 59)) return 'Intermezzo P.'; // 09:45 - 12:59
    if (minutes >= 13 * 60) return 'Pomeriggio P.'; // >= 13:00
    return null;
  };

  // Restituisce l'etichetta da visualizzare (gestisce anche Personalizzato -> etichette P.)
  const getDisplayTurnoLabel = (orario: TurnoCreato): string => {
    if (isRiposoPlaceholder(orario)) {
      // Baseline: nessun chip/etichetta visibile
      return '';
    }

    const turnoRaw = (orario.turno || '').trim();
    const turnoUpper = turnoRaw.toUpperCase();
    if (turnoUpper === 'ASSENZA') return 'Assenza';
    if (turnoUpper === 'RIPOSO') return 'Riposo';
    if (turnoUpper === 'PERSONALIZZATO') {
      const derived = derivePersonalizzatoLabel(orario.oraPrevInizio);
      return derived || 'Personalizzato';
    }
    return turnoRaw || 'Riposo';
  };

  const formatOrario = (oraInizio: string | null, oraFine: string | null) => {
    if (!oraInizio || !oraFine) {
      return '- '; // Per i riposi reali mostra "- "
    }
    return `${oraInizio.substring(0, 5)} - ${oraFine.substring(0, 5)}`;
  };

  // Funzione per formattare il tooltip con gli orari previsti del turno
  const formatTooltipOrariPrevisti = (orario: TurnoCreato) => {
    // Per tutte le assenze, non mostrare le ore nel tooltip
    if ((orario.turno || '').toUpperCase() === 'ASSENZA') {
      return ''; // niente dettagli entrata/uscita per le assenze
    }
    const entrata = orario.oraPrevInizio ? orario.oraPrevInizio.substring(0, 5) : '-';
    const uscita = orario.oraPrevFine ? orario.oraPrevFine.substring(0, 5) : '-';
    return `Entrata: ${entrata}\nUscita: ${uscita}`;
  };

  if ((loading || negoziLoading) && negoziAccessibili.length === 0) {
    return (
      <Container>
        <Typography>Caricamento...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
  <LockBanner />
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <CalendarIcon sx={{ mr: 1, fontSize: 32, color: PAGE_COLOR }} />
          <Typography variant="h4" component="h1">
            Generazione Orari
          </Typography>
          {selectedMese && selectedAnno && (
            <Typography variant="h6" sx={{ ml: 2, color: 'text.secondary' }}>
              - {mesi[selectedMese - 1]} {selectedAnno}
            </Typography>
          )}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            {/* Bottone visibilità mese - solo Admin/Coordinatore */}
            {(() => {
              // Sostituzione bottone MOSTRA/NASCONDI MESE con uno switch "Mese visibile"
              const u = authService.getCurrentUser();
              const isPriv = u && (u.ruolo === 'Admin' || u.ruolo === 'Coordinatore');
              const canShow = isPriv && !!selectedNegozio && !!selectedMese && !!selectedAnno && meseVisibile !== null;
              if (!canShow) return null;
              return (
                <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  {/* Checkbox Avvisi, visibile solo ad Admin/Coordinatore */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={avvisiEnabled}
                        onChange={(e) => setAvvisiEnabled(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Avvisi"
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">Mese Nascosto</Typography>
                  <Switch
                    checked={!!meseVisibile}
                    onChange={handleToggleVisibilita}
                    disabled={visibilitaBusy}
                    color="primary"
                    inputProps={{ 'aria-label': 'Visibilità mese' }}
                  />
                  <Typography variant="body2" color="text.secondary">Mese Visibile</Typography>
                </Box>
              );
            })()}

            {(() => {
              // Visibile ora anche ai Coordinatori oltre che agli Admin
              const u = authService.getCurrentUser();
              if (!(u && (u.ruolo === 'Admin' || u.ruolo === 'Coordinatore'))) return null;
              return (
                <Button
                  variant="contained"
                  startIcon={<GroupAddIcon />}
                  onClick={() => navigate('/quantita-personale')}
                  sx={{
                    height: '56px',
                    bgcolor: '#b052c0',
                    color: 'white',
                    '&:hover': { bgcolor: '#b052c0' }
                  }}
                >
                  Gestione Quantità Personale
                </Button>
              );
            })()}
          </Box>
        </Box>

        {/* Spazio riservato al banner: la matrice non si sposta alla comparsa del successo. */}
        <Box sx={{ minHeight: 72 }}>
          {error ? (
            <ErrorBanner message={error} onRetry={() => {
              if (negoziError) refetchNegozi();
              if (dipError) refetchDip();
              refetchTurni();
              refetchStats();
            }} />
          ) : success && (
            <>
              {(isAdmin || activeGenerationWarnings.length === 0) && (
                <Alert severity={activeGenerationWarnings.length > 0 ? "warning" : "success"} sx={{ mb: weeklyRestDiagnostics.some(d => d.generated === 0 && d.capacityForRests > 0) ? 1 : 3 }} onClose={() => setSuccess(null)}>
                  {success}
                </Alert>
              )}
              {activeGenerationWarnings.length > 0 && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  Generazione parziale: alcuni turni non sono stati assegnati.
                  {isAdmin && (
                    <>
                      <div>Dettaglio dei turni non generati:</div>
                      <ul>
                        {activeGenerationWarnings.map((w, index) => (
                          <li key={`${w.data}-${index}`}>
                            {dayjs(w.data).format('DD/MM/YYYY')}
                            {w.dipendente ? ` — ${w.dipendente}` : ''}
                            {w.turno ? ` — ${w.turno}` : ''}: {w.reason === 'CAPACITY_INSUFFICIENT'
                              ? 'Personale disponibile insufficiente' : w.reason}
                            {w.required > 0 ? ` (${w.eligible}/${w.required})` : ''}
                          </li>
                        ))}
                      </ul>
                      {weeklyRestDiagnostics.some(d => d.generated === 0 && d.capacityForRests > 0) && (
                        <div>Riposi non assegnati: {weeklyRestDiagnostics
                          .filter(d => d.generated === 0 && d.capacityForRests > 0)
                          .map(d => `${dayjs(d.data).format('DD/MM/YYYY')}: ${formatWeeklyRestExclusions(d.exclusions) || 'motivo non specificato'}`)
                          .join('; ')}.</div>
                      )}
                    </>
                  )}
                </Alert>
              )}
            </>
          )}
        </Box>

        {isCoordinatoreLocked && lockTargetsAggiornati.length > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {(() => {
              const allTargets = lockTargetsAggiornati;
              if (allTargets.length === 1) {
                const t = allTargets[0];
                return `Operativita limitata: devi sistemare le ore negative di ${t.fullName || `${t.nome} ${t.cognome}`.trim()} (negozio ${t.negozio}, differenza: ${formatDecimalHours(t.differenza)}). Puoi navigare i tuoi negozi normalmente.`;
              }
              const nomi = allTargets.map(t => t.fullName || `${t.nome} ${t.cognome}`.trim()).join(', ');
              return `Operativita limitata: ${allTargets.length} dipendenti con ore negative: ${nomi}. Puoi navigare tra le loro schede normalmente.`;
            })()}
          </Alert>
        )}

        {/* Filtri */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
          <Box sx={{ minWidth: 250, flex: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Negozio</InputLabel>
              <Select
                value={selectedNegozio}
                label="Negozio"
                onChange={(e) => setSelectedNegozio(e.target.value)}
              >
                {negoziAccessibili.map((negozio) => (
                  <MenuItem key={negozio.neg} value={negozio.neg}>
                    {negozio.negozio} ({negozio.neg})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ minWidth: 150, flex: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Mese</InputLabel>
              <Select
                value={selectedMese}
                label="Mese"
                onChange={(e) => setSelectedMese(Number(e.target.value))}
              >
                {mesi.map((mese, index) => (
                  <MenuItem key={index} value={index + 1}>
                    {mese}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ minWidth: 150, flex: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Anno</InputLabel>
              <Select
                value={selectedAnno}
                label="Anno"
                onChange={(e) => setSelectedAnno(Number(e.target.value))}
              >
                {anni.map((anno) => (
                  <MenuItem key={anno} value={anno}>
                    {anno}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          

          <Box sx={{ minWidth: 220 }}>
            <Button
              variant="contained"
              onClick={handleOpenStepMenu}
              disabled={loading || !selectedNegozio || !selectedMese || !selectedAnno}
              startIcon={<CalendarIcon />}
              sx={{ height: '56px', width: '100%' }}
            >
              GENERAZIONE A STEP
            </Button>
            <Menu
              anchorEl={stepMenuAnchor}
              open={openStepMenu}
              onClose={handleCloseStepMenu}
            >
              {isAdmin && (
                <>
                  <MenuItem onClick={() => { handleCloseStepMenu(); generaOrariAutomatici(); }}>GENERA COMPLETO</MenuItem>
                  <Divider />
                </>
              )}
              <MenuItem onClick={() => { handleCloseStepMenu(); handleSelectGenerazioneStep('domeniche'); }}>DOMENICHE</MenuItem>
              <MenuItem onClick={() => { handleCloseStepMenu(); handleSelectGenerazioneStep('sabati'); }}>SABATI</MenuItem>
              <MenuItem onClick={() => { handleCloseStepMenu(); handleSelectGenerazioneStep('riposi'); }}>RIPOSI SETTIMANALI</MenuItem>
              <MenuItem onClick={() => { handleCloseStepMenu(); handleSelectGenerazioneStep('resto'); }}>RESTO SETTIMANA</MenuItem>
            </Menu>
          </Box>

          {/* Rimosso il pulsante Aggiorna su richiesta */}

          <Box sx={{ minWidth: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Tooltip title="Esporta (Screenshot / Pdf)">
              <span>
                <IconButton
                  onClick={handleOpenPdfMenu}
                  disabled={exporting || !selectedNegozio || !selectedMese || !selectedAnno || matriceOrari.length === 0}
                  color="default"
                  size="large"
                  sx={{
                    height: '56px',
                    width: '56px',
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <PictureAsPdfIcon color="error" />
                </IconButton>
              </span>
            </Tooltip>
            <Menu anchorEl={pdfMenuAnchor} open={openPdfMenu} onClose={handleClosePdfMenu}>
              <MenuItem disabled={exporting} onClick={() => { handleClosePdfMenu(); handleExportScreenshotPdf(); }}>Screenshot</MenuItem>
              <MenuItem disabled={exporting} onClick={() => { handleClosePdfMenu(); handleExportPdf(); }}>Pdf</MenuItem>
            </Menu>
          </Box>
          {/* Toggle Vista Estesa */}
          <Box sx={{ minWidth: 120 }}>
            <Button
              variant={esteso ? 'contained' : 'outlined'}
              onClick={() => {
                if (matrixScrollRef.current) {
                  lastScrollLeftRef.current = matrixScrollRef.current.scrollLeft || 0;
                }
                setEsteso((v) => !v);
              }}
              sx={{ 
                height: '56px', 
                width: '100%',
                bgcolor: esteso ? PAGE_COLOR : 'transparent',
                color: esteso ? 'white' : PAGE_COLOR,
                borderColor: PAGE_COLOR,
                '&:hover': {
                  bgcolor: esteso ? '#455A64' : 'action.hover',
                  borderColor: PAGE_COLOR
                }
              }}
            >
              Esteso
            </Button>
          </Box>
          
          <Box sx={{ minWidth: 150, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              variant="outlined"
              color="error"
              onClick={handleCancellaOrari}
              disabled={loading || !selectedNegozio || !selectedMese || !selectedAnno}
              startIcon={<DeleteIcon />}
              sx={{ height: '56px', width: '100%' }}
            >
              Cancella
            </Button>
            {isAdmin && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleCancellaDuplicati}
                disabled={loading || !selectedNegozio || !selectedMese || !selectedAnno}
                startIcon={<DeleteIcon />}
                sx={{ minHeight: '40px', width: '100%' }}
              >
                Cancella duplicati
              </Button>
            )}
          </Box>
        </Box>

        {/* Nessuna toolbar visiva: le azioni multi-selezione vengono ora fornite via menu contestuale */}

        {/* Statistiche visibili solo agli Admin */}
        {(() => { const u = authService.getCurrentUser(); if (!(u && u.ruolo === 'Admin')) return null; return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Card sx={{ minWidth: 200 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6">{dipendenti.length}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Dipendenti Totali
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ); })()}

        {/* Tabella Orari */}
        {selectedNegozio && selectedMese && selectedAnno && (
          <Paper sx={{ mt: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: PAGE_COLOR, color: 'white', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <CalendarIcon sx={{ mr: 1 }} />
                {(() => {
                  const visLabel = meseVisibile === null ? '' : ` - ${meseVisibile ? 'Visibile' : 'Nascosto'}`;
                  if (esteso && dominantView) {
                    const mIdx = dominantView.month - 1;
                    const y = dominantView.year;
                    return `Calendario Orari - ${mesi[mIdx]} ${y}${visLabel}`;
                  }
                  return `Calendario Orari - ${mesi[selectedMese - 1]} ${selectedAnno}${visLabel}`;
                })()}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={fitMonth}
                    onChange={(e) => {
                      const v = e.target.checked;
                      // Non disattivare più Esteso quando si attiva "Mese intero"
                      setFitMonth(v);
                    }}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: 'white' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'white' }
                    }}
                  />
                }
                label="Mese intero"
                sx={{ color: 'white' }}
              />
            </Box>
            
            {turniLoading || dipLoading ? (
              <InlineLoader rows={6} />
            ) : matriceOrari.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  Nessun orario generato per {mesi[selectedMese - 1]} {selectedAnno}.
                  <br />
                  Clicca su "GENERAZIONE A STEP" e poi "GENERA COMPLETO" per creare automaticamente gli orari del mese.
                </Typography>
              </Box>
            ) : (
              <Box
                ref={matrixScrollRef}
                sx={{
                  overflowX: (!fitMonth || (fitMonth && esteso)) ? 'auto' : 'hidden',
                  overflowY: 'hidden',
                  overscrollBehavior: 'contain', // evita scroll chaining verso il body
                  touchAction: (!fitMonth || esteso) ? 'pan-x' : 'auto', // abilita pan orizzontale quando serve
                  userSelect: 'none' // disabilita selezione testo/copia nativa del browser nella matrice
                }}
                onWheel={!fitMonth || esteso ? handleMatrixWheel : undefined}
              >
                <Table stickyHeader sx={{ tableLayout: fitMonth ? 'fixed' : 'auto' }}>
                  <TableHead>
                    <TableRow>
                      {!screenshotMode && (
                      <TableCell 
                        ref={attivoHeaderRef}
                        sx={{ 
                          width: 80, 
                          bgcolor: 'grey.100', 
                          fontWeight: 'bold',
                          position: 'sticky',
                          left: 0,
                          zIndex: 10,
                          borderBottom: '3px solid rgba(224, 224, 224, 1)'
                        }}
                      >
                        Attivo
                      </TableCell>
                      )}
                      {!screenshotMode && (
                      <TableCell
                        ref={neoHeaderRef}
                        sx={{
                          width: NEOASSUNTA_COL_WIDTH,
                          minWidth: NEOASSUNTA_COL_WIDTH,
                          maxWidth: NEOASSUNTA_COL_WIDTH,
                          bgcolor: 'grey.100',
                          fontWeight: 'bold',
                          position: 'sticky',
                          left: 80,
                          zIndex: 10,
                          borderBottom: '3px solid rgba(224, 224, 224, 1)'
                        }}
                      >
                        Neoassunta
                      </TableCell>
                      )}
                      <TableCell 
                        ref={dipHeaderRef}
                        sx={{ 
                          minWidth: fitMonth ? 140 : 150, 
                          width: fitMonth ? 140 : undefined,
                          bgcolor: 'grey.100', 
                          fontWeight: 'bold',
                          position: 'sticky',
                          left: screenshotMode ? 0 : 80 + NEOASSUNTA_COL_WIDTH,
                          zIndex: 10,
                          borderBottom: '3px solid rgba(224, 224, 224, 1)',
                          whiteSpace: fitMonth ? 'nowrap' : undefined,
                          overflow: fitMonth ? 'hidden' : undefined,
                          textOverflow: fitMonth ? 'ellipsis' : undefined,
                          p: fitMonth ? 0 : undefined
                        }}
                        onContextMenu={handleOpenGlobalMenu}
                      >
                        Dipendente
                      </TableCell>
                      {(() => {
                        const headers: React.ReactNode[] = [];
                        const monthIndex = selectedMese ? selectedMese - 1 : -1;
                        const today = dayjs();
                        allGridDays.forEach((d, idx) => {
                          const isCurrMonth = d.month() === monthIndex && d.year() === selectedAnno;
                          const isWeekend = d.day() === 0 || d.day() === 6;
                          const dayNum = d.date();
                          const dayName = d.format('ddd');
                          const warnDay = isWarningDay(d);
                          const isToday = d.isSame(today, 'day');
                          // Evidenzia se almeno una riga ha annotazioni speciali quel giorno
                          const hasSpecialAnnot = (() => {
                            const labels = ['inventario','ricartellinamento','giro'];
                            const dateKey = d.format('YYYY-MM-DD');
                            return (annotazioniGiorno[dateKey] || []).some(l => labels.includes(l.toLowerCase()));
                          })();
                          headers.push(
                            <TableCell
                              key={`hdr-${d.format('YYYY-MM-DD')}-${idx}`}
                              title={activeGenerationWarnings.filter(w => w.data === d.format('YYYY-MM-DD')).map(w => w.reason).join('; ')}
                              sx={{
                                minWidth: fitMonth && dayColWidth ? `${dayColWidth}px` : (fitMonth ? undefined : 120),
                                maxWidth: fitMonth && dayColWidth ? `${dayColWidth}px` : undefined,
                                width: fitMonth && dayColWidth ? `${dayColWidth}px` : undefined,
                                textAlign: 'center',
                                bgcolor: warnDay ? 'rgba(244,67,54,0.2)' : (hasSpecialAnnot ? 'rgba(76,175,80,0.25)' : (isCurrMonth ? (isWeekend ? 'grey.200' : 'white') : 'grey.300')),
                                color: isCurrMonth ? 'inherit' : 'grey.700',
                                fontWeight: 'bold',
                                borderLeft: isToday ? '3px solid #4caf5080' : (idx === 0 ? '2px solid #e0e0e0' : '1px solid #e0e0e0'),
                                borderRight: isToday ? '3px solid #4caf5080' : '1px solid #e0e0e0',
                                borderTop: isToday ? '3px solid #4caf5080' : '1px solid #e0e0e0',
                                borderBottom: '3px solid rgba(224, 224, 224, 1)',
                                p: fitMonth ? 0.5 : 1,
                                boxSizing: 'border-box'
                              }}
                              onContextMenu={(e) => handleOpenDayMenu(e, dayNum, d.format('YYYY-MM-DD'))}
                            >
                              <Box>
                                {fitMonth ? (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
                                    <Typography variant="caption" fontWeight="bold" sx={{ lineHeight: 1 }}>
                                      {dayNum}
                                    </Typography>
                                    <Typography variant="caption" sx={{ lineHeight: 1, fontWeight: 400 }}>
                                      {dayName.toLowerCase()}
                                    </Typography>
                                    {esteso && !isCurrMonth && (
                                      <Typography variant="caption" sx={{ lineHeight: 1, fontWeight: 400, fontSize: '0.6rem', opacity: 0.7 }}>
                                        {d.format('MMM').toLowerCase()}
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (
                                  <>
                                    <Typography variant="body2" fontWeight="bold">{dayNum}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>{dayName}</Typography>
                                    {esteso && !isCurrMonth && (
                                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1, fontSize: '0.7rem', opacity: 0.7 }}>
                                        {d.format('MMM').toLowerCase()}
                                      </Typography>
                                    )}
                                  </>
                                )}
                              </Box>
                            </TableCell>
                          );
                        });
                        return headers;
                      })()}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const righe = screenshotMode
                        ? sortedMatriceOrari.filter(r => {
                            // Escludi non attivi
                            const attivo = dipendentiAttivi[r.dipendente] !== false;
                            if (!attivo) return false;
                            // Escludi maternità (overlap con mese corrente)
                            const monthIndex = selectedMese ? selectedMese - 1 : -1;
                            const mStart = dayjs().year(selectedAnno).month(monthIndex).date(1);
                            const mEnd = mStart.endOf('month');
                            const maternita = (periodiMaternita[r.dipendente] || []).some(p => dayjs(p.start).isBefore(mEnd.add(1,'day')) && dayjs(p.end).isAfter(mStart.subtract(1,'day')));
                            if (maternita) return false;
                            // Deve avere almeno un turno nel mese corrente
                            const daysInMonth = mStart.daysInMonth();
                            for (let d = 1; d <= daysInMonth; d++) {
                              const arr = r.orari[d]; if (Array.isArray(arr) && arr.length > 0) return true;
                            }
                            return false;
                          })
                        : sortedMatriceOrari.filter(r => dipendentiAttivi[r.dipendente] !== false);
                      const totalRows = righe.length;
                      return righe.map((riga, index) => {
                        const isLastRow = index === totalRows - 1;
                        return (
                      <TableRow key={index}>
                        {!screenshotMode && (
                        <TableCell 
                          sx={{ 
                            width: 80,
                            minWidth: 80,
                            maxWidth: 80,
                            textAlign: 'center',
                            bgcolor: 'grey.50',
                            position: 'sticky',
                            left: 0,
                            zIndex: 5,
                            p: fitMonth ? 0 : undefined
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: fitMonth ? 40 : 'auto', justifyContent: 'center' }}>
                            <Checkbox
                              checked={dipendentiAttivi[riga.dipendente] !== false}
                              onChange={(e) => handleToggleDipendenteAttivo(riga.dipendente)}
                              disabled={!!periodiMaternita[riga.dipendente]}
                              size="small"
                            />
                            {isDipendenteTemporaneo(riga.dipendente) && (
                              <DeleteIcon 
                                sx={{ 
                                  color: '#f44336', 
                                  cursor: 'pointer',
                                  fontSize: '1.2rem'
                                }}
                                onClick={() => handleRimuoviDipendente(riga.dipendente)}
                              />
                            )}
                            {isDipendenteMomentaneo(riga.dipendente) && (
                              <DeleteIcon
                                sx={{
                                  color: '#f44336',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem'
                                }}
                                onClick={() => handleRimuoviDipendenteMomentaneo(riga.dipendente)}
                              />
                            )}
                          </Box>
                        </TableCell>
                        )}
                        {!screenshotMode && (
                        <TableCell
                          sx={{
                            width: NEOASSUNTA_COL_WIDTH,
                            minWidth: NEOASSUNTA_COL_WIDTH,
                            maxWidth: NEOASSUNTA_COL_WIDTH,
                            textAlign: 'center',
                            bgcolor: 'grey.50',
                            position: 'sticky',
                            left: 80,
                            zIndex: 5,
                            p: fitMonth ? 0 : undefined
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: fitMonth ? 40 : 'auto' }}>
                            <Tooltip title={canEditNeoassunta ? 'Escludi dal conteggio personale e mostra ●' : 'Visualizzazione (permessi insufficienti)'}>
                              <span>
                                <Checkbox
                                  checked={isNuovoAssunto(riga.dipendente)}
                                  onChange={(e) => setNeoassuntaOverride(prev => ({ ...prev, [riga.dipendente]: e.target.checked }))}
                                  size="small"
                                  disabled={!canEditNeoassunta}
                                />
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        )}
                        <TableCell 
                          sx={{ 
                            fontWeight: 'bold',
                            bgcolor: 'grey.50',
                            position: 'sticky',
                            left: screenshotMode ? 0 : 80 + NEOASSUNTA_COL_WIDTH,
                            zIndex: 5,
                            p: fitMonth ? 0 : undefined,
                            minWidth: fitMonth ? 140 : undefined,
                            width: fitMonth ? 140 : undefined
                          }}
                          onContextMenu={(e) => handleOpenDipMenu(e, riga.dipendente)}
                        >
                          <Box sx={{ display: 'flex', alignItems: fitMonth ? 'center' : 'center', gap: 1, height: fitMonth ? 40 : 'auto' }}>
                            {fitMonth ? (
                              (() => {
                                const parts = (riga.dipendente || '').trim().split(' ').filter(Boolean);
                                // Logica divisione:
                                // 4 parti: "anna maria rossi bianchi" -> primaRiga="anna maria", secondaRiga="rossi bianchi"
                                // 3 parti: "anna maria filippi" -> primaRiga="anna maria", secondaRiga="filippi"
                                // 2 parti: "anna filippi" -> primaRiga="anna", secondaRiga="filippi"
                                // 1 parte: tutto sulla prima riga
                                let primaRiga = '';
                                let secondaRiga = '';
                                if (parts.length >= 4) {
                                  // Es: "anna maria rossi bianchi" -> primaRiga="anna maria", secondaRiga="rossi bianchi"
                                  const meta = Math.ceil(parts.length / 2);
                                  primaRiga = parts.slice(0, meta).join(' ');
                                  secondaRiga = parts.slice(meta).join(' ');
                                } else if (parts.length === 3) {
                                  // Es: "anna maria filippi" -> primaRiga="anna maria", secondaRiga="filippi"
                                  primaRiga = parts.slice(0, 2).join(' ');
                                  secondaRiga = parts[2];
                                } else if (parts.length === 2) {
                                  // Es: "anna filippi" -> primaRiga="anna", secondaRiga="filippi"
                                  primaRiga = parts[0];
                                  secondaRiga = parts[1];
                                } else {
                                  primaRiga = parts[0] || riga.dipendente || '';
                                }
                                const prefisso = isNuovoAssunto(riga.dipendente) ? '● ' : '';
                                const isTemp = isDipendenteTemporaneo(riga.dipendente);
                                const isMom = isDipendenteMomentaneo(riga.dipendente);
                                const dipColor = getDipendenteColor(riga.dipendente);
                                return (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: 36 }}>
                                    <Box sx={{ width: 115, minWidth: 115, maxWidth: 115, overflow: 'hidden', height: 36, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0 }}>
                                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.70rem', lineHeight: 1.15, fontWeight: (isTemp || isMom) ? 700 : 400, color: dipColor, whiteSpace: 'nowrap' }}>
                                        {prefisso}{primaRiga}
                                      </Typography>
                                      {secondaRiga && (
                                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.70rem', lineHeight: 1.15, fontWeight: (isTemp || isMom) ? 700 : 400, color: dipColor, whiteSpace: 'nowrap' }}>
                                          {secondaRiga}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Tooltip title="Vai a Orario Dipendente">
                                      <IconButton size="small" onClick={() => handleOpenOrarioDipendente(riga.dipendente)} sx={{ p: 0.25 }}>
                                        <AccessTimeIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                );
                              })()
                            ) : (
                              <>
                                <span style={{ 
                                  color: getDipendenteColor(riga.dipendente),
                                  fontWeight: (isDipendenteTemporaneo(riga.dipendente) || isDipendenteMomentaneo(riga.dipendente)) ? 'bold' : 'normal'
                                }}>
                                  {isNuovoAssunto(riga.dipendente) ? '● ' : ''}{riga.dipendente}
                                </span>
                                {isDipendenteTemporaneo(riga.dipendente) && (
                                  <Chip 
                                    label="TEMP" 
                                    size="small" 
                                    color="warning" 
                                    variant="outlined"
                                    sx={{ fontSize: '0.6rem', height: 16 }}
                                  />
                                )}
                                {isDipendenteMomentaneo(riga.dipendente) && (
                                  <Chip
                                    label="MOM"
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.6rem', height: 16, color: '#9C27B0', borderColor: '#9C27B0' }}
                                  />
                                )}
                                <Tooltip title="Vai a Orario Dipendente">
                                  <IconButton size="small" onClick={() => handleOpenOrarioDipendente(riga.dipendente)}>
                                    <AccessTimeIcon fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                        {(() => {
                          const cells: React.ReactNode[] = [];
                          const monthIndex = selectedMese ? selectedMese - 1 : -1;
                          const today = dayjs();
                          allGridDays.forEach((d, idx) => {
                            const isCurrMonth = d.month() === monthIndex && d.year() === selectedAnno;
                            const isPrevMonth = d.isBefore(dayjs().year(selectedAnno).month(monthIndex).date(1), 'day');
                            const dayNum = d.date();
                            const dateIso = d.format('YYYY-MM-DD');
                            // Nei layout Estesa e Mese intero anche le celle di contorno
                            // appartengono alla matrice e consentono lo scambio verticale.
                            const canVerticalDrag = isCurrMonth || esteso || fitMonth;
                            const isWeekend = d.day() === 0 || d.day() === 6;
                            const isToday = d.isSame(today, 'day');
                            const orari = isCurrMonth
                              ? (riga.orari[dayNum] || [])
                              : (isPrevMonth
                                ? (riga.orariPrev?.[dayNum] || [])
                                : (riga.orariNext?.[dayNum] || []));
                            const hasOrari = !!(orari && orari.length > 0);
                            
                            // Funzione per renderizzare il contenuto della cella
                            const renderCellContent = () => {
            if (!orari || orari.length === 0) {
                                return (
                                  <Box 
                                    sx={{ 
                                      height: `${TURN_CELL_HEIGHT}px`, 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      justifyContent: 'center',
                                      border: 'none',
                                      borderRadius: 1,
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <Typography variant="caption" color="text.disabled">
                                      -
                                    </Typography>
                                  </Box>
                                );
                              }
                              
                              if (orari.length === 1) {
                                // Singolo turno - rendering come prima
                                const orario = orari[0];
                                const isTurnoEst = isTurnoEsterno(orario);
                                if (fitMonth) {
                                  const base = getDisplayTurnoLabel(orario);
                                  // Baseline: nessun contenuto in modalità "mese intero"
                                  if (!base) return null;
                                  const { bg, fg } = getBadgePalette(base);
                                  // Per le assenze, mostra il CODICE_TIPO_ORARIO nel tooltip invece di "Assenza" (solo per Coordinatore/Admin)
                                  const tooltipLabel = isPrivileged && (orario.turno || '').toUpperCase() === 'ASSENZA' && orario.codiceTipoOrario ? orario.codiceTipoOrario : base;
                                  const orariPrevisti = formatTooltipOrariPrevisti(orario);
                                  const tooltip = `${tooltipLabel}${orariPrevisti ? `\n${orariPrevisti}` : ''}${isTurnoEst ? `\nNegozio: ${orario.neg}` : ''}`;
                                  // Mostra il negozio del turno (da turni_creati) solo se diverso dal negozio corrente.
                                  const negDaMostrare = isTurnoEst ? orario.neg : undefined;
                                  // Modalità screenshot: 3 righe (iniziale / ora inizio / ora fine)
                                  if (screenshotMode) {
                                    const startHHMM = orario.oraPrevInizio ? orario.oraPrevInizio.substring(0,5) : '';
                                    const endHHMM = orario.oraPrevFine ? orario.oraPrevFine.substring(0,5) : '';
                                    const isPreferenza = isPreferenzaTurno(orario);
                                    return (
                                      <Box
                                        title={tooltip}
                                        sx={{ width: '100%', height: `${TURN_CELL_HEIGHT}px`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.25, cursor: (orario.blocco || isTurnoEst) ? 'default' : 'pointer', opacity: orario.blocco ? 0.9 : 1, position: 'relative' }}
                                        draggable={canVerticalDrag && !orario.blocco && !isTurnoEst}
                                        onDragStart={(!canVerticalDrag || orario.blocco || isTurnoEst) ? undefined : (e) => handleDragStart(e, riga.dipendente, dateIso, orario)}
                                        onDragEnd={(!canVerticalDrag || orario.blocco || isTurnoEst) ? undefined : handleDragEnd}
                                        onPointerDown={isTurnoEst ? undefined : (e) => { e.stopPropagation(); handleTurnoPointerDown(e as any, riga.dipendente, dayNum, orario, idx); }}
                                        onPointerMove={isTurnoEst ? undefined : (e) => { e.stopPropagation(); handleTurnoPointerMove(e as any, riga.dipendente, idx); }}
                                        onPointerUp={isTurnoEst ? undefined : (e) => { e.stopPropagation(); handleTurnoPointerUp(riga.dipendente, idx); }}
                                        onPointerLeave={isTurnoEst ? undefined : (e) => { e.stopPropagation(); handleTurnoPointerLeave(); }}
                                      >
                                        <Box sx={{ width: '90%', height: 12, bgcolor: bg, color: fg, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.7rem', lineHeight: '12px' }}>
                                          {getTurnoInitial(base)}
                                        </Box>
                                        <Typography variant="caption" sx={{ fontSize: '0.55rem', lineHeight: 1 }}>
                                          {startHHMM}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontSize: '0.55rem', lineHeight: 1 }}>
                                          {endHHMM}
                                        </Typography>
                                        {orario.blocco && !isPreferenza ? (
                                          <LockIcon sx={{ position: 'absolute', top: -2, left: '50%', transform: 'translateX(-50%)', fontSize: '12px', color: 'rgba(0,0,0,0.54)', pointerEvents: 'none' }} />
                                        ) : null}
                                        {isPreferenza && (
                                          <FavoriteIcon sx={{ position: 'absolute', top: -2, left: '50%', transform: 'translateX(-50%)', fontSize: '12px', color: '#e91e63', pointerEvents: 'none' }} />
                                        )}
                                        {negDaMostrare && (
                                          <Typography variant="caption" sx={{ fontSize: '0.55rem', lineHeight: 1, color: '#FF9800', fontWeight: 700 }}>
                                            {negDaMostrare}
                                          </Typography>
                                        )}
                                      </Box>
                                    );
                                  }
                                  return (
                                    <Box 
                                      title={tooltip} 
                                      sx={{ width: '100%', height: `${TURN_CELL_HEIGHT}px`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.25, cursor: (orario.blocco || isTurnoEst) ? 'default' : 'pointer', opacity: orario.blocco ? 0.9 : 1, position: 'relative' }}
                                      draggable={canVerticalDrag && !orario.blocco && !isTurnoEst}
                                      onDragStart={(!canVerticalDrag || orario.blocco || isTurnoEst) ? undefined : (e) => handleDragStart(e, riga.dipendente, dateIso, orario)}
                                      onDragEnd={(!canVerticalDrag || orario.blocco || isTurnoEst) ? undefined : handleDragEnd}
                                      onPointerDown={isTurnoEst ? undefined : (e) => { e.stopPropagation(); handleTurnoPointerDown(e as any, riga.dipendente, dayNum, orario, idx); }}
                                      onPointerMove={isTurnoEst ? undefined : (e) => { e.stopPropagation(); handleTurnoPointerMove(e as any, riga.dipendente, idx); }}
                                      onPointerUp={isTurnoEst ? undefined : (e) => { e.stopPropagation(); handleTurnoPointerUp(riga.dipendente, idx); }}
                                      onPointerLeave={isTurnoEst ? undefined : (e) => { e.stopPropagation(); handleTurnoPointerLeave(); }}
                                    >
                                      <Box sx={{ width: '90%', height: 12, bgcolor: bg, color: fg, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.7rem', lineHeight: '12px' }}>
                                        {getTurnoInitial(base)}
                                      </Box>
                                      {orario.blocco && !isPreferenzaTurno(orario) ? (
                                        <LockIcon sx={{ position: 'absolute', top: -2, left: '50%', transform: 'translateX(-50%)', fontSize: '12px', color: 'rgba(0,0,0,0.54)', pointerEvents: 'none' }} />
                                      ) : null}
                                      {isPreferenzaTurno(orario) && (
                                        <FavoriteIcon sx={{ position: 'absolute', top: -2, left: '50%', transform: 'translateX(-50%)', fontSize: '12px', color: '#e91e63', pointerEvents: 'none' }} />
                                      )}
                                      {negDaMostrare && (
                                        <Typography variant="caption" sx={{ fontSize: '0.55rem', lineHeight: 1, color: '#FF9800', fontWeight: 700 }}>
                                          {negDaMostrare}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                }
                                // Modalità screenshot: layout tre righe
                                if (screenshotMode) {
                                  const base = getDisplayTurnoLabel(orario);
                                  const { bg, fg } = getBadgePalette(base);
                                  const startHHMM = orario.oraPrevInizio ? orario.oraPrevInizio.substring(0,5) : '';
                                  const endHHMM = orario.oraPrevFine ? orario.oraPrevFine.substring(0,5) : '';
                                  const isPreferenza = isPreferenzaTurno(orario);
                                  return (
                                    <Box 
                                      title={formatTooltipOrariPrevisti(orario)}
                                      sx={{ 
                                        position: 'relative', 
                                        cursor: (orario.blocco || isTurnoEst) ? 'default' : 'pointer',
                                        opacity: orario.blocco ? 0.9 : 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: `${TURN_CELL_HEIGHT}px`
                                      }}
                                      draggable={canVerticalDrag && !orario.blocco && !isTurnoEst}
                                      onDragStart={(!canVerticalDrag || orario.blocco || isTurnoEst) ? undefined : (e) => handleDragStart(e, riga.dipendente, dateIso, orario)}
                                      onDragEnd={(!canVerticalDrag || orario.blocco || isTurnoEst) ? undefined : handleDragEnd}
                                      onPointerDown={isTurnoEst ? undefined : (e) => { e.stopPropagation(); handleTurnoPointerDown(e as any, riga.dipendente, dayNum, orario, idx); }}
                                      onPointerMove={isTurnoEst ? undefined : (e) => { e.stopPropagation(); handleTurnoPointerMove(e as any, riga.dipendente, idx); }}
                                      onPointerUp={isTurnoEst ? undefined : (e) => { e.stopPropagation(); handleTurnoPointerUp(riga.dipendente, idx); }}
                                      onPointerLeave={isTurnoEst ? undefined : (e) => { e.stopPropagation(); handleTurnoPointerLeave(); }}
                                    >
                                      <Box sx={{ width: '85%', height: 12, bgcolor: bg, color: fg, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.7rem', lineHeight: '12px' }}>
                                        {getTurnoInitial(base)}
                                      </Box>
                                      <Typography variant="caption" sx={{ fontSize: '0.6rem', lineHeight: 1 }}>
                                        {startHHMM}
                                      </Typography>
                                      <Typography variant="caption" sx={{ fontSize: '0.6rem', lineHeight: 1 }}>
                                        {endHHMM}
                                      </Typography>
                                      {orario.blocco && !isPreferenza ? (
                                        <LockIcon sx={{ position: 'absolute', top: 2, left: '50%', transform: 'translateX(-50%)', fontSize: '12px', color: 'rgba(0,0,0,0.54)', pointerEvents: 'none' }} />
                                      ) : null}
                                      {isPreferenza && (
                                        <FavoriteIcon sx={{ position: 'absolute', top: 2, left: '50%', transform: 'translateX(-50%)', fontSize: '12px', color: '#e91e63', pointerEvents: 'none' }} />
                                      )}
                                      {getDisplayTurnoLabel(orario) === 'Assenza' && isPrivileged && !shouldMaskAssenze && (
                                        <Typography variant="caption" display="block" sx={{ fontWeight: 700, color: 'error.main' }}>
                                          {orario.codiceTipoOrario || 'ASSENZA'}
                                        </Typography>
                                      )}
                                      {isTurnoEst && (
                                        <Typography 
                                          variant="caption" 
                                          display="block" 
                                          sx={{ 
                                            fontSize: '0.6rem', 
                                            color: '#FF9800',
                                            fontWeight: 'bold'
                                          }}
                                        >
                                          {orario.neg}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                }
                                return (
                                  <Box 
                                    title={formatTooltipOrariPrevisti(orario)}
                                    sx={{ 
                                      position: 'relative', 
                                      cursor: (orario.blocco || isTurnoEst) ? 'default' : 'pointer',
                                      opacity: orario.blocco ? 0.9 : 1,
                                      height: `${TURN_CELL_HEIGHT}px`,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    draggable={canVerticalDrag && !orario.blocco && !isTurnoEst}
                                    onDragStart={(!canVerticalDrag || orario.blocco || isTurnoEst) ? undefined : (e) => handleDragStart(e, riga.dipendente, dateIso, orario)}
                                    onDragEnd={(!canVerticalDrag || orario.blocco || isTurnoEst) ? undefined : handleDragEnd}
                                    onPointerDown={isTurnoEst ? undefined : (e) => { e.stopPropagation(); handleTurnoPointerDown(e as any, riga.dipendente, dayNum, orario, idx); }}
                                    onPointerMove={isTurnoEst ? undefined : (e) => { e.stopPropagation(); handleTurnoPointerMove(e as any, riga.dipendente, idx); }}
                                    onPointerUp={isTurnoEst ? undefined : (e) => { e.stopPropagation(); handleTurnoPointerUp(riga.dipendente, idx); }}
                                    onPointerLeave={isTurnoEst ? undefined : (e) => { e.stopPropagation(); handleTurnoPointerLeave(); }}
                                  >
                                    {(() => {
                                      // Baseline: nessun contenuto (cella completamente vuota visivamente)
                                      if (isBaselineTurno(orario)) return null;
                                      const base = getDisplayTurnoLabel(orario);
                                      if (!base) return null; // safety
                                      const { bg, fg } = getBadgePalette(base);
                                      const useCustom = isCustomTurnoPalette(base);
                                      const isPreferenza = isPreferenzaTurno(orario);
                                      return (
                                        <Chip
                                          label={base}
                                          color={useCustom ? 'default' : (getTurnoColor(base) as any)}
                                          size="small"
                                          icon={orario.blocco && !isPreferenza ? <LockIcon sx={{ fontSize: '12px' }} /> : (isPreferenza ? <FavoriteIcon sx={{ fontSize: '12px' }} /> : undefined)}
                                          sx={{ 
                                            mb: 0.5, 
                                            fontSize: '0.7rem',
                                            ...(useCustom && {
                                              backgroundColor: bg,
                                              color: fg,
                                              '&:hover': { backgroundColor: bg }
                                            }),
                                            ...(isPreferenza && {
                                              '& .MuiChip-icon': {
                                                color: '#e91e63 !important'
                                              }
                                            })
                                          }}
                                        />
                                      );
                                    })()}
                                    {(() => {
                                      if (isBaselineTurno(orario)) return null; // nessun '-' sotto cella baseline
                                      const hideCons = ((orario.turno || '').toUpperCase() === 'ASSENZA') && ['FERIE','MALATTIA'].includes((orario.codiceTipoOrario || '').toUpperCase());
                                      if (hideCons) return null;
                                      return (
                                        <Typography variant="caption" display="block">
                                          {formatOrario(orario.oraPrevInizio, orario.oraPrevFine)}
                                        </Typography>
                                      );
                                    })()}
                                    {getDisplayTurnoLabel(orario) === 'Assenza' && isPrivileged && !shouldMaskAssenze && (
                                      <Typography variant="caption" display="block" sx={{ fontWeight: 700, color: 'error.main' }}>
                                        {orario.codiceTipoOrario || 'ASSENZA'}
                                      </Typography>
                                    )}
                                    {isTurnoEst && (
                                      <Typography 
                                        variant="caption" 
                                        display="block" 
                                        sx={{ 
                                          fontSize: '0.65rem', 
                                          color: '#FF9800',
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        {orario.neg}
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              } else {
                                // Turni multipli - mostra solo i nomi dei turni con tooltip
                                const tooltipContent = orari.map((orario: TurnoCreato) => {
                                  const label = getDisplayTurnoLabel(orario);
                                  // Per le assenze, mostra il CODICE_TIPO_ORARIO nel tooltip invece di "Assenza" (solo per Coordinatore/Admin)
                                  const tooltipLabel = isPrivileged && (orario.turno || '').toUpperCase() === 'ASSENZA' && orario.codiceTipoOrario ? orario.codiceTipoOrario : label;
                                  const orariPrevisti = formatTooltipOrariPrevisti(orario);
                                  return orariPrevisti ? `${tooltipLabel}:\n${orariPrevisti}` : tooltipLabel;
                                }).join('\n\n');
                                
                                if (fitMonth) {
                                  // In modalità compatta crea strisce al 70% della larghezza per i primi due turni.
                                  const maxToShow = 2;
                                  const toShow = orari.slice(0, maxToShow);
                                  const remaining = orari.length - toShow.length;
                                  // Se esistono turni esterni, mostra il loro Neg (priorità al primo esterno); altrimenti non mostrare nulla.
                                  const externalNegs = orari.filter((o: TurnoCreato) => isTurnoEsterno(o)).map((o: TurnoCreato) => o.neg);
                                  const blockedAggregate = orari.some((o: TurnoCreato) => o && o.blocco);
                                  const hasPreferenza = orari.some((o: TurnoCreato) => isPreferenzaTurno(o));
                                  return (
                                    <Box 
                                      title={tooltipContent} 
                                      sx={{ width: '100%', height: `${TURN_CELL_HEIGHT}px`, position: 'relative', display: 'flex', flexDirection: 'column', gap: 0.125, py: 0, alignItems: 'center', justifyContent: 'center', cursor: blockedAggregate ? 'default' : 'pointer', opacity: blockedAggregate ? 0.9 : 1 }}
                                      draggable={canVerticalDrag && !blockedAggregate}
                                      onDragStart={(!canVerticalDrag || blockedAggregate) ? undefined : (e) => handleDragStart(e, riga.dipendente, dateIso, { blocco: blockedAggregate ? 1 : 0 })}
                                      onDragEnd={(!canVerticalDrag || blockedAggregate) ? undefined : handleDragEnd}
                                      onPointerDown={(e) => { e.stopPropagation(); handleTurnoPointerDown(e as any, riga.dipendente, dayNum, { blocco: blockedAggregate ? 1 : 0 }, idx); }}
                                      onPointerMove={(e) => { e.stopPropagation(); handleTurnoPointerMove(e as any, riga.dipendente, idx); }}
                                      onPointerUp={(e) => { e.stopPropagation(); handleTurnoPointerUp(riga.dipendente, idx); }}
                                      onPointerLeave={(e) => { e.stopPropagation(); handleTurnoPointerLeave(); }}
                                    >
                                      {toShow.map((orario: TurnoCreato, index: number) => {
                                        const base = getDisplayTurnoLabel(orario);
                                        const { bg, fg } = getBadgePalette(base);
                                        return (
                                          <React.Fragment key={`${orario.id}-${index}`}>
                                            <Box sx={{ width: '90%', height: 6, bgcolor: bg, color: fg, fontSize: '0.55rem', lineHeight: '6px', fontWeight: 700, textAlign: 'center', borderRadius: 2 }}>
                                              {getTurnoInitial(base)}
                                            </Box>
                                            <Typography variant="caption" sx={{ fontSize: '0.5rem', lineHeight: 1, color: 'text.primary', fontWeight: 700 }}>
                                              {orario.neg}
                                            </Typography>
                                          </React.Fragment>
                                        );
                                      })}
                                      {remaining > 0 && (
                                        <Typography variant="caption" sx={{ position: 'absolute', top: 0, right: '6%', fontSize: '0.55rem', fontWeight: 700, color: 'grey.700' }}>{`+${remaining}`}</Typography>
                                      )}
                                      {blockedAggregate && !hasPreferenza ? (
                                        <LockIcon sx={{ position: 'absolute', top: -2, left: '50%', transform: 'translateX(-50%)', fontSize: '12px', color: 'rgba(0,0,0,0.54)', pointerEvents: 'none' }} />
                                      ) : null}
                                      {hasPreferenza && (
                                        <FavoriteIcon sx={{ position: 'absolute', top: -2, left: '50%', transform: 'translateX(-50%)', fontSize: '12px', color: '#e91e63', pointerEvents: 'none' }} />
                                      )}
                                    </Box>
                                  );
                                }
                                // In vista non compatta: abilita la mano anche nello spazio tra i chip.
                                const blockedAggregate2 = orari.some((o: TurnoCreato) => o && o.blocco);
                                return (
                                  <Box 
                                    title={tooltipContent}
                                    sx={{ 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      gap: 0.25,
                                      alignItems: 'center',
                                      cursor: blockedAggregate2 ? 'default' : 'pointer'
                                    }}
                                  >
                                    {orari.map((orario: TurnoCreato, index: number) => {
                                      const labelBase = getDisplayTurnoLabel(orario);
                                      const isEsterno = isTurnoEsterno(orario);
                                      const labelAssenza = (labelBase === 'Assenza')
                                        ? (shouldMaskAssenze ? labelBase : (isPrivileged ? `${labelBase}${orario.codiceTipoOrario ? ` (${orario.codiceTipoOrario})` : ''}` : labelBase))
                                        : labelBase;
                                      const label = labelAssenza;
                                      const { bg, fg } = getBadgePalette(labelBase);
                                      const useCustom = isCustomTurnoPalette(labelBase);
                                      const isPreferenza = isPreferenzaTurno(orario);
                                      return (
                                        <React.Fragment key={`${orario.id}-${index}`}>
                                          <Chip
                                            label={label}
                                            title={`${formatTooltipOrariPrevisti(orario)}${isEsterno ? `\nNegozio: ${orario.neg}` : ''}`}
                                            color={useCustom ? 'default' : (getTurnoColor(labelBase) as any)}
                                            size="small"
                                            icon={orario.blocco && !isPreferenza ? <LockIcon sx={{ fontSize: '10px' }} /> : (isPreferenza ? <FavoriteIcon sx={{ fontSize: '10px' }} /> : undefined)}
                                            sx={{ 
                                              fontSize: '0.65rem',
                                              height: '20px',
                                              cursor: orario.blocco ? 'default' : 'pointer',
                                              ...(useCustom && {
                                                backgroundColor: bg,
                                                color: fg,
                                                '&:hover': { backgroundColor: bg }
                                              }),
                                              ...(isPreferenza && {
                                                '& .MuiChip-icon': {
                                                  color: '#e91e63 !important'
                                                }
                                              })
                                            }}
                                          />
                                          <Typography variant="caption" sx={{ fontSize: '0.6rem', lineHeight: 1, color: 'text.primary', fontWeight: 700 }}>
                                            {orario.neg}
                                          </Typography>
                                        </React.Fragment>
                                      );
                                    })}
                                  </Box>
                                );
                              }
                            };

                            cells.push(
                              <TableCell
                                key={`cell-${riga.dipendente}-${d.format('YYYY-MM-DD')}-${idx}`}
                                title={activeGenerationWarnings.filter(w => w.data === d.format('YYYY-MM-DD') && (!w.dipendente || w.dipendente === riga.dipendente)).map(w => w.reason).join('; ')}
                                ref={(element) => registerSelectionCell(riga.dipendente, idx, element as HTMLTableCellElement | null)}
                                sx={{
                                  textAlign: 'center',
                                  padding: '8px 4px',
                                  position: 'relative',
                                  bgcolor: (() => {
                                    const baseBg = isCurrMonth ? (isWeekend ? 'grey.100' : 'white') : 'grey.50';
                                    const cellDate = d.format('YYYY-MM-DD');
                                    const hasMaternita = (orari || []).some(o => (o.codiceTipoOrario || '').toUpperCase() === "MATERNITA'") ||
                                      (periodiMaternita[riga.dipendente] || []).some(r => cellDate >= r.start && cellDate <= r.end);
                                    if (hasMaternita) return '#ffc0cb80';
                                    const warnDay = isWarningDay(d);
                                    const incompleteCell = !(orari || []).some(o => !isBaselineTurno(o));
                                    const employeeWarning = activeGenerationWarnings.some(w =>
                                      w.data === cellDate && w.dipendente === riga.dipendente);
                                    if (incompleteCell && (employeeWarning || warnDay)
                                        && (dipendentiAttivi[riga.dipendente] ?? true)) return 'rgba(244,67,54,0.12)';
                                    return baseBg;
                                  })(),
                                  borderLeft: isToday ? '3px solid #4caf5080' : (idx === 0 ? '2px solid #e0e0e0' : '1px solid #e0e0e0'),
                                  borderRight: isToday ? '3px solid #4caf5080' : '1px solid #e0e0e0',
                                  borderTop: '1px solid #e0e0e0',
                                  borderBottom: (isToday && isLastRow) ? '3px solid #4caf5080' : '1px solid #e0e0e0',
                                  minWidth: fitMonth && dayColWidth ? `${dayColWidth}px` : (fitMonth ? undefined : 120),
                                  maxWidth: fitMonth && dayColWidth ? `${dayColWidth}px` : undefined,
                                  width: fitMonth && dayColWidth ? `${dayColWidth}px` : undefined,
                                  boxSizing: 'border-box',
                                  // Cursor default per la cella: il puntatore (hand) deve apparire SOLO sui chip turno.
                                  // Mantenendo 'default' qui, le aree vuote o fuori dal chip mostrano il cursore normale.
                                  cursor: 'default',
                                  '&:hover': {
                                    outline: '2px solid rgba(96,125,139,0.3)'
                                  },
                                  '&.turn-drag-over': {
                                    bgcolor: `${PAGE_COLOR}40`,
                                    borderTop: '2px dashed #1976d2',
                                    outline: '2px dashed #1976d2'
                                  },
                                  '&.row-selection-preview': {
                                    bgcolor: 'rgba(0,180,255,0.3)',
                                    outline: '2px solid rgba(0,180,255,0.5)'
                                  }
                                }}
                                onMouseDown={(e) => {
                                  if (e.button !== 0) return;
                                  const gridIdx = idx;
                                  // Avvia long press SOLO se il click è sul background della cella (non su chip / elementi interni)
                                  if (e.currentTarget === e.target) {
                                    startLongPress(riga.dipendente, gridIdx);
                                  }
                                  setBulkDragActive(false);
                                }}
                                onMouseEnter={() => {
                                  const preview = selectionPreviewRef.current;
                                  if (preview?.dipendente === riga.dipendente) {
                                    setSelectionPreview({ ...preview, endIdx: idx });
                                  }
                                }}
                                onMouseUp={() => {
                                  // Se long press attivo: termina selezione; altrimenti nessuna evidenziazione
                                  cancelLongPress();
                                  if (longPressActiveRef.current) {
                                    commitSelectionPreview();
                                  }
                                }}
                                onDragOver={(e) => {
                                  // Se drag massivo attivo e siamo su dipendente diverso: consenti drop
                                  if (bulkDragActive && rowSelection && rowSelection.dipendente !== riga.dipendente) {
                                    e.preventDefault();
                                    e.dataTransfer.dropEffect = 'move';
                                    return;
                                  }
                                  // In vista estesa consenti anche i mesi adiacenti.
                                  if (canVerticalDrag) handleDragOver(e as any, riga.dipendente, dateIso);
                                }}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => {
                                  // Drop drag massivo
                                  if (bulkDragActive && rowSelection && rowSelection.dipendente !== riga.dipendente) {
                                    e.preventDefault();
                                    executeBulkSwap(rowSelection.dipendente, riga.dipendente, Math.min(rowSelection.startIdx,rowSelection.endIdx), Math.max(rowSelection.startIdx,rowSelection.endIdx));
                                    setBulkDragActive(false);
                                    return;
                                  }
                                  // Drop turno singolo
                                  if (canVerticalDrag) handleDrop(e as any, riga.dipendente, dateIso);
                                }}
                                // Disabilita il drag nativo della cella (ma lascia funzionare il drag dei chip interni)
                                draggable={false}
                                onContextMenu={(e) => {
                                  // Se la cella contiene un turno esterno, passarlo come orario del contextMenu
                                  // in modo che handleAddOrUpdateTurno usi sempre il suo ID (non un baseline locale)
                                  const turnoEsternoCella = orari.find(t => isTurnoEsterno(t));
                                  const orarioContestuale = turnoEsternoCella ?? (orari.length > 0 ? orari[0] : null);
                                  handleContextMenu(e, riga.dipendente, dayNum, d.format('YYYY-MM-DD'), orarioContestuale);
                                }}
                              >
                                {/* Layer evidenzia selezione */}
                                {isCellSelected(riga.dipendente, idx) && (
                                  <Box sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    bgcolor: bulkDragActive ? 'rgba(0,120,255,0.35)' : 'rgba(0,180,255,0.3)', // colore diverso se drag massivo attivo
                                    pointerEvents: 'none'
                                  }} />
                                )}
                                {/* Overlay biberon per allattamento (IDMat=7) se non coperto da maternità */}
                                {renderCellContent()}
                                {(() => {
                                  const cellDate = d.format('YYYY-MM-DD');
                                  const inMaternita = (periodiMaternita[riga.dipendente] || []).some(r => cellDate >= r.start && cellDate <= r.end);
                                  if (inMaternita) return null; // priorità maternità
                                  const inAllattamento = (periodiAllattamento[riga.dipendente] || []).some(r => cellDate >= r.start && cellDate <= r.end);
                                  if (!inAllattamento) return null;
                                  return (
                                    <Box sx={{ position: 'absolute', top: 2, left: 2, zIndex: 1, pointerEvents: 'none' }}>
                                      <Tooltip title="Allattamento">
                                        <Typography component="span" sx={{ fontSize: '0.75rem', lineHeight: 1 }}>🍼</Typography>
                                      </Tooltip>
                                    </Box>
                                  );
                                })()}
                              </TableCell>
                            );
                          });
                          return cells;
                        })()}
                      </TableRow>
                    );
                      });
                    })()}
                  </TableBody>
                </Table>
              </Box>
            )}
            
            {/* Pulsante per aggiungere dipendenti da altri negozi (Admin e Coordinatore) + Dipendenti Disattivati */}
            {(() => {
              const viewerInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
              const ruolo = (viewerInfo?.ruolo || '').toLowerCase();
              const canAdd = ruolo === 'admin' || ruolo === 'coordinatore';
              const numDisattivati = matriceOrari.filter(r => dipendentiAttivi[r.dipendente] === false).length;
              return (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                  {canAdd ? (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        startIcon={<PersonAddIcon />}
                        onClick={handleOpenAggiungiDipendenti}
                      >
                        Aggiungi Dipendente da Altro Negozio
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<GroupAddIcon />}
                        onClick={() => setOpenAggiuntaMomentaneoDialog(true)}
                      >
                        Aggiungi Dipendente Momentaneo
                      </Button>
                      <Button
                        variant="outlined"
                        color="success"
                        startIcon={<PersonAddIcon />}
                        onClick={() => setOpenConfermaDialog(true)}
                      >
                        Conferma Dipendente
                      </Button>
                    </Box>
                  ) : <Box />}
                  {numDisattivati > 0 && (
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<PersonOffIcon />}
                      onClick={() => setOpenDisattivatiDialog(true)}
                    >
                      Dipendenti Disattivati ({numDisattivati})
                    </Button>
                  )}
                </Box>
              );
            })()}
          </Paper>
        )}
      </Paper>

      {/* Pannello Statistiche estratto in componente dedicato */}
      {selectedNegozio && selectedMese && selectedAnno && (
        <GenerazioneOrarioStats
          meseLabel={mesi[selectedMese - 1]}
          anno={selectedAnno}
          loading={statsLoading}
          hasStatistiche={statistiche.length > 0}
          statisticheSorted={statisticheSorted}
          statisticheTurniSorted={statisticheTurniSorted}
        />
      )}

      {/* Floating Action Button per aprire i log pianificazione */}
      <Tooltip title="Log Pianificazione Domeniche/Sabati" placement="left">
        <Fab
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 1300,
            boxShadow: 3,
            bgcolor: '#546E7A',
            color: 'white',
            '&:hover': { bgcolor: '#37474F' },
          }}
          onClick={handleOpenLogs}
          aria-label="Log pianificazione"
        >
          <AnalyticsIcon />
        </Fab>
      </Tooltip>

      {/* Floating Action Button per aprire le regole - solo Admin */}
      {isAdmin && (
        <Tooltip title="Regole Generazione" placement="left">
          <Fab
            sx={{
              position: 'fixed',
              bottom: 20,
              left: 20,
              zIndex: 1300,
              boxShadow: 3,
              bgcolor: PAGE_COLOR,
              color: 'white',
              '&:hover': {
                bgcolor: '#455A64',
              }
            }}
            onClick={() => setRulesDrawerOpen(!rulesDrawerOpen)}
            aria-label="Gestisci regole"
          >
            <SettingsIcon />
          </Fab>
        </Tooltip>
      )}

      {/* Drawer Log Pianificazione */}
      <Drawer
        anchor="right"
        open={logsDrawerOpen}
        onClose={() => setLogsDrawerOpen(false)}
        sx={{ '& .MuiDrawer-paper': { width: 580, boxSizing: 'border-box' } }}
        ModalProps={{ keepMounted: true }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
            <AnalyticsIcon sx={{ mr: 1, color: PAGE_COLOR }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>Dettaglio riposi pianificati</Typography>
              {logsData && (
                <Typography variant="caption" color="text.secondary">
                  {logsData.negozio} · {logsData.mese} {logsData.anno}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={() => setLogsDrawerOpen(false)}><CloseIcon fontSize="small" /></IconButton>
          </Box>

          {logsLoading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 6, gap: 2 }}>
              <CircularProgress sx={{ color: PAGE_COLOR }} />
              <Typography variant="body2" color="text.secondary">Caricamento dati in corso…</Typography>
            </Box>
          )}
          {logsError && <Alert severity="error" sx={{ m: 2 }}>{logsError}</Alert>}

          {!logsLoading && !logsError && !logsData && (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Seleziona negozio, mese e anno, poi riapri questo pannello.
              </Typography>
            </Box>
          )}

          {!logsLoading && !logsError && logsData && (
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              <Tabs
                value={logsTab}
                onChange={(_e, v) => setLogsTab(v)}
                textColor="inherit"
                TabIndicatorProps={{ style: { backgroundColor: '#1976D2' } }}
                sx={{ borderBottom: '1px solid #e0e0e0', '& .MuiTab-root': { minHeight: 44, fontSize: '0.82rem', fontWeight: 600 } }}
              >
                <Tab label="🌙 Domeniche" />
                <Tab label="📅 Sabati" />
              </Tabs>

              {/* ══════════════ TAB DOMENICHE ══════════════ */}
              {logsTab === 0 && (
                <Box sx={{ p: 2 }}>

                  {/* Box riepilogo */}
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: '#E3F2FD', borderColor: '#90CAF9' }}>
                    <Typography variant="subtitle2" sx={{ color: '#1565C0', mb: 0.5 }}>
                      Riepilogo domeniche — {logsData.mese} {logsData.anno}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="body2">
                        <strong>{logsData.domeniche.length}</strong> domeniche nel mese
                      </Typography>
                      <Typography variant="body2">
                        <strong>{logsData.riposiPerDomenica}</strong> persone che riposano ogni domenica
                      </Typography>
                      <Typography variant="body2">
                        <strong>{logsData.domeniche.length * logsData.riposiPerDomenica + (logsData.bonusRiposiDomenicali ?? 0)}</strong> riposi domenicali totali da assegnare
                      </Typography>
                    </Box>
                  </Paper>

                  {/* Graduatoria domeniche */}
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#1565C0' }}>
                    Graduatoria di priorità (chi ha riposato meno domeniche va prima)
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ '& th': { backgroundColor: '#BBDEFB', fontWeight: 700, fontSize: '0.75rem' } }}>
                          <TableCell>Dipendente</TableCell>
                          <TableCell align="center" title="Quante domeniche ha riposato sul totale dei sabati lavorati (più bassa = priorità maggiore)">
                            % dom. riposate
                          </TableCell>
                          <TableCell align="center" title="Data dell'ultima domenica di riposo">
                            Ultima domenica di riposo
                          </TableCell>
                          <TableCell align="center" title="Quante domeniche di riposo spettano questo mese">
                            Spettanti questo mese
                          </TableCell>
                          <TableCell align="center" title="Riposi già presenti nel database: Turno=Riposo con CODICE=RIPOSO, oppure Turno=ASSENZA con qualsiasi codice.">
                            Già Presenti
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...logsData.dipendentiInfo]
                          .filter(d => dipendentiAttivi[d.nome] !== false)
                          .sort((a, b) => {
                            // neoassunte sempre in fondo
                            if (a.neoassunta !== b.neoassunta) return a.neoassunta ? 1 : -1;
                            return a.ratioDom - b.ratioDom || (a.ultimoRiposoDom ?? '').localeCompare(b.ultimoRiposoDom ?? '');
                          })
                          .map((d, idx) => (
                            <TableRow key={d.nome} sx={{ backgroundColor: d.neoassunta ? '#FFF8E1' : (idx % 2 === 0 ? '#fff' : '#F5F5F5'), '& td': { fontSize: '0.78rem', py: 0.5 } }}>
                              <TableCell sx={{ fontWeight: 600 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  {d.nome}
                                  {d.neoassunta && (
                                    <Chip size="small" label="Neoassunta" color="warning" variant="outlined"
                                      sx={{ fontSize: '0.65rem', height: 18 }} />
                                  )}
                                  {d.temporaneo && (
                                    <Chip size="small" label="T" color="info" variant="outlined"
                                      title="Dipendente temporaneo — riposi cumulativi (negozio origine + negozio corrente)"
                                      sx={{ fontSize: '0.65rem', height: 18 }} />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="center" sx={{ color: d.ratioDom > 0.35 ? '#c62828' : '#2e7d32', fontWeight: 600 }}>
                                {(d.ratioDom * 100).toFixed(1)}%
                              </TableCell>
                              <TableCell align="center">
                                {d.ultimoRiposoDom
                                  ? dayjs(d.ultimoRiposoDom).format('DD/MM/YYYY')
                                  : <Typography variant="caption" color="text.disabled">mai</Typography>}
                              </TableCell>
                              <TableCell align="center">
                                {d.neoassunta
                                  ? <Chip size="small" label="non conta" color="warning" variant="outlined" sx={{ fontSize: '0.72rem' }} />
                                  : <Chip size="small" label={d.targetDom === 0 ? 'nessuna' : `${d.targetDom}`}
                                      color={d.targetDom > 0 ? 'primary' : 'default'} variant="outlined"
                                      sx={{ fontSize: '0.72rem' }} />
                                }
                              </TableCell>
                              <TableCell align="center">
                                <Chip size="small"
                                  label={d.collocatiDom}
                                  color={d.neoassunta ? 'default' : (d.collocatiDom === d.targetDom ? 'success' : d.collocatiDom < d.targetDom ? 'warning' : 'error')}
                                  variant="filled" sx={{ fontSize: '0.72rem' }} />
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Calendario domeniche */}
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#1565C0' }}>
                    Chi riposa ogni domenica
                  </Typography>
                  {logsData.domeniche.map(g => (
                    <Paper key={g.data} variant="outlined" sx={{
                      mb: 1.5, p: 1.5,
                      borderLeft: g.overflow ? '4px solid #FF9800' : '4px solid #1976D2',
                      backgroundColor: g.overflow ? '#FFF8E1' : '#fff'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1565C0' }}>
                          {dayjs(g.data).format('dddd DD MMMM')}
                        </Typography>
                        {g.overflow && (
                          <Chip size="small" label="⚠ più del previsto" color="warning" variant="outlined" sx={{ fontSize: '0.68rem' }} />
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {g.riposanti.length} su {g.slot} posti
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {g.riposanti.length > 0
                          ? g.riposanti.map(nome => (
                              <Chip key={nome} label={nome} size="small" color="primary" variant="filled"
                                sx={{ fontSize: '0.72rem' }} />
                            ))
                          : <Typography variant="caption" color="text.disabled">Nessuno pianificato per questa domenica</Typography>
                        }
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* ══════════════ TAB SABATI ══════════════ */}
              {logsTab === 1 && (
                <Box sx={{ p: 2 }}>

                  {/* Box riepilogo */}
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: '#F3E5F5', borderColor: '#CE93D8' }}>
                    <Typography variant="subtitle2" sx={{ color: '#6A1B9A', mb: 0.5 }}>
                      Riepilogo sabati — {logsData.mese} {logsData.anno}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="body2">
                        <strong>{logsData.sabati.length}</strong> sabati nel mese
                      </Typography>
                      <Typography variant="body2">
                        <strong>{logsData.riposiPerSabato}</strong> persone che riposano ogni sabato
                      </Typography>
                      <Typography variant="body2">
                        <strong>{logsData.sabati.length * logsData.riposiPerSabato + (logsData.bonusRiposiSabatali ?? 0)}</strong> riposi di sabato totali da assegnare
                      </Typography>
                    </Box>
                  </Paper>

                  {/* Graduatoria sabati */}
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#6A1B9A' }}>
                    Graduatoria di priorità (chi ha riposato meno sabati va prima)
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ '& th': { backgroundColor: '#E1BEE7', fontWeight: 700, fontSize: '0.75rem' } }}>
                          <TableCell>Dipendente</TableCell>
                          <TableCell align="center" title="Quanti sabati ha riposato sul totale dei sabati lavorati (più bassa = priorità maggiore)">
                            % sab. riposati
                          </TableCell>
                          <TableCell align="center" title="Data dell'ultimo sabato di riposo">
                            Ultimo sabato di riposo
                          </TableCell>
                          <TableCell align="center" title="Quanti sabati di riposo spettano questo mese">
                            Spettanti questo mese
                          </TableCell>
                          <TableCell align="center" title="Riposi registrati nel database: Turno=Riposo con CODICE=RIPOSO, oppure Turno=ASSENZA con qualsiasi codice.">
                            Nel DB
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...logsData.dipendentiInfo]
                          .filter(d => dipendentiAttivi[d.nome] !== false)
                          .sort((a, b) => {
                            if (a.neoassunta !== b.neoassunta) return a.neoassunta ? 1 : -1;
                            return a.ratioSab - b.ratioSab || (a.ultimoRiposoSab ?? '').localeCompare(b.ultimoRiposoSab ?? '');
                          })
                          .map((d, idx) => (
                            <TableRow key={d.nome} sx={{ backgroundColor: d.neoassunta ? '#FFF8E1' : (idx % 2 === 0 ? '#fff' : '#F5F5F5'), '& td': { fontSize: '0.78rem', py: 0.5 } }}>
                              <TableCell sx={{ fontWeight: 600 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  {d.nome}
                                  {d.neoassunta && (
                                    <Chip size="small" label="Neoassunta" color="warning" variant="outlined"
                                      sx={{ fontSize: '0.65rem', height: 18 }} />
                                  )}
                                  {d.temporaneo && (
                                    <Chip size="small" label="T" color="info" variant="outlined"
                                      title="Dipendente temporaneo — riposi cumulativi (negozio origine + negozio corrente)"
                                      sx={{ fontSize: '0.65rem', height: 18 }} />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="center" sx={{ color: d.ratioSab > 0.35 ? '#c62828' : '#2e7d32', fontWeight: 600 }}>
                                {(d.ratioSab * 100).toFixed(1)}%
                              </TableCell>
                              <TableCell align="center">
                                {d.ultimoRiposoSab
                                  ? dayjs(d.ultimoRiposoSab).format('DD/MM/YYYY')
                                  : <Typography variant="caption" color="text.disabled">mai</Typography>}
                              </TableCell>
                              <TableCell align="center">
                                {d.neoassunta
                                  ? <Chip size="small" label="non conta" color="warning" variant="outlined" sx={{ fontSize: '0.72rem' }} />
                                  : <Chip size="small" label={d.targetSab === 0 ? 'nessuno' : `${d.targetSab}`}
                                      color={d.targetSab > 0 ? 'secondary' : 'default'} variant="outlined"
                                      sx={{ fontSize: '0.72rem' }} />
                                }
                              </TableCell>
                              <TableCell align="center">
                                <Chip size="small"
                                  label={d.collocatiSab}
                                  color={d.neoassunta ? 'default' : (d.collocatiSab === d.targetSab ? 'success' : d.collocatiSab < d.targetSab ? 'warning' : 'error')}
                                  variant="filled" sx={{ fontSize: '0.72rem' }} />
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Calendario sabati */}
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#6A1B9A' }}>
                    Chi riposa ogni sabato
                  </Typography>
                  {logsData.sabati.map(g => (
                    <Paper key={g.data} variant="outlined" sx={{
                      mb: 1.5, p: 1.5,
                      borderLeft: g.overflow ? '4px solid #FF9800' : '4px solid #9C27B0',
                      backgroundColor: g.overflow ? '#FFF8E1' : '#fff'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#6A1B9A' }}>
                          {dayjs(g.data).format('dddd DD MMMM')}
                        </Typography>
                        {g.overflow && (
                          <Chip size="small" label="⚠ più del previsto" color="warning" variant="outlined" sx={{ fontSize: '0.68rem' }} />
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {g.riposanti.length} su {g.slot} posti
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {g.riposanti.length > 0
                          ? g.riposanti.map(nome => (
                              <Chip key={nome} label={nome} size="small" color="secondary" variant="filled"
                                sx={{ fontSize: '0.72rem' }} />
                            ))
                          : <Typography variant="caption" color="text.disabled">Nessuno pianificato per questo sabato</Typography>
                        }
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}

            </Box>
          )}
        </Box>
      </Drawer>

      {/* Drawer per la gestione delle regole */}
      <Drawer
        anchor="right"
        open={rulesDrawerOpen}
        onClose={() => setRulesDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 400,
            boxSizing: 'border-box',
          },
        }}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <SettingsIcon sx={{ mr: 1, color: PAGE_COLOR }} />
            <Typography variant="h5" component="h2">
              Regole di Generazione
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Gestisci le regole applicate durante la generazione automatica degli orari.
            Le regole attive verranno considerate dal sistema.
          </Typography>

          <List>
            {[...schedulingRules].sort((a, b) => a.priority - b.priority).map((rule, index) => (
              <React.Fragment key={rule.id}>
                <ListItem sx={{ px: 0 }}>
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {rule.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {rule.description}
                        </Typography>
                        <Chip 
                          label={`Priorità: ${rule.priority}`} 
                          size="small" 
                          variant="outlined"
                          color={rule.priority <= 3 ? 'error' : rule.priority <= 6 ? 'warning' : 'default'}
                        />
                      </Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={rule.enabled}
                            onChange={() => handleRuleToggle(rule.id)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: PAGE_COLOR,
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: PAGE_COLOR,
                              },
                            }}
                          />
                        }
                        label=""
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  </Box>
                </ListItem>
                {index < schedulingRules.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Statistiche Regole
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Chip 
                label={`${schedulingRules.filter(r => r.enabled).length} Attive`}
                color="success"
                variant="filled"
              />
              <Chip 
                label={`${schedulingRules.filter(r => !r.enabled).length} Disattive`}
                color="default"
                variant="outlined"
              />
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Menu contestuale */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
  onContextMenu={(e) => { e.preventDefault(); }}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {renderContextMenuItems()}
      </Menu>

      {/* Menu contestuale per Giorno (solo Blocco Verticale) */}
      <Menu
        open={dayMenu !== null}
        onClose={handleCloseDayMenu}
        onContextMenu={(e) => { e.preventDefault(); }}
        anchorReference="anchorPosition"
        anchorPosition={
          dayMenu !== null
            ? { top: dayMenu.mouseY, left: dayMenu.mouseX }
            : undefined
        }
      >
        {dayMenu && (
          <>
            {festivitaData && festivitaData.some(f => f.dataFestivita === dayMenu.dateIso) && (
              <MenuItem onClick={() => handleAggiungiChiusoDittaByDay(dayMenu.dateIso)}>
                Aggiungi chiuso ditta
              </MenuItem>
            )}
            {(() => {
              const anyHas = (label: string) =>
                (annotazioniGiorno[dayMenu.dateIso] || []).some(l => l.toLowerCase() === label.toLowerCase());
              const hasGiro = anyHas('Giro');
              const hasRic = anyHas('Ricartellinamento');
              const hasInv = anyHas('Inventario');
              return (
                <>
                  {hasInv ? (
                    <MenuItem onClick={() => handleCancellaAnnotazioneByDay(dayMenu.dateIso, 'Inventario')}>
                      Cancella Inventario
                    </MenuItem>
                  ) : (
                    <MenuItem onClick={() => handleSegnaAnnotazioneByDay(dayMenu.dateIso, 'Inventario')}>
                      Segna Inventario
                    </MenuItem>
                  )}
                  {hasRic ? (
                    <MenuItem onClick={() => handleCancellaAnnotazioneByDay(dayMenu.dateIso, 'Ricartellinamento')}>
                      Cancella Ricartellinamento
                    </MenuItem>
                  ) : (
                    <MenuItem onClick={() => handleSegnaAnnotazioneByDay(dayMenu.dateIso, 'Ricartellinamento')}>
                      Segna Ricartellinamento
                    </MenuItem>
                  )}
                  {hasGiro ? (
                    <MenuItem onClick={() => handleCancellaAnnotazioneByDay(dayMenu.dateIso, 'Giro')}>
                      Cancella Giro
                    </MenuItem>
                  ) : (
                    <MenuItem onClick={() => handleSegnaAnnotazioneByDay(dayMenu.dateIso, 'Giro')}>
                      Segna Giro
                    </MenuItem>
                  )}
                </>
              );
            })()}
            <MenuItem onClick={() => handleEliminaVerticaleByDay(dayMenu.dateIso)}>
              <DeleteIcon sx={{ mr: 1, fontSize: 16 }} />
              Elimina Verticale
            </MenuItem>
            <MenuItem onClick={() => handleBloccaVerticaliByDay(dayMenu.dateIso)}>
              <LockIcon sx={{ mr: 1, fontSize: 16 }} />
              Blocca Verticale
            </MenuItem>
            {(() => {
              const d = dayjs(dayMenu.dateIso);
              const day = d.date();
              const isCurrMonthDate = d.month() === (selectedMese - 1) && d.year() === selectedAnno;
              const isPrevMonthDate = d.isBefore(dayjs().year(selectedAnno).month(selectedMese - 1).date(1), 'day');
              return matriceOrari.some(r => {
                const orariGiorno = isCurrMonthDate ? (r.orari[day] || []) : (isPrevMonthDate ? (r.orariPrev?.[day] || []) : (r.orariNext?.[day] || []));
                return (orariGiorno || []).some(t => t && t.blocco === 1);
              });
            })() && (
              <MenuItem onClick={() => handleSbloccaVerticaliByDay(dayMenu.dateIso)}>
                <LockOpenIcon sx={{ mr: 1, fontSize: 16 }} />
                Sblocca Verticale
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      {/* Menu contestuale per Dipendente (solo Blocco Orizzontale) */}
      <Menu
        open={dipMenu !== null}
        onClose={handleCloseDipMenu}
        onContextMenu={(e) => { e.preventDefault(); }}
        anchorReference="anchorPosition"
        anchorPosition={
          dipMenu !== null
            ? { top: dipMenu.mouseY, left: dipMenu.mouseX }
            : undefined
        }
      >
        {dipMenu && (
          <>
            <MenuItem onClick={() => handleBloccaOrizzontaliByDip(dipMenu.dipendente)}>
              <LockIcon sx={{ mr: 1, fontSize: 16 }} />
              Blocca Orizzontale
            </MenuItem>
            <MenuItem onClick={() => handleEliminaOrizzontaleByDip(dipMenu.dipendente)}>
              <DeleteIcon sx={{ mr: 1, fontSize: 16 }} />
              Elimina Orizzontale
            </MenuItem>
            {(() => {
              const riga = matriceOrari.find(r => r.dipendente === dipMenu.dipendente);
              if (!riga) return false;
              // Verifica blocchi su tutti i giorni visibili (mese corrente + prev/next)
              const anyBlockedVisible = allGridDays.some(d => {
                const day = d.date();
                const isCurrMonthDate = d.month() === (selectedMese - 1) && d.year() === selectedAnno;
                const isPrevMonthDate = d.isBefore(dayjs().year(selectedAnno).month(selectedMese - 1).date(1), 'day');
                const orariGiorno = isCurrMonthDate ? (riga.orari[day] || []) : (isPrevMonthDate ? (riga.orariPrev?.[day] || []) : (riga.orariNext?.[day] || []));
                return (orariGiorno || []).some((t: any) => t && t.blocco === 1);
              });
              return anyBlockedVisible;
            })() && (
              <MenuItem onClick={() => handleSbloccaOrizzontaliByDip(dipMenu.dipendente)}>
                <LockOpenIcon sx={{ mr: 1, fontSize: 16 }} />
                Sblocca Orizzontale
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      {/* Menu contestuale globale (header Dipendenti) */}
      <Menu
        open={globalMenu !== null}
        onClose={handleCloseGlobalMenu}
        onContextMenu={(e) => { e.preventDefault(); }}
        anchorReference="anchorPosition"
        anchorPosition={
          globalMenu !== null
            ? { top: globalMenu.mouseY, left: globalMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleBloccaTutto}>
          <LockIcon sx={{ mr: 1, fontSize: 16 }} />
          Blocca tutto
        </MenuItem>
        {(() => {
          // Mostra Sblocca tutto solo se esiste almeno un turno bloccato
          const hasBlocked = matriceOrari.some(r => 
            Object.values(r.orari).some((og: any) => (og || []).some((t: any) => t && t.blocco === 1))
          );
          return hasBlocked;
        })() && (
          <MenuItem onClick={handleSbloccaTutto}>
            <LockOpenIcon sx={{ mr: 1, fontSize: 16 }} />
            Sblocca tutto
          </MenuItem>
        )}
      </Menu>

      {/* Sottomenu Assenza: elenco codici CODICE_TIPO_ORARIO */}
      <Menu
        open={assenzaMenu !== null}
        onClose={handleCloseAssenzaMenu}
  onContextMenu={(e) => { e.preventDefault(); }}
        anchorReference="anchorPosition"
        anchorPosition={
          assenzaMenu !== null
            ? { top: assenzaMenu.mouseY, left: assenzaMenu.mouseX }
            : undefined
        }
      >
        {CODICI_ASSENZA.filter(cod => {
          if (assenzaMenu && isFestivita(assenzaMenu.dateIso) && !isCodiceConsentitoInFestivita(cod)) {
            return false;
          }
          // Filtra LEGGE 104 PERMESSO in base al dipendente selezionato
          if (cod === 'LEGGE 104 PERMESSO' && assenzaMenu) {
            // Se la lista è vuota (Admin), mostra sempre
            if (dipendentiAutorizzati104.length === 0) return true;
            
            // Altrimenti verifica che il dipendente sia autorizzato
            const dipObj = dipendenti.find(d => d.nome === assenzaMenu.dipendente);
            if (!dipObj || !dipObj.codicePersonale) return false;
            
            return dipendentiAutorizzati104.includes(dipObj.codicePersonale);
          }
          
          if (cod === 'CHIUSO DITTA') {
            // Verifica se la data selezionata è una festività
            if (!assenzaMenu || !festivitaData) return false;
            return festivitaData.some(f => f.dataFestivita === assenzaMenu.dateIso);
          }
          return true;
        }).map(cod => {
          // Controlla se LEGGE 104 PERMESSO ha raggiunto il limite mensile
          if (cod === 'LEGGE 104 PERMESSO' && assenzaMenu) {
            const dipObj = dipendenti.find(d => d.nome === assenzaMenu.dipendente);
            const cp = dipObj?.codicePersonale;
            // Admin (lista vuota) non ha limite
            if (cp && dipendentiAutorizzati104.length > 0) {
              const info = utilizzo104[cp];
              if (info && info.ggMese > 0 && info.remainingGg === 0) {
                return (
                  <Tooltip
                    key={cod}
                    title={
                      <span style={{ color: '#ff6b6b', fontWeight: 600 }}>
                        Limite permessi raggiunto ({info.usedGg}/{info.ggMese} gg)
                      </span>
                    }
                    placement="right"
                    arrow
                  >
                    <span style={{ display: 'block' }}>
                      <MenuItem
                        disabled
                        sx={{
                          color: 'text.disabled',
                          opacity: 0.5,
                          cursor: 'not-allowed',
                          '&.Mui-disabled': { pointerEvents: 'auto' }
                        }}
                      >
                        {cod}
                      </MenuItem>
                    </span>
                  </Tooltip>
                );
              }
            }
          }
          return (
            <MenuItem key={cod} onClick={() => {
              // Per CORSI AI DIPENDENTI e SMART WORKING, apri dialog per richiedere orari previsti obbligatori
              if (cod === 'CORSI AI DIPENDENTI' || cod === 'SMART WORKING') {
                setOrariPrevistiData({ codice: cod, oraPrevInizio: '', oraPrevFine: '' });
                setOrariPrevistiDialog(true);
                // Non chiudere assenzaMenu qui, lo farà il dialog
              } else {
                handleAddAssenza(cod);
              }
            }}>
              {cod}
            </MenuItem>
          );
        })}
      </Menu>

      {/* Sottomenu Aggiungi Turno: elenca i turni standard come nelle celle vuote */}
      <Menu
        open={addMenu !== null}
        onClose={handleCloseAddMenu}
        onContextMenu={(e) => { e.preventDefault(); }}
        anchorReference="anchorPosition"
        anchorPosition={
          addMenu !== null
            ? { top: addMenu.mouseY, left: addMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={() => handleAddTurno('Mattina')}>Aggiungi Mattina</MenuItem>
        <MenuItem onClick={() => handleAddTurno('Pomeriggio')}>Aggiungi Pomeriggio</MenuItem>
        <MenuItem onClick={() => handleAddTurno('Intermezzo')}>Aggiungi Intermezzo</MenuItem>
        {!isFestivita(addMenu?.dateIso || '') && <MenuItem onClick={() => handleAddTurno('Riposo')}>Aggiungi Riposo</MenuItem>}
        <MenuItem onClick={() => handleAddTurno('Personalizzato')}>Aggiungi Personalizzato</MenuItem>
      </Menu>

      {/* Sottomenu CAMBIA TURNO: elenco turni presenti nella cella */}
      <Menu
        open={changeMenu !== null}
        onClose={handleCloseChangeMenu}
        onContextMenu={(e) => { e.preventDefault(); }}
        anchorReference="anchorPosition"
        anchorPosition={
          changeMenu !== null
            ? { top: changeMenu.mouseY, left: changeMenu.mouseX }
            : undefined
        }
      >
        {changeMenu && (() => {
          const list = getTurniInCella(changeMenu.dipendente, changeMenu.dateIso);
          if (!list.length) {
            return <MenuItem disabled>Nessun turno in cella</MenuItem>;
          }
          return (
            <>
              {list.map((t) => {
                const start = (t.oraPrevInizio || '').substring(0,5);
                const end = (t.oraPrevFine || '').substring(0,5);
                const label = (t.turno || 'Turno') === 'ASSENZA'
                  ? `Assenza${t.codiceTipoOrario ? ' · ' + t.codiceTipoOrario : ''}`
                  : `${t.turno}${start || end ? ` · ${start || '--:--'}-${end || '--:--'}` : ''}`;
                return (
                  <MenuItem key={t.id} onClick={() => handleChangeTurnoFromMenu(changeMenu.dipendente, changeMenu.dateIso, t)}>
                    {label}
                  </MenuItem>
                );
              })}
            </>
          );
        })()}
      </Menu>

      {/* Sottomenu ELIMINA TURNO: tutti o singolo */}
      <Menu
        open={deleteMenu !== null}
        onClose={handleCloseDeleteMenu}
        onContextMenu={(e) => { e.preventDefault(); }}
        anchorReference="anchorPosition"
        anchorPosition={
          deleteMenu !== null
            ? { top: deleteMenu.mouseY, left: deleteMenu.mouseX }
            : undefined
        }
      >
        {deleteMenu && (() => {
          const list = getTurniInCella(deleteMenu.dipendente, deleteMenu.dateIso);
          if (!list.length) {
            return <MenuItem disabled>Nessun turno in cella</MenuItem>;
          }
          return (
            <>
              <MenuItem onClick={() => handleDeleteAllTurniInCella(deleteMenu.dipendente, deleteMenu.dateIso)}>
                Elimina tutti i turni
              </MenuItem>
              <Divider />
              {list.map((t) => {
                const start = (t.oraPrevInizio || '').substring(0,5);
                const end = (t.oraPrevFine || '').substring(0,5);
                const label = (t.turno || 'Turno') === 'ASSENZA'
                  ? `Assenza${t.codiceTipoOrario ? ' · ' + t.codiceTipoOrario : ''}`
                  : `${t.turno}${start || end ? ` · ${start || '--:--'}-${end || '--:--'}` : ''}`;
                return (
                  <MenuItem key={t.id} onClick={() => handleDeleteTurnoById(t.id)}>
                    {label}
                  </MenuItem>
                );
              })}
            </>
          );
        })()}
      </Menu>

      {/* Dialog: Aggiungi preferenza (da context menu Generazione Orari) */}
      <Dialog open={!!prefDialog} onClose={() => { setPrefDialog(null); setPrefDialogError(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FavoriteBorderIcon sx={{ color: '#e91e63' }} />
          Aggiungi preferenza
        </DialogTitle>
        <DialogContent>
          {prefDialog && (
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Dipendente: <strong>{prefDialog.dipendente}</strong>
              <br />
              Data: <strong>{dayjs(prefDialog.dateIso).format('dddd D MMMM YYYY')}</strong>
            </Typography>
          )}
          {prefDialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>{prefDialogError}</Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              variant="outlined"
              disabled={prefDialogLoading}
              onClick={() => handlePrefDialogInsert('Mattina')}
              sx={{ justifyContent: 'flex-start' }}
            >
              Mattina
            </Button>
            <Button
              variant="outlined"
              disabled={prefDialogLoading}
              onClick={() => handlePrefDialogInsert('Pomeriggio')}
              sx={{ justifyContent: 'flex-start' }}
            >
              Pomeriggio
            </Button>
            {prefDialog && !isFestivita(prefDialog.dateIso) && dayjs(prefDialog.dateIso).day() !== 0 && dayjs(prefDialog.dateIso).day() !== 6 && (
              <Button
                variant="outlined"
                disabled={prefDialogLoading}
                onClick={() => handlePrefDialogInsert('Intermezzo')}
                sx={{ justifyContent: 'flex-start' }}
              >
                Intermezzo
              </Button>
            )}
            <Button
              variant="outlined"
              disabled={prefDialogLoading}
              onClick={() => handlePrefDialogInsert('Riposo')}
              sx={{ justifyContent: 'flex-start', color: '#e91e63', borderColor: '#e91e63' }}
            >
              Riposo
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPrefDialog(null); setPrefDialogError(null); }} disabled={prefDialogLoading}>
            Annulla
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo turno personalizzato */}
      <Dialog 
        open={customTurnoDialog} 
        onClose={() => setCustomTurnoDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Turno Personalizzato</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Ora Inizio"
              type="time"
              value={customTurnoData.oraInizio}
              onChange={(e) => setCustomTurnoData(prev => ({ ...prev, oraInizio: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Ora Fine"
              type="time"
              value={customTurnoData.oraFine}
              onChange={(e) => setCustomTurnoData(prev => ({ ...prev, oraFine: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomTurnoDialog(false)}>
            Annulla
          </Button>
          <Button onClick={handleCustomTurnoSave} variant="contained">
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Orari Previsti obbligatori per CORSI AI DIPENDENTI e SMART WORKING */}
      <Dialog
        open={orariPrevistiDialog}
        onClose={() => {
          setOrariPrevistiDialog(false);
          setOrariPrevistiData({ codice: '', oraPrevInizio: '', oraPrevFine: '' });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{orariPrevistiData.codice}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, color: 'text.secondary' }}>
            Per "{orariPrevistiData.codice}" è obbligatorio inserire gli orari previsti prima di salvare.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Ora Prevista Inizio"
              type="time"
              value={orariPrevistiData.oraPrevInizio}
              onChange={(e) => setOrariPrevistiData(prev => ({ ...prev, oraPrevInizio: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
              error={orariPrevistiData.oraPrevInizio === ''}
              helperText={orariPrevistiData.oraPrevInizio === '' ? 'Campo obbligatorio' : ''}
            />
            <TextField
              label="Ora Prevista Fine"
              type="time"
              value={orariPrevistiData.oraPrevFine}
              onChange={(e) => setOrariPrevistiData(prev => ({ ...prev, oraPrevFine: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
              error={orariPrevistiData.oraPrevFine === ''}
              helperText={orariPrevistiData.oraPrevFine === '' ? 'Campo obbligatorio' : ''}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOrariPrevistiDialog(false);
            setOrariPrevistiData({ codice: '', oraPrevInizio: '', oraPrevFine: '' });
            setAssenzaMenu(null);
          }}>
            Annulla
          </Button>
          <Button 
            onClick={handleOrariPrevistiSave} 
            variant="contained"
            disabled={!orariPrevistiData.oraPrevInizio || !orariPrevistiData.oraPrevFine}
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Cambio turno per selezione multipla */}
      <Dialog
        open={!!swapDialog?.open}
        onClose={handleCloseSwapDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cambio turno</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Seleziona il dipendente con cui scambiare i turni per i giorni selezionati
            {rowSelection ? ` (${Math.min(rowSelection.startIdx,rowSelection.endIdx)+1}→${Math.max(rowSelection.startIdx,rowSelection.endIdx)+1})` : ''}.
          </Typography>
          <TextField
            fullWidth
            label="Cerca dipendente"
            value={swapSearch}
            onChange={(e) => setSwapSearch(e.target.value)}
            margin="normal"
            placeholder="Digita per filtrare..."
          />
          <List dense>
            {swapCandidates
              .filter(d => d.nome.toLowerCase().includes(swapSearch.toLowerCase()))
              .map(d => (
                <ListItem key={d.codicePersonale || d.nome} disablePadding>
                  <ListItemButton onClick={() => handleExecuteSwapWith(d.nome)}>
                    <ListItemText primary={d.nome} secondary={isDipendenteTemporaneo(d.nome) ? 'Temporaneo' : undefined} />
                  </ListItemButton>
                </ListItem>
              ))}
            {swapCandidates.filter(d => d.nome.toLowerCase().includes(swapSearch.toLowerCase())).length === 0 && (
              <Typography sx={{ mt: 1, color: 'text.secondary' }}>Nessun dipendente trovato</Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSwapDialog}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Dipendenti Disattivati */}
      <Dialog
        open={openDisattivatiDialog}
        onClose={() => setOpenDisattivatiDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonOffIcon color="warning" />
          Dipendenti Disattivati
          <IconButton
            size="small"
            onClick={() => setOpenDisattivatiDialog(false)}
            sx={{ ml: 'auto' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {matriceOrari.filter(r => dipendentiAttivi[r.dipendente] === false).length === 0 ? (
            <Typography variant="body2" color="text.secondary">Nessun dipendente disattivato.</Typography>
          ) : (
            <List dense disablePadding>
              {matriceOrari
                .filter(r => dipendentiAttivi[r.dipendente] === false)
                .map(r => (
                  <ListItem key={r.dipendente} disablePadding>
                    <ListItemButton onClick={() => handleToggleDipendenteAttivo(r.dipendente)} dense>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={false}
                          tabIndex={-1}
                          disableRipple
                          size="small"
                        />
                      </ListItemIcon>
                      <ListItemText primary={r.dipendente} />
                    </ListItemButton>
                  </ListItem>
                ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDisattivatiDialog(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog per aggiungere dipendenti da altri negozi */}
      <Dialog
        open={aggiungiDipendentiDialog}
        onClose={() => setAggiungiDipendentiDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Aggiungi Dipendenti da Altri Negozi
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Cerca dipendente per nome"
            value={ricercaDipendente}
            onChange={(e) => setRicercaDipendente(e.target.value)}
            margin="normal"
            placeholder="Inserisci il nome del dipendente..."
          />
          <List>
            {dipendentiDisponibili
              .filter(dip => 
                dip.nome.toLowerCase().includes(ricercaDipendente.toLowerCase())
              )
              .map((dip) => (
              <ListItem key={dip.codicePersonale}>
                <ListItemText 
                  primary={dip.nome} 
                  secondary={`Negozio: ${dip.neg} - Ore settimanali: ${dip.oreSettimanali}`} 
                />
                <Button 
                  onClick={() => handleAggiungiDipendente(dip)}
                  disabled={dipendenti.some(d => d.codicePersonale === dip.codicePersonale)}
                  variant="outlined"
                  size="small"
                >
                  {dipendenti.some(d => d.codicePersonale === dip.codicePersonale) ? 'Già aggiunto' : 'Aggiungi'}
                </Button>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAggiungiDipendentiDialog(false);
            setRicercaDipendente(''); // Resetta la ricerca quando chiudi
          }}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Nessun dialog per bulk swap: usare drag & drop dopo aver scelto "Inizia drag massivo" dal menu contestuale */}
      
      {/* Dialog conferma incolla su turno esistente */}
      <Dialog 
        open={confirmPasteDialog?.open || false} 
        onClose={() => setConfirmPasteDialog(null)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Conferma sostituzione turno</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler <strong>sostituire</strong> il turno esistente con quello copiato?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmPasteDialog(null)} color="inherit">
            No
          </Button>
          <Button 
            onClick={() => {
              if (confirmPasteDialog) {
                executeActualPaste(confirmPasteDialog.dipendente, confirmPasteDialog.dateIso);
              }
              setConfirmPasteDialog(null);
            }} 
            color="primary" 
            variant="contained"
          >
            Sì
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog open={missingConfigOpen} onClose={() => setMissingConfigOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configurazioni quantita_personale mancanti</DialogTitle>
        <DialogContent dividers>
          {missingConfigDates.length === 0 ? (
            <Typography>Nessuna data elencata nel messaggio.</Typography>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Le seguenti date (Lun-Ven) non hanno una riga configurata in quantita_personale per Mattina/Pomeriggio/Intermezzo e impediscono il calcolo dei riposi:
              </Typography>
              <List dense>
                {missingConfigDates.map(d => (
                  <ListItem key={d}>
                    <ListItemIcon>
                      <WarningAmberIcon color="warning" />
                    </ListItemIcon>
                    <ListItemText primary={d} />
                  </ListItem>
                ))}
              </List>
              <Alert severity="warning" sx={{ mt: 2 }}>
                Aggiungi le righe mancanti nella tabella quantita_personale e ripeti la generazione dei riposi.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMissingConfigOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Aggiungi Dipendente Momentaneo */}
      {selectedNegozio && (
        <AggiuntaDipendenteMomentaneoDialog
          open={openAggiuntaMomentaneoDialog}
          onClose={() => setOpenAggiuntaMomentaneoDialog(false)}
          onCreated={async () => {
            setOpenAggiuntaMomentaneoDialog(false);
            // Ricarica prima i dipendenti (include ora i momentanei) poi la matrice orari
            if (selectedNegozio && selectedMese && selectedAnno) {
              await fetchDipendenti();
              fetchOrariGenerati();
            }
          }}
          negozio={selectedNegozio}
        />
      )}

      {/* Dialog: Conferma Dipendente Momentaneo */}
      {selectedNegozio && (
        <ConfermaDipendenteDialog
          open={openConfermaDialog}
          onClose={() => setOpenConfermaDialog(false)}
          onConfermed={async () => {
            setOpenConfermaDialog(false);
            if (selectedNegozio && selectedMese && selectedAnno) {
              // La baseline viene già generata dal backend nella stessa chiamata /conferma.
              // Qui ci limitiamo a ricaricare dipendenti e turni.
              await fetchDipendenti();
              fetchOrariGenerati();
            }
          }}
          negozio={selectedNegozio}
          mese={selectedMese ? mesi[selectedMese - 1] : undefined}
          anno={selectedAnno ?? undefined}
        />
      )}

    </Container>
  );
};

export default GenerazioneOrario;
